var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState, forwardRef, useImperativeHandle, } from 'react';
import { createPortal } from 'react-dom';
import '../styles/mathEditor.scss';
import { MathfieldElement } from 'mathlive';
import 'mathlive';
// Import memory optimization helpers
import { batchDisposeMathfields, scheduleSuggestGarbageCollection, ensureAriaLiveText, } from '../helper/mathliveOptimizations';
import { selectTool } from '../helper/helper';
import { announce, announceAssertive } from '../helper/announcer';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { ColorEnum } from './ColorPicker';
import ButtonComponent from './ButtonComponent';
import { saveState, } from '../helper/stateManagerHelper';
import { countVisibleCharsInLatex } from '../helper/latexCharCount';
import globalStore from '../store';
import { t } from 'i18next';
var WHITESPACE_CHAR = '\u200B';
// MathLive's .ML__content (the shadow-DOM content wrapper) defaults to
// `overflow: hidden`, which clips a tall fraction's empty \placeholder{} box
// — its top extends a few px above the vertically-centered content box and
// gets cut (a filled digit sits lower, so it renders fine). Forcing it visible
// removes the clip so the placeholder shows fully.
// Trade-off: .ML__content's overflow:hidden also contains content wider/taller
// than the field, so `visible` lets a long expression paint outside the field
// box. Acceptable here because these editors hold short labels/cells; revisit
// if long expressions in narrow boxes start spilling.
var PLACEHOLDER_FIX_CSS = ' .ML__content { overflow: visible !important; }';
// ─── Window-scroll lock (singleton) ──────────────────────────────────────────
// iOS scrolls the whole document to keep a focused math-field visible. This app
// scrolls via an inner container, so that window scroll is an unwanted "jump".
// This lock pins window scroll to its saved position.
//
// MUST be a module-level singleton (not per-math-field): a per-field listener
// leaks when MathLive recreates the field without a blur, leaving multiple
// listeners with different saved positions that fight forever (window bounces
// 0 ↔ N on every frame — the iOS-26 typing-jump infinite loop). One listener
// can't fight itself (the `restoring` + already-at-target guards stop re-entry).
//
// Removal is debounced so the lock SURVIVES the keypad blur→refocus churn
// (tapping a keypad button blurs the math-field for a moment). Without this the
// lock is torn down mid-edit and reinstalled AFTER the jump, capturing the
// jumped position instead of the original.
var _winLockListener = null;
var _winLockPos = { x: 0, y: 0 };
var _winLockRestoring = false;
var _winLockRemoveTimer = null;
function installWinLock(savedX, savedY) {
    if (_winLockRemoveTimer) {
        clearTimeout(_winLockRemoveTimer);
        _winLockRemoveTimer = null;
    }
    // Already active → keep the original (pre-jump) saved position; don't recapture.
    if (_winLockListener)
        return;
    _winLockPos = { x: savedX, y: savedY };
    // Singleton scroll-restore for iOS 26 focus/input scroll. MUST stay a
    // singleton (one listener, debounced removal) — a per-field listener leaks
    // across MathLive field recreation and multiple locks fight each other
    // (window bounces 0↔N forever). Combined with holdWindowScrollBriefly() this
    // reduces the typing window-scroll to a non-visible 1-frame blip.
    // (Tried & rejected: overflow:hidden and position:fixed on <body> — iOS still
    // scrolled the document; position:fixed also risks layout breakage.)
    _winLockListener = function () {
        if (_winLockRestoring)
            return;
        if (window.scrollX === _winLockPos.x && window.scrollY === _winLockPos.y)
            return;
        _winLockRestoring = true;
        window.scrollTo(_winLockPos.x, _winLockPos.y);
        requestAnimationFrame(function () {
            _winLockRestoring = false;
        });
    };
    window.addEventListener('scroll', _winLockListener, { passive: true, capture: true });
}
function removeWinLock() {
    // Debounce: a keypad tap briefly blurs the math-field, then refocuses it.
    // Defer removal so a refocus within the window cancels it and the lock
    // stays put through the whole editing session.
    if (_winLockRemoveTimer)
        return;
    _winLockRemoveTimer = setTimeout(function () {
        _winLockRemoveTimer = null;
        if (_winLockListener) {
            window.removeEventListener('scroll', _winLockListener, { capture: true });
            _winLockListener = null;
        }
    }, 450);
}
// Proactively hold the window scroll for a short burst of animation frames.
// iOS 26 scrolls the *document* on each input (caret scroll-into-view,
// miscalculated under the canvas CSS transform). The scroll-lock listener only
// reacts to the `scroll` event, which iOS fires AFTER the frame paints — so the
// jump is briefly visible. rAF runs BEFORE paint, so re-asserting the saved
// position every frame reverts iOS's scroll before it's shown. Called from the
// input/keystroke handlers. Captures the position at call time (window is at its
// resting position then) and pins it for `frames` frames.
var _winHoldRaf = 0;
function holdWindowScrollBriefly(frames) {
    if (frames === void 0) { frames = 24; }
    var x = window.scrollX;
    var y = window.scrollY;
    if (_winHoldRaf)
        cancelAnimationFrame(_winHoldRaf);
    var n = 0;
    var tick = function () {
        if (window.scrollX !== x || window.scrollY !== y)
            window.scrollTo(x, y);
        n += 1;
        _winHoldRaf = n < frames ? requestAnimationFrame(tick) : 0;
    };
    _winHoldRaf = requestAnimationFrame(tick);
}
// Container-scroll lock — the iOS 26 counterpart of installWinLock for the
// *scroll container* rather than the window. On iPad iOS 26.5, typing into a
// focused math-field makes Safari scroll the nearest scrollable ancestor
// (`.customScrollbarContent`, the whiteboard canvas) to bring the caret into
// view — measured at dScrollTop=-364 / dScrollLeft=-565, which flings the
// editor off-screen. window.scrollY stays 0, so installWinLock never sees it;
// this pins the *element's* scrollTop/scrollLeft instead. Same singleton +
// debounced-removal contract (one listener; a keypad-tap blur/refocus must not
// tear it down and recapture the jumped position). Works for every context
// (bare canvas editor, JumpBar label, Table cell) because it locks whichever
// `.customScrollbarContent` is the editor's nearest scrollable ancestor.
var _elLockListener = null;
var _elLockEl = null;
var _elLockPos = { top: 0, left: 0 };
var _elLockRestoring = false;
var _elLockRemoveTimer = null;
function installElLock(el) {
    if (!el)
        return;
    if (_elLockRemoveTimer) {
        clearTimeout(_elLockRemoveTimer);
        _elLockRemoveTimer = null;
    }
    // Already active → keep the original (pre-jump) saved position; don't recapture.
    if (_elLockListener)
        return;
    _elLockEl = el;
    _elLockPos = { top: el.scrollTop, left: el.scrollLeft };
    _elLockListener = function () {
        if (_elLockRestoring || !_elLockEl)
            return;
        if (_elLockEl.scrollTop === _elLockPos.top && _elLockEl.scrollLeft === _elLockPos.left)
            return;
        _elLockRestoring = true;
        _elLockEl.scrollTop = _elLockPos.top;
        _elLockEl.scrollLeft = _elLockPos.left;
        requestAnimationFrame(function () {
            _elLockRestoring = false;
        });
    };
    el.addEventListener('scroll', _elLockListener, { passive: true, capture: true });
}
function removeElLock() {
    if (_elLockRemoveTimer)
        return;
    _elLockRemoveTimer = setTimeout(function () {
        _elLockRemoveTimer = null;
        if (_elLockListener && _elLockEl) {
            _elLockEl.removeEventListener('scroll', _elLockListener, { capture: true });
        }
        _elLockListener = null;
        _elLockEl = null;
    }, 450);
}
// rAF-before-paint counterpart of holdWindowScrollBriefly for the container.
// iOS fires the container `scroll` event AFTER the frame paints, so the lock
// listener alone leaves a visible 1-frame fling; re-asserting the saved
// scrollTop/Left every frame for a short burst reverts it before paint.
var _elHoldRaf = 0;
function holdElScrollBriefly(el, frames) {
    if (frames === void 0) { frames = 24; }
    if (!el)
        return;
    var top = el.scrollTop;
    var left = el.scrollLeft;
    if (_elHoldRaf)
        cancelAnimationFrame(_elHoldRaf);
    var n = 0;
    var tick = function () {
        if (el.scrollTop !== top || el.scrollLeft !== left) {
            el.scrollTop = top;
            el.scrollLeft = left;
        }
        n += 1;
        _elHoldRaf = n < frames ? requestAnimationFrame(tick) : 0;
    };
    _elHoldRaf = requestAnimationFrame(tick);
}
/**
 * Safely focus a MathfieldElement, catching MathLive internal errors.
 * Firefox: MathLive's onBlur handler crashes accessing ariaLiveText.textContent
 * when ariaLiveText hasn't been initialized in the shadow DOM yet.
 */
var safeFocusMathField = function (el) {
    try {
        if (el.isConnected) {
            // Ensure ariaLiveText exists before focus triggers blur/focus cycle
            if (el.tagName === 'MATH-FIELD') {
                ensureAriaLiveText(el);
            }
            el.focus({ preventScroll: true });
        }
    }
    catch (_) {
        // MathLive internal error (e.g., ariaLiveText undefined in Firefox)
    }
};
/**
 * Walk backwards from the current cursor position to find a math-field element.
 * Handles cases where spacer text nodes (WHITESPACE_CHAR) sit between the
 * cursor and the math-field. On iPad the cursor often lands inside these
 * spacers, so we need to look past them.
 */
var findMathFieldBeforeCursor = function () {
    var _a, _b, _c;
    var sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0)
        return null;
    var range = sel.getRangeAt(0);
    var startContainer = range.startContainer, startOffset = range.startOffset;
    // Case 1: cursor is at the editor (element) level — check child before offset
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
        var node = startContainer.childNodes[startOffset - 1];
        if ((node === null || node === void 0 ? void 0 : node.nodeName) === 'MATH-FIELD')
            return node;
        // If the node before is a spacer text node, look one further back
        if ((node === null || node === void 0 ? void 0 : node.nodeType) === Node.TEXT_NODE) {
            var prev = node.previousSibling;
            if ((prev === null || prev === void 0 ? void 0 : prev.nodeName) === 'MATH-FIELD')
                return prev;
        }
        return null;
    }
    // Case 2: cursor is inside a text node
    if (startContainer.nodeType === Node.TEXT_NODE) {
        var textNode = startContainer;
        var textBefore = (_b = (_a = textNode.textContent) === null || _a === void 0 ? void 0 : _a.slice(0, startOffset)) !== null && _b !== void 0 ? _b : '';
        // Only look past the text node if all characters before the cursor are
        // whitespace / zero-width spaces (i.e. cursor is effectively at the
        // boundary of a spacer node, not in the middle of real content)
        var isOnlySpacers = textBefore.replace(/[\u200B\s]/g, '').length === 0;
        if (startOffset === 0 || isOnlySpacers) {
            // Walk backwards through previous siblings, skipping spacer text nodes
            var prev = textNode.previousSibling;
            // If cursor is at offset 0, also check the current node's previous sibling
            // If cursor is inside a spacer, start from currentNode.previousSibling
            while (prev) {
                if (prev.nodeName === 'MATH-FIELD')
                    return prev;
                // Skip over whitespace/spacer text nodes
                if (prev.nodeType === Node.TEXT_NODE &&
                    ((_c = prev.textContent) !== null && _c !== void 0 ? _c : '').replace(/[\u200B\s]/g, '').length === 0) {
                    prev = prev.previousSibling;
                    continue;
                }
                break; // hit real content — stop looking
            }
        }
    }
    return null;
};
/**
 * Walk forward from the current cursor position to find a math-field element.
 * Symmetric counterpart to findMathFieldBeforeCursor — used so ArrowRight at
 * a position immediately to the LEFT of a math-field enters it at offset 0.
 */
var findMathFieldAfterCursor = function () {
    var _a, _b, _c;
    var sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0)
        return null;
    var range = sel.getRangeAt(0);
    var startContainer = range.startContainer, startOffset = range.startOffset;
    // Case 1: cursor at element level — check the child at this offset
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
        var node = startContainer.childNodes[startOffset];
        if ((node === null || node === void 0 ? void 0 : node.nodeName) === 'MATH-FIELD')
            return node;
        if ((node === null || node === void 0 ? void 0 : node.nodeType) === Node.TEXT_NODE) {
            var next = node.nextSibling;
            if ((next === null || next === void 0 ? void 0 : next.nodeName) === 'MATH-FIELD')
                return next;
        }
        return null;
    }
    // Case 2: cursor inside a text node — only walk forward if the chars
    // after the cursor are all whitespace / spacer (cursor is effectively
    // at the boundary, not in the middle of real content)
    if (startContainer.nodeType === Node.TEXT_NODE) {
        var textNode = startContainer;
        var textAfter = (_b = (_a = textNode.textContent) === null || _a === void 0 ? void 0 : _a.slice(startOffset)) !== null && _b !== void 0 ? _b : '';
        var isOnlySpacers = textAfter.replace(/[​\s]/g, '').length === 0;
        if (startOffset === textNode.length || isOnlySpacers) {
            var next = textNode.nextSibling;
            while (next) {
                if (next.nodeName === 'MATH-FIELD')
                    return next;
                if (next.nodeType === Node.TEXT_NODE &&
                    ((_c = next.textContent) !== null && _c !== void 0 ? _c : '').replace(/[​\s]/g, '').length === 0) {
                    next = next.nextSibling;
                    continue;
                }
                break;
            }
        }
    }
    return null;
};
/**
 * Parses a MathLive LaTeX string and returns whether it represents a single
 * valid number (integer, fraction, or mixed number) and its value.
 *
 * Examples:
 *   "3"              → { isValid: true,  value: 3 }
 *   "\frac{2}{3}"    → { isValid: true,  value: { num: 2, deno: 3 } }
 *   "1\frac{2}{3}"   → { isValid: true,  value: { num: 5, deno: 3 } }
 *   "-2\frac{1}{4}"  → { isValid: true,  value: { num: -9, deno: 4 } }
 *   "x+1"            → { isValid: false, value: null }
 */
/**
 * Convert a plain-text equation string (legacy "equation" type) to LaTeX.
 * Replaces `a/b` (integers or decimals) with `\frac{a}{b}`.
 * Example: "30/60 + 15/60 =" → "\frac{30}{60} + \frac{15}{60} ="
 */
export var equationStringToLatex = function (text) {
    return text.replace(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/g, '\\frac{$1}{$2}');
};
// MathLive renders `\placeholder{}` as a ▢ box, but only inside a real
// math-field. When a standalone placeholder box leaks into a plain text / HTML
// node (e.g. a manipulative label where MathLive's insert dispatches raw LaTeX
// into the contenteditable), it shows the literal characters "\placeholder{}"
// instead. Normalising it to the same symbol MathLive uses (U+25A2 ▢) keeps
// the visible box without needing a math-field. Fractions are unaffected —
// their placeholder lives inside a `type:'math'` item rendered by MathLive.
var PLACEHOLDER_BOX_SYMBOL = '▢'; // ▢ — matches MathLive placeholderSymbol
var normalizePlaceholderText = function (s) {
    return s ? s.replace(/\\placeholder\{[^}]*\}/g, PLACEHOLDER_BOX_SYMBOL) : s;
};
export var parseNumericLatex = function (latex) {
    var s = latex.replace(/[\s\u200B\u200C\u200D\uFEFF]/g, '');
    // MathLive's `mf.value` wraps content in `$ ... $` math delimiters.
    // Strip leading/trailing `$` (and `$$`) so the regexes below can match.
    s = s.replace(/^\$+|\$+$/g, '');
    // Normalise the LaTeX shorthand `\fracND` (no braces around single-token
    // arguments) to the canonical `\frac{N}{D}` form. MathLive emits the
    // shorthand whenever an argument is a single token, e.g. `\frac{1}{2}`
    // is serialized as `\frac12`. Three variants must be handled:
    // both-shorthand `\fracAB`, num-only `\fracA{B}`, deno-only `\frac{A}B`.
    // Run repeatedly until stable so combos like `2\frac34` and
    // `\frac1{12}` resolve fully.
    var prev = '';
    while (prev !== s) {
        prev = s;
        s = s.replace(/\\frac([0-9])([0-9])/g, '\\frac{$1}{$2}');
        s = s.replace(/\\frac([0-9])\{/g, '\\frac{$1}{');
        s = s.replace(/\\frac(\{[^{}]*\})([0-9])/g, '\\frac$1{$2}');
    }
    // Integer: e.g. "3", "-5"
    if (/^-?\d+$/.test(s)) {
        return { isValid: true, value: parseInt(s, 10) };
    }
    // Pure fraction: e.g. "\frac{2}{3}", "-\frac{2}{3}"
    var fracMatch = s.match(/^(-?)\\frac\{(\d+)\}\{(\d+)\}$/);
    if (fracMatch) {
        var sign = fracMatch[1] === '-' ? -1 : 1;
        var num = parseInt(fracMatch[2], 10) * sign;
        var deno = parseInt(fracMatch[3], 10);
        if (deno === 0)
            return { isValid: false, value: null };
        return { isValid: true, value: { num: num, deno: deno } };
    }
    // Mixed number: e.g. "1\frac{2}{3}", "-2\frac{1}{4}"
    var mixedMatch = s.match(/^(-?)(\d+)\\frac\{(\d+)\}\{(\d+)\}$/);
    if (mixedMatch) {
        var sign = mixedMatch[1] === '-' ? -1 : 1;
        var whole = parseInt(mixedMatch[2], 10);
        var numer = parseInt(mixedMatch[3], 10);
        var deno = parseInt(mixedMatch[4], 10);
        if (deno === 0)
            return { isValid: false, value: null };
        var num = (whole * deno + numer) * sign;
        return { isValid: true, value: { num: num, deno: deno } };
    }
    return { isValid: false, value: null };
};
var MathEditor = forwardRef(function (props, ref) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    var editorRef = useRef(null);
    var keypadWrapperRef = useRef(null);
    var _u = useState(false), mlReady = _u[0], setMlReady = _u[1];
    var _v = useState(false), mathKeyboardVisible = _v[0], setmathKeyboardVisible = _v[1];
    var keypadTriggerRef = useRef(null);
    var _w = useState({ top: 0, left: 0 }), keypadPos = _w[0], setKeypadPos = _w[1];
    var _x = globalStore.useStoreState(function (st) { return st.playerStore; }), canvasHeight = _x.canvasHeight, zoomLevel = _x.zoomLevel;
    var _y = useContext(DataContext), setText = _y.setText, selectedTool = _y.selectedTool, setSelectedTool = _y.setSelectedTool, color = _y.color, setContextMenu = _y.setContextMenu;
    var _z = props.setActiveLink, setActiveLink = _z === void 0 ? function () { } : _z, _0 = props.idPreffix, idPreffix = _0 === void 0 ? 'newMath' : _0, index = props.index, data = props.data, _1 = props.isTextUpdate, isTextUpdate = _1 === void 0 ? true : _1, cb = props.cb, keypadButtons = props.keypadButtons, bottomBtns = props.bottomBtns, _2 = props.viewonly, viewonly = _2 === void 0 ? false : _2, _3 = props.selectOnViewOnly, selectOnViewOnly = _3 === void 0 ? false : _3, className = props.className, _4 = props.disableTransform, disableTransform = _4 === void 0 ? false : _4, setActiveMathText = props.setActiveMathText, _5 = props.isManipulative, isManipulative = _5 === void 0 ? false : _5, _6 = props.style, style = _6 === void 0 ? {} : _6, _7 = props.throwChangeOnBlur, throwChangeOnBlur = _7 === void 0 ? false : _7, _8 = props.maxCharacters, maxCharacters = _8 === void 0 ? undefined : _8, colorProp = props.color, onChange = props.onChange, _9 = props.onFocusEvent, onFocusEvent = _9 === void 0 ? false : _9, _10 = props.placeHolder, placeHolder = _10 === void 0 ? 'Type' : _10, onKeyDown = props.onKeyDown, _11 = props.dataPlaceholder, dataPlaceholder = _11 === void 0 ? undefined : _11, _12 = props.offsetKeypadByEditorHeight, offsetKeypadByEditorHeight = _12 === void 0 ? false : _12, _13 = props.blockNewLine, blockNewLine = _13 === void 0 ? false : _13, onRemove = props.onRemove, onClickWhenLocked = props.onClickWhenLocked, _14 = props.scrollIntoViewOnFocus, scrollIntoViewOnFocus = _14 === void 0 ? false : _14, _15 = props.keepSelectedOnInternalBlur, keepSelectedOnInternalBlur = _15 === void 0 ? false : _15, _16 = props.enableFocusPanel, enableFocusPanel = _16 === void 0 ? false : _16, _17 = props.stickyKeypadAfterInsertOnTouch, stickyKeypadAfterInsertOnTouch = _17 === void 0 ? false : _17, _18 = props.suppressAutoFocusWhenToolbarActive, suppressAutoFocusWhenToolbarActive = _18 === void 0 ? false : _18, onTab = props.onTab, _19 = props.outerAriaHidden, outerAriaHidden = _19 === void 0 ? false : _19, _20 = props.suppressOuterGroup, suppressOuterGroup = _20 === void 0 ? false : _20, editorAriaLabel = props.editorAriaLabel, onTouchFocusSelf = props.onTouchFocusSelf;
    var selectionType = "".concat(idPreffix, "_").concat(index);
    // Equation items use a display font size, but that number is typically sourced
    // from a production format where values like 130 are authoring-scale.
    // Render at 40 % of the stored size so the text fits the canvas naturally.
    var effectiveFontSize = (data === null || data === void 0 ? void 0 : data.type) === 'equation' ? Math.round((((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.size) || 20) * 0.4) : ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.size) || 20;
    var onTabRef = useRef(onTab);
    useEffect(function () { onTabRef.current = onTab; }, [onTab]);
    var lastFocusedMathField = useRef(null);
    var lastSelection = useRef(null);
    var lastCursorPosition = useRef(null);
    var timeoutRef = useRef(null);
    var onChangeSaveTimerRef = useRef(null);
    var textBeforeEditRef = useRef(null);
    var textEditHistoryIndexRef = useRef(-1);
    var wasFocusedRef = useRef(false);
    var getMathDataRef = useRef(null);
    var mathFieldInstancesRef = useRef(new Set());
    // Per-math-field cache of the last announced caret-in-fraction position
    // ('above' = numerator, 'below' = denominator, 'outside' = elsewhere).
    // Used by the selectionchange listener to announce only when the caret
    // transitions between numerator / denominator / outside — not on every
    // keystroke, which would flood the SR. WeakMap so the entries are
    // garbage-collected with the math-field they key on.
    var lastFracBranchRef = useRef(new WeakMap());
    var isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    var insertMathGuardRef = useRef(0);
    // Timestamp of the last setMathData DOM rebuild (initial mount, duplication,
    // or a full content reset). A duplicated text item is auto-selected
    // (isFocused=true) as soon as it mounts, and re-inserting its math-fields
    // into the DOM can trigger a stray native focus on one of them before the
    // user has clicked anything — without this guard that stray focus would
    // satisfy the isFocused check below and pop the math keypad open unprompted.
    var justHydratedRef = useRef(0);
    var keypadGuardRef = useRef(0);
    // Set when the user opens the keypad via the Math Keypad toolbar button.
    // The next time the keypad becomes visible (after React renders it and
    // keypadWrapperRef is attached), we'll move focus to the first keypad
    // button ('1') instead of leaving it on the math-field. One-shot — the
    // useEffect that consumes it clears it back to false.
    var focusKeypadOnOpenRef = useRef(false);
    var isColorChangeFocusRef = useRef(false);
    // Timestamp of last touchstart on keypad. Used to skip the synthetic
    // mousedown that iPad Safari fires ~300ms after a touch — the 80ms
    // debounce in keypadBtnClickHandler is too short to catch it.
    var keypadTouchRef = useRef(0);
    // Long-press timer for programmatic text selection on iPad.
    // If native selection doesn't trigger within 500ms of a touch, we
    // select the word under the touch point using caretRangeFromPoint.
    var longPressTouchRef = useRef(null);
    var longPressTouchPosRef = useRef(null);
    // Mirror mathKeyboardVisible and isFocused in refs so the data-sync useEffect
    // can read the latest values without adding them to its dependency array.
    var mathKeyboardVisibleRef = useRef(false);
    var isFocusedRef = useRef(false);
    // Visually-hidden aria-live region for announcing fraction position (numerator/denominator)
    var fracPositionLiveRef = useRef(null);
    // Focus-options popover: when the editorDiv is reached via keyboard (Tab),
    // don't drop the caret straight into contentEditable. Show a small menu
    // with "Enter editing mode" + "Open options" so SR / keyboard users have
    // a clear choice (WCAG 4.1.2 + iPad VoiceOver best practice).
    var focusOptionsRef = useRef(null);
    // When the text box sits near the top of the canvas, the popover (which
    // renders above the box) can have nowhere to scroll to — the scroll
    // container's scrollTop can't go negative. In that case render it below
    // the box instead of leaving it clipped.
    var _21 = useState(false), focusPanelRight = _21[0], setFocusPanelRight = _21[1];
    // true when the next focus on editorDiv came from a keyboard event.
    // Reset after each focus is handled. Mouse clicks skip the popover.
    var focusFromKeyboardRef = useRef(false);
    // true once the user has chosen "edit" — suppresses the popover until blur.
    var editingAllowedRef = useRef(false);
    var viewOnlyRef = useRef(viewonly);
    var clickedAnchorRef = useRef(null);
    // Compute isFocused early so its ref sync fires BEFORE the data?.data?.text
    // useEffect (React fires effects in declaration order).
    var isFocused = selectedTool.length === 1 && selectedTool[0] === selectionType;
    // TEMP DIAGNOSTIC (iOS 26 jump). __jumpSnap(label, el, id) samples the element
    // + window + scroll-container for N frames and logs any frame that moves >2px
    // plus a DONE summary; a capture 'scroll' listener logs what scrolls. This
    // pinned the cause to `.customScrollbarContent` being scrolled by iOS on
    // input (fixed via installElLock/holdElScrollBriefly). Left commented out so
    // it can be re-enabled quickly if the jump ever regresses.
    /* eslint-disable */
    /*
    useEffect(() => {
      if ((window as any).__jumpInstalled) return;
      (window as any).__jumpInstalled = true;
      // Snapshot every scrollable ancestor of `el` (scrollTop/scrollLeft) so we
      // can detect WHICH container scrolls — not just the two we guessed before.
      const ancestorScrolls = (el: HTMLElement | null) => {
        const out: { node: HTMLElement; top: number; left: number; tag: string }[] = [];
        let n: HTMLElement | null = el;
        while (n && n !== document.body && n !== document.documentElement) {
          if (n.scrollHeight > n.clientHeight + 1 || n.scrollWidth > n.clientWidth + 1) {
            const cls =
              typeof n.className === 'string' && n.className
                ? `.${n.className.trim().split(/\s+/).slice(0, 2).join('.')}`
                : n.tagName;
            out.push({ node: n, top: Math.round(n.scrollTop), left: Math.round(n.scrollLeft), tag: cls });
          }
          n = n.parentElement;
        }
        out.push({ node: document.scrollingElement as HTMLElement, top: Math.round(window.scrollY), left: Math.round(window.scrollX), tag: 'WINDOW' });
        return out;
      };
      const offs = (el: HTMLElement | null) => {
        const r = el?.getBoundingClientRect();
        // The whole JumpBar widget (not just the inner editor) — if this moves,
        // the widget itself is being repositioned, not scrolled.
        const wrap = el?.closest('.jumpBarManipulative') as HTMLElement | null;
        const wr = wrap?.getBoundingClientRect();
        const label = el?.closest('.jumpBarLabelContent') as HTMLElement | null;
        const vv = (window as any).visualViewport;
        return {
          elTop: r ? Math.round(r.top) : -1,
          elLeft: r ? Math.round(r.left) : -1,
          wrapTop: wr ? Math.round(wr.top) : -1,
          // iOS shifts the *visual* viewport (not window.scrollY) when it tries to
          // open the native keyboard on focus → elements move with scrollY=0.
          vvTop: vv ? Math.round(vv.offsetTop) : -1,
          vvH: vv ? Math.round(vv.height) : -1,
          labelH: label ? Math.round(label.offsetHeight) : -1,
        };
      };
      (window as any).__jumpSnap = (label: string, el: HTMLElement | null, id: string) => {
        const base = offs(el);
        const baseScrolls = ancestorScrolls(el);
        const FRAMES = 120;
        let frame = 0;
        let maxEl = 0;
        const sample = () => {
          frame++;
          const o = offs(el);
          const dEl = o.elTop - base.elTop;
          const dElL = o.elLeft - base.elLeft;
          const dWrap = o.wrapTop - base.wrapTop;
          const dVv = o.vvTop - base.vvTop;
          const dVvH = o.vvH - base.vvH;
          const dLh = o.labelH - base.labelH;
          // Which ancestor scrolled since t0?
          const nowScrolls = ancestorScrolls(el);
          const moved = baseScrolls
            .map((b, i) => ({ tag: b.tag, dT: (nowScrolls[i]?.top ?? b.top) - b.top, dL: (nowScrolls[i]?.left ?? b.left) - b.left }))
            .filter(s => Math.abs(s.dT) > 2 || Math.abs(s.dL) > 2)
            .map(s => `${s.tag}[dT=${s.dT},dL=${s.dL}]`)
            .join(' ');
          if (Math.abs(dEl) > Math.abs(maxEl)) maxEl = dEl;
          if (Math.abs(dEl) > 2 || Math.abs(dElL) > 2 || Math.abs(dWrap) > 2 || Math.abs(dVv) > 2 || Math.abs(dVvH) > 2 || Math.abs(dLh) > 2 || moved || frame === FRAMES) {
            console.log(`[JUMP:${label}] ${id} f${frame} dElTop=${dEl} dElLeft=${dElL} dWrapTop=${dWrap} dVvTop=${dVv} dVvH=${dVvH} dLabelH=${dLh} scrolled={${moved}}`);
          }
          if (frame < FRAMES) requestAnimationFrame(sample);
          else console.log(`[JUMP:${label}] ${id} DONE maxElTopMove=${maxEl}`);
        };
        console.log(`[JUMP:${label}] ${id} @t0 elTop=${base.elTop} wrapTop=${base.wrapTop} vvTop=${base.vvTop} vvH=${base.vvH} labelH=${base.labelH} scrollers=[${baseScrolls.map(s => `${s.tag}:${s.top}/${s.left}`).join(', ')}]`);
        requestAnimationFrame(sample);
      };
      const onScroll = (e: Event) => {
        const t = e.target as any;
        const node = t === document ? document.scrollingElement : t;
        const cls =
          node && typeof node.className === 'string' && node.className
            ? `.${node.className.trim().split(/\s+/).join('.')}`
            : node?.tagName;
        console.log('[JUMP:scroll]', cls, { top: Math.round(node?.scrollTop ?? 0), winY: Math.round(window.scrollY) });
      };
      document.addEventListener('scroll', onScroll, true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    */
    /* eslint-enable */
    useEffect(function () {
        mathKeyboardVisibleRef.current = mathKeyboardVisible;
        customEventHandler.dispatchEvent('mathKeyboardToggle', {
            visible: mathKeyboardVisible,
        });
        // If the keypad just opened via the Math Keypad toolbar button, move
        // focus from the freshly-inserted mf to the first keypad button ('1')
        // so the user can drive math input from the keyboard immediately.
        if (mathKeyboardVisible && focusKeypadOnOpenRef.current) {
            focusKeypadOnOpenRef.current = false;
            // rAF lets React paint the keypad and attach keypadWrapperRef before
            // we query for the first button.
            requestAnimationFrame(function () {
                var _a;
                var firstBtn = (_a = keypadWrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not([disabled]):not([tabindex="-1"])');
                if (firstBtn)
                    firstBtn.focus();
            });
        }
    }, [mathKeyboardVisible]);
    // Track whether the last user-originated input was keyboard or pointer.
    // We listen globally (capture phase) so we set the flag BEFORE the
    // editorDiv's focus handler runs.
    useEffect(function () {
        var onKey = function (e) {
            if (e.key === 'Tab')
                focusFromKeyboardRef.current = true;
        };
        var onPointer = function (e) {
            var _a;
            focusFromKeyboardRef.current = false;
            // Only count as editing consent if the click is on the editor itself,
            // not on popover buttons (pencil, kebab) — those are inside focusOptionsRef.
            var target = e.target;
            if ((_a = focusOptionsRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            editingAllowedRef.current = true;
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('mousedown', onPointer, true);
        document.addEventListener('touchstart', onPointer, true);
        return function () {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('mousedown', onPointer, true);
            document.removeEventListener('touchstart', onPointer, true);
        };
    }, []);
    // Auto-focus the first button in the focusOptions dialog when it opens via keyboard.
    useEffect(function () {
        if (!isFocused || !enableFocusPanel || mathKeyboardVisible)
            return;
        setFocusPanelRight(false); // re-measure fresh each time the panel opens
        var raf2 = 0;
        // Double rAF: a single setTimeout(0)/rAF can fire before the browser has
        // finished laying out and painting the just-shown panel at the current
        // zoom scale, so the first getBoundingClientRect() read is stale — this
        // was why the correct scroll only kicked in after a few extra clicks.
        // Waiting two frames guarantees layout/paint has settled before measuring.
        var raf1 = requestAnimationFrame(function () {
            raf2 = requestAnimationFrame(function () {
                var panel = focusOptionsRef.current;
                var firstBtn = panel === null || panel === void 0 ? void 0 : panel.querySelector('button');
                if (!panel)
                    return;
                // Only steal focus into the panel when it was actually opened via
                // keyboard (Tab) — matching this effect's own intent. Without this
                // check the effect ran unconditionally on every mathKeyboardVisible
                // → false transition (e.g. clicking from a math-field into plain
                // text in the same box), stealing focus to the pencil button and
                // triggering MathLive's internal focus-recovery to reclaim it right
                // back into the math-field, reopening the keypad the user just left.
                if (firstBtn && focusFromKeyboardRef.current) {
                    // preventScroll: native focus() triggers scroll-into-view that miscalculates
                    // offsets under .mainHolder's transform: scale(zoomLevel), same root cause as
                    // the mf.scrollIntoView no-op above. We still want the panel brought into view,
                    // so do it manually below with zoom-corrected math instead of skipping it.
                    firstBtn.focus({ preventScroll: true });
                }
                var scrollParent = panel.closest('[class*="scrollbar"], [class*="Scrollbar"]');
                if (!scrollParent)
                    return;
                var panelRect = panel.getBoundingClientRect();
                var parentRect = scrollParent.getBoundingClientRect();
                // The scroll container's own box can extend visually behind the sidebar
                // overlay, so comparing against parentRect.left alone under-reports
                // clipping when the box sits at the canvas's left edge — the sidebar
                // still covers the panel even though parentRect says there's room.
                // Clamp the effective left bound to whichever is further right.
                var sidebarEl = document.querySelector('.sidebar');
                var visibleLeft = sidebarEl
                    ? Math.max(parentRect.left, sidebarEl.getBoundingClientRect().right)
                    : parentRect.left;
                // The transform: scale(zoomLevel) lives on .mainHolder, a DESCENDANT of this
                // scroll container — per the CSS transform spec, the scrollable-overflow
                // region is computed from the descendant's post-transform (visual) box, so
                // scrollTop/scrollLeft already move in the same visual-pixel units as
                // getBoundingClientRect. Dividing by zoomLevel here under-corrects: empirically
                // it left ~(1 - 1/scale) of the offset every time, requiring ~7 clicks to
                // converge at 150% zoom.
                // behavior: 'auto' (instant), not 'smooth' — a smooth scroll is still
                // mid-animation when the user's very next click re-measures against
                // that in-flight scrollTop/scrollLeft, so only part of the correction
                // lands and it takes several clicks to fully converge.
                //
                // The panel renders directly to the left of the text box (vertically
                // centered on it), so it can still clip vertically (a tall box, or one
                // near the top/bottom canvas edge) as well as horizontally (near the
                // left edge) — correct both axes in one scrollTo call.
                var deltaLeft = 0;
                var deltaTop = 0;
                if (panelRect.left < visibleLeft) {
                    // Panel renders left of the text box. If the box sits near the left edge
                    // of the canvas (or under the sidebar), scrollLeft can't go negative
                    // enough to fully reveal it — in that case flip the panel to the box's
                    // right instead of leaving it clipped.
                    var neededLeft = visibleLeft - panelRect.left;
                    if (neededLeft > scrollParent.scrollLeft) {
                        setFocusPanelRight(true);
                    }
                    else {
                        deltaLeft = -neededLeft;
                    }
                }
                else if (panelRect.right > parentRect.right) {
                    deltaLeft = panelRect.right - parentRect.right;
                }
                if (panelRect.top < parentRect.top) {
                    deltaTop = -(parentRect.top - panelRect.top);
                }
                else if (panelRect.bottom > parentRect.bottom) {
                    deltaTop = panelRect.bottom - parentRect.bottom;
                }
                if (deltaLeft !== 0 || deltaTop !== 0) {
                    scrollParent.scrollTo({
                        left: scrollParent.scrollLeft + deltaLeft,
                        top: scrollParent.scrollTop + deltaTop,
                        behavior: 'auto',
                    });
                }
            });
        });
        return function () {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
    }, [isFocused, enableFocusPanel, zoomLevel, mathKeyboardVisible]);
    useEffect(function () {
        var _a, _b;
        isFocusedRef.current = isFocused;
        var touchFocusRafId = 0;
        // Reset editing-allowed gate when the editor loses selection.
        if (!isFocused) {
            editingAllowedRef.current = false;
        }
        if (!isFocused) {
            // Only run cleanup when transitioning from focused→unfocused,
            // not on initial mount. Mounting an unfocused MathEditor should
            // not clear the global selection (which would destroy the cursor
            // in a sibling editor that the user is actively typing in).
            if (wasFocusedRef.current) {
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                // Clear any lingering text selection when editor loses focus
                (_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
                var editor = editorRef.current;
                if (editor) {
                    var emptyMfs_1 = [];
                    editor.querySelectorAll('math-field').forEach(function (node) {
                        var mf = node;
                        if ((mf.value || '').trim() === '') {
                            emptyMfs_1.push(mf);
                        }
                    });
                    if (emptyMfs_1.length > 0) {
                        emptyMfs_1.forEach(function (mf) {
                            mathFieldInstancesRef.current.delete(mf);
                            mf.remove();
                        });
                        batchDisposeMathfields(emptyMfs_1);
                        editor.normalize();
                    }
                }
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(function () {
                    timeoutRef.current = null;
                    var content = calcMathData();
                    if (content.length === 0 && onRemove) {
                        onRemove();
                    }
                    else {
                        getMathData();
                    }
                }, 100);
            }
        }
        else if (isFocused && !wasFocusedRef.current) {
            // Transitioning to focused: capture text state before any editing starts
            if (isTextUpdate && typeof index === 'number' && !isManipulative) {
                var rawText = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.text;
                var textArr = Array.isArray(rawText)
                    ? rawText
                    : rawText
                        ? [{ type: 'html', value: rawText }]
                        : [];
                textBeforeEditRef.current = JSON.stringify(textArr);
            }
            // On touch devices (iPad), the first tap selects the item (blue border)
            // but iPad Safari doesn't reliably activate the editing session — even
            // when the DIV is document.activeElement there may be no cursor and
            // keystrokes won't register. Always focus + place a collapsed selection
            // at the end to force the editing session open.
            if (isTouchDevice && editorRef.current && !viewonly) {
                touchFocusRafId = requestAnimationFrame(function () {
                    var editor = editorRef.current;
                    if (!editor)
                        return;
                    editor.focus({ preventScroll: true });
                    // Place a collapsed selection at the end so iPad shows the cursor
                    var sel = window.getSelection();
                    if (sel && (sel.rangeCount === 0 || !editor.contains(sel.anchorNode))) {
                        var range = document.createRange();
                        range.selectNodeContents(editor);
                        range.collapse(false); // collapse to end
                        sel.removeAllRanges();
                        sel.addRange(range);
                    }
                });
            }
        }
        if (isFocused && !wasFocusedRef.current) {
            // Emit alignment state immediately when this editor gains focus so
            // BottomPanel alignment buttons reflect the current editor's alignment.
            emitFormattingState(null);
        }
        if (!isFocused && wasFocusedRef.current) {
            customEventHandler.dispatchEvent('formattingStateChanged', {
                bold: false,
                italic: false,
                underline: false,
                leftAlign: false,
                centerAlign: false,
                rightAlign: false,
            });
            // Save text state on focus-out for whiteboard MathEditors (text state in GameController).
            // Only save when there is actual content and the text changed since focus-in.
            if (isTextUpdate &&
                typeof index === 'number' &&
                !isManipulative &&
                textBeforeEditRef.current !== null) {
                var output = calcMathData();
                var hasContent = output.length > 0 &&
                    output.some(function (item) {
                        if (item.type === 'math')
                            return !!(item.latex && item.latex.trim() !== '');
                        return ((item.value || '')
                            .replace(/<[^>]*>/g, '')
                            .replace(/\u200B/g, '')
                            .trim() !== '');
                    });
                var newSerialized = JSON.stringify(output);
                if (hasContent && textBeforeEditRef.current !== newSerialized) {
                    var newFullItem = __assign(__assign({}, data), { data: __assign(__assign({}, data.data), { text: output }) });
                    saveState({
                        type: "".concat(idPreffix, "_").concat(index),
                        id: index,
                        propertyName: 'textContent',
                        old: textBeforeEditRef.current,
                        new: JSON.stringify(newFullItem),
                    });
                }
                textBeforeEditRef.current = null;
            }
        }
        wasFocusedRef.current = isFocused;
        return function () { cancelAnimationFrame(touchFocusRafId); };
    }, [isFocused]);
    // Re-emit when alignment prop changes (after clicking an alignment button) so
    // BottomPanel reflects the new alignment while this editor is focused.
    useEffect(function () {
        if (!isFocusedRef.current)
            return;
        emitFormattingState(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.alignment]);
    useEffect(function () {
        viewOnlyRef.current = viewonly;
        // Disable/enable all existing mathfield elements when viewonly changes
        var editor = editorRef.current;
        if (editor) {
            editor.querySelectorAll('math-field').forEach(function (mf) {
                mf.readonly = !!viewonly;
            });
        }
    }, [viewonly]);
    // Get total character count in the editor (text nodes + math field values)
    var getTotalCharacterCount = function (ignoreSpaces) {
        if (ignoreSpaces === void 0) { ignoreSpaces = false; }
        var editor = editorRef.current;
        if (!editor)
            return 0;
        var count = 0;
        editor.childNodes.forEach(function (node) {
            var _a;
            if (node.nodeType === Node.TEXT_NODE) {
                var text = (((_a = node.textContent) === null || _a === void 0 ? void 0 : _a.replace(/\u200B/g, '')) || '').trim();
                if (ignoreSpaces)
                    text = text.replace(/\s+/g, '');
                count += text.length;
            }
            else if (node.nodeName === 'MATH-FIELD') {
                var mf = node;
                var latexValue = (mf.value || '').trim();
                // Skip truly empty mathfields (placeholder counts as 1 visible box)
                if (latexValue !== '') {
                    count += countVisibleCharsInLatex(latexValue, ignoreSpaces);
                }
            }
            else if (node.nodeType === Node.ELEMENT_NODE) {
                var el = node;
                // For HTML elements, count only text content (excluding child math fields)
                // Get direct text nodes only
                var textLength_1 = 0;
                el.childNodes.forEach(function (child) {
                    var _a;
                    if (child.nodeType === Node.TEXT_NODE) {
                        var text = ((_a = child.textContent) === null || _a === void 0 ? void 0 : _a.replace(/\u200B/g, '')) || '';
                        if (ignoreSpaces)
                            text = text.trim();
                        textLength_1 += text.length;
                    }
                    // Don't count math fields here as they're counted separately
                });
                count += textLength_1;
            }
        });
        return count;
    };
    // Returns true when the cursor in `mf` is inside a \frac argument.
    // Used to bypass the >= maxCharacters block so each fraction side can
    // independently reach the limit (the input-event revert handler enforces
    // the per-side limit using > rather than >=).
    // Uses MathLive's internal atom model to walk up the parent chain and
    // check for a 'genfrac' ancestor — getValue(0, pos) is unreliable when
    // the cursor is nested inside a fraction group.
    var isInFrac = function (mf) {
        var _a;
        try {
            var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
            if (!model)
                return false;
            var atom = model.at(model.position);
            while (atom && atom.type !== 'root') {
                if (atom.type === 'genfrac')
                    return true;
                atom = atom.parent;
            }
        }
        catch (_b) {
            // model API not accessible — fall through
        }
        return false;
    };
    // Returns 'numerator' | 'denominator' | null based on where the cursor sits
    // inside a \frac. MathLive stores the numerator in the 'above' branch and
    // the denominator in the 'below' branch of a genfrac atom.
    var getFracBranch = function (mf) {
        var _a, _b, _c;
        try {
            var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
            if (!model)
                return null;
            var atom = model.at(model.position);
            while (atom && atom.type !== 'root') {
                if (((_b = atom.parent) === null || _b === void 0 ? void 0 : _b.type) === 'genfrac') {
                    var branch = (_c = atom.treeBranch) !== null && _c !== void 0 ? _c : atom.branch;
                    if (branch === 'above')
                        return 'numerator';
                    if (branch === 'below')
                        return 'denominator';
                }
                atom = atom.parent;
            }
        }
        catch (_d) {
            // model API not accessible
        }
        return null;
    };
    // Announce the fraction position (numerator / denominator) to screen readers
    // via the polite live region. Called on selectionchange and focusin.
    var announceFracPosition = function (mf) {
        var branch = getFracBranch(mf);
        var liveEl = fracPositionLiveRef.current;
        if (!liveEl)
            return;
        if (branch === 'numerator') {
            liveEl.textContent = t('text.mathEditor.fracNumerator');
        }
        else if (branch === 'denominator') {
            liveEl.textContent = t('text.mathEditor.fracDenominator');
        }
        else {
            liveEl.textContent = '';
        }
    };
    var unwrap = function (command, latex) {
        var prefix = "\\".concat(command, "{");
        var result = '';
        var i = 0;
        while (i < latex.length) {
            var idx = latex.indexOf(prefix, i);
            if (idx === -1) {
                result += latex.slice(i);
                break;
            }
            result += latex.slice(i, idx);
            // Find matching closing brace by counting brace depth
            var depth = 1;
            var j = idx + prefix.length;
            while (j < latex.length && depth > 0) {
                if (latex[j] === '{')
                    depth++;
                else if (latex[j] === '}')
                    depth--;
                j++;
            }
            // Extract inner content (between opening { and matching })
            result += latex.slice(idx + prefix.length, j - 1);
            i = j;
        }
        return result;
    };
    var wrap = function (command, latex) { return "\\".concat(command, "{").concat(latex, "}"); };
    // Font size steps for text (in pixels)
    var FONT_SIZE_STEPS = [12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72];
    // LaTeX size commands (smallest to largest)
    var LATEX_SIZE_COMMANDS = [
        'tiny',
        'scriptsize',
        'footnotesize',
        'small',
        'normalsize',
        'large',
        'Large',
        'LARGE',
        'huge',
        'Huge',
    ];
    // Apply font size to selected regular text
    var applyFontSize = function (direction) {
        if (viewOnlyRef.current)
            return;
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
            return;
        var range = sel.getRangeAt(0);
        if (range.collapsed)
            return;
        // Helper to find font-size span that fully contains the selection
        var findContainingFontSpan = function () {
            var _a, _b;
            var startContainer = range.startContainer;
            var endContainer = range.endContainer;
            // Find font-size span from start
            var startSpan = startContainer.nodeType === Node.ELEMENT_NODE
                ? startContainer.closest('span[style*="font-size"]')
                : (_a = startContainer.parentElement) === null || _a === void 0 ? void 0 : _a.closest('span[style*="font-size"]');
            // Find font-size span from end
            var endSpan = endContainer.nodeType === Node.ELEMENT_NODE
                ? endContainer.closest('span[style*="font-size"]')
                : (_b = endContainer.parentElement) === null || _b === void 0 ? void 0 : _b.closest('span[style*="font-size"]');
            // If both are in the same span, and selection covers the entire span content, return it
            if (startSpan && endSpan && startSpan === endSpan && startSpan instanceof HTMLSpanElement) {
                // Check if selection covers the entire span content
                var spanRange = document.createRange();
                spanRange.selectNodeContents(startSpan);
                // If the selection fully covers the span, we can update it
                if (range.compareBoundaryPoints(Range.START_TO_START, spanRange) <= 0 &&
                    range.compareBoundaryPoints(Range.END_TO_END, spanRange) >= 0) {
                    return startSpan;
                }
                // Also return if the selection is entirely within this span
                return startSpan;
            }
            return null;
        };
        // Try to find existing font-size span
        var existingSpan = findContainingFontSpan();
        var currentSize = effectiveFontSize; // Default editor size
        if (existingSpan) {
            var sizeMatch = existingSpan.style.fontSize.match(/(\d+)/);
            if (sizeMatch) {
                currentSize = parseInt(sizeMatch[1], 10);
            }
        }
        // Find next size step
        var newSize;
        var currentIndex = FONT_SIZE_STEPS.findIndex(function (s) { return s >= currentSize; });
        if (direction === 'larger') {
            var nextIndex = currentIndex === -1
                ? FONT_SIZE_STEPS.length - 1
                : Math.min(currentIndex + 1, FONT_SIZE_STEPS.length - 1);
            newSize = FONT_SIZE_STEPS[nextIndex];
            // If current size is larger than all steps, increase by 10%
            if (currentSize >= FONT_SIZE_STEPS[FONT_SIZE_STEPS.length - 1]) {
                newSize = Math.round(currentSize * 1.1);
            }
        }
        else {
            var prevIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
            newSize = FONT_SIZE_STEPS[prevIndex];
            // If current size is between steps, go to lower step
            if (currentIndex === -1) {
                newSize = FONT_SIZE_STEPS[FONT_SIZE_STEPS.length - 1];
            }
            // Minimum size
            if (newSize < FONT_SIZE_STEPS[0]) {
                newSize = FONT_SIZE_STEPS[0];
            }
        }
        // If selection is entirely within an existing font-size span, just update it
        if (existingSpan) {
            existingSpan.style.fontSize = "".concat(newSize, "px");
            // Restore selection to the span
            var newRange_1 = document.createRange();
            newRange_1.selectNodeContents(existingSpan);
            sel.removeAllRanges();
            sel.addRange(newRange_1);
            return;
        }
        // Otherwise, extract and wrap selection in a new span
        var extractedContent = range.extractContents();
        // Flatten any nested font-size spans in the extracted content
        var tempDiv = document.createElement('div');
        tempDiv.appendChild(extractedContent);
        var nestedSpans = tempDiv.querySelectorAll('span[style*="font-size"]');
        nestedSpans.forEach(function (span) {
            // Unwrap the span but keep its contents
            var parent = span.parentNode;
            while (span.firstChild) {
                parent === null || parent === void 0 ? void 0 : parent.insertBefore(span.firstChild, span);
            }
            span.remove();
        });
        var sizeSpan = document.createElement('span');
        sizeSpan.style.fontSize = "".concat(newSize, "px");
        while (tempDiv.firstChild) {
            sizeSpan.appendChild(tempDiv.firstChild);
        }
        range.insertNode(sizeSpan);
        // Restore selection
        var newRange = document.createRange();
        newRange.selectNodeContents(sizeSpan);
        sel.removeAllRanges();
        sel.addRange(newRange);
    };
    // Apply font size to selected math or text
    var execFontSize = function (direction) {
        var _a;
        if (viewOnlyRef.current)
            return;
        var mf = lastFocusedMathField.current;
        if (!mf) {
            applyFontSize(direction);
            (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
            return;
        }
        // Re-focus the mathfield BEFORE reading selection.
        safeFocusMathField(mf);
        var sel = mf.selection;
        var startOffset = sel.ranges[0][0];
        // Handle math field selection
        var latex = mf.getValue(sel);
        if (!latex || latex.trim().length === 0) {
            // No selection in math field, try regular text
            applyFontSize(direction);
            return;
        }
        // Detect current size from LaTeX wrapping and strip it
        var currentSizeIndex = LATEX_SIZE_COMMANDS.indexOf('normalsize'); // default 4
        for (var i = 0; i < LATEX_SIZE_COMMANDS.length; i++) {
            var cmd = LATEX_SIZE_COMMANDS[i];
            if (latex.includes("\\".concat(cmd, "{")) ||
                latex.includes("\\".concat(cmd, " ")) ||
                latex.includes("\\".concat(cmd))) {
                currentSizeIndex = i;
                latex = unwrap(cmd, latex);
                break;
            }
        }
        // Step up or down
        var newSizeIndex;
        if (direction === 'larger') {
            newSizeIndex = Math.min(currentSizeIndex + 1, LATEX_SIZE_COMMANDS.length - 1);
        }
        else {
            newSizeIndex = Math.max(currentSizeIndex - 1, 0);
        }
        // Wrap with new size command (skip if normalsize to keep LaTeX clean)
        if (latex.includes("\\".concat(LATEX_SIZE_COMMANDS[currentSizeIndex]))) {
            // remove the previous size command
            latex = latex.replace("\\".concat(LATEX_SIZE_COMMANDS[currentSizeIndex]), '');
        }
        if (LATEX_SIZE_COMMANDS[newSizeIndex]) {
            latex = wrap(LATEX_SIZE_COMMANDS[newSizeIndex], latex);
        }
        mf.insert(latex, {
            insertionMode: 'replaceSelection',
        });
        // Re-select the inserted content so repeated clicks can step further
        // (same pattern as applyFontSize does for regular text)
        var endOffset = mf.position;
        mf.selection = { ranges: [[startOffset, endOffset]] };
    };
    var applyFormatting = function (cmd) {
        var _a, _b, _c, _d, _e;
        if (viewOnlyRef.current)
            return;
        var sel = window.getSelection();
        if (!sel)
            return;
        // Use the live selection if available, otherwise fall back to saved selection
        // (Edge may collapse the selection despite preventDefault on mousedown)
        var range = sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
        if (!range || range.collapsed) {
            if (lastSelection.current && !lastSelection.current.collapsed) {
                range = lastSelection.current.cloneRange();
                sel.removeAllRanges();
                sel.addRange(range);
            }
            else {
                return;
            }
        }
        var tagName = cmd === 'bold' ? 'strong' : cmd === 'italic' ? 'em' : 'u';
        // Check if the entire selection is within a tag of the same type
        var container = range.commonAncestorContainer;
        var existingTag = null;
        if (container.nodeType === Node.ELEMENT_NODE) {
            existingTag = container.closest(tagName);
        }
        else {
            existingTag = ((_a = container.parentElement) === null || _a === void 0 ? void 0 : _a.closest(tagName)) || null;
        }
        // Check if selection boundaries are within the same formatting tag
        var startContainer = range.startContainer;
        var endContainer = range.endContainer;
        var startParent = startContainer.nodeType === Node.ELEMENT_NODE
            ? startContainer
            : startContainer.parentElement;
        var endParent = endContainer.nodeType === Node.ELEMENT_NODE
            ? endContainer
            : endContainer.parentElement;
        var startTag = (_b = startParent === null || startParent === void 0 ? void 0 : startParent.closest(tagName)) !== null && _b !== void 0 ? _b : null;
        var endTag = (_c = endParent === null || endParent === void 0 ? void 0 : endParent.closest(tagName)) !== null && _c !== void 0 ? _c : null;
        // Edge fallback: when the range container is the editor element itself,
        // closest() won't find the tag. Check if the child node at the offset
        // is the formatting tag directly.
        if (!startTag && startContainer.nodeType === Node.ELEMENT_NODE) {
            var childAtOffset = startContainer.childNodes[range.startOffset];
            if (((_d = childAtOffset === null || childAtOffset === void 0 ? void 0 : childAtOffset.nodeName) === null || _d === void 0 ? void 0 : _d.toLowerCase()) === tagName) {
                startTag = childAtOffset;
            }
        }
        if (!endTag && endContainer.nodeType === Node.ELEMENT_NODE) {
            var childBefore = endContainer.childNodes[range.endOffset - 1];
            if (((_e = childBefore === null || childBefore === void 0 ? void 0 : childBefore.nodeName) === null || _e === void 0 ? void 0 : _e.toLowerCase()) === tagName) {
                endTag = childBefore;
            }
        }
        // Also check: if existingTag was found via commonAncestor, use it as fallback
        if (!startTag && !endTag && existingTag) {
            startTag = existingTag;
            endTag = existingTag;
        }
        if (startTag && endTag && startTag === endTag) {
            // Unwrap: selection is entirely within the tag, so remove formatting
            var parent_1 = startTag.parentNode;
            if (parent_1) {
                // Store the first and last nodes before unwrapping
                var firstNode = startTag.firstChild;
                var lastNode = startTag.lastChild;
                // Unwrap the tag
                while (startTag.firstChild) {
                    parent_1.insertBefore(startTag.firstChild, startTag);
                }
                parent_1.removeChild(startTag);
                // Keep the unwrapped content selected
                if (firstNode && lastNode) {
                    var newRange = document.createRange();
                    newRange.setStartBefore(firstNode);
                    newRange.setEndAfter(lastNode);
                    sel.removeAllRanges();
                    sel.addRange(newRange);
                    lastSelection.current = newRange.cloneRange();
                }
            }
        }
        else {
            // Wrap: apply formatting
            var selectedContent = range.extractContents();
            var formatElement = document.createElement(tagName);
            formatElement.appendChild(selectedContent);
            range.insertNode(formatElement);
            // Keep the wrapped content selected
            var newRange = document.createRange();
            newRange.selectNodeContents(formatElement);
            sel.removeAllRanges();
            sel.addRange(newRange);
            lastSelection.current = newRange.cloneRange();
        }
    };
    // Detect current formatting state from the active selection (regular text or mathfield)
    // and dispatch it so BottomPanel can highlight the active formatting buttons.
    var emitFormattingState = function (mf) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var activeMf = mf !== null && mf !== void 0 ? mf : lastFocusedMathField.current;
        var bold = false;
        var italic = false;
        var underline = false;
        if (activeMf) {
            var latex = activeMf.getValue(activeMf.selection);
            bold = latex.includes('\\mathbf{') || latex.includes('\\bm{');
            italic = latex.includes('\\mathit{');
            underline = latex.includes('\\underline{');
        }
        else if (idPreffix === 'tableText') {
            // Table cells use data-model bold/italic/underline (CSS on <td>), not DOM <strong>/<em>/<u> tags.
            bold = (_b = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.bold) !== null && _b !== void 0 ? _b : false;
            italic = (_d = (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.italic) !== null && _d !== void 0 ? _d : false;
            underline = (_f = (_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.underline) !== null && _f !== void 0 ? _f : false;
        }
        else {
            try {
                bold = document.queryCommandState('bold');
                italic = document.queryCommandState('italic');
                underline = document.queryCommandState('underline');
            }
            catch (_) { }
        }
        var alignment = (_h = (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.alignment) !== null && _h !== void 0 ? _h : 'left';
        customEventHandler.dispatchEvent('formattingStateChanged', {
            bold: bold,
            italic: italic,
            underline: underline,
            leftAlign: alignment === 'left',
            centerAlign: alignment === 'center',
            rightAlign: alignment === 'right',
        });
    };
    var exec = function (cmd) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (viewOnlyRef.current)
            return;
        var mf = lastFocusedMathField.current;
        if (!mf) {
            // Save scroll position — formatting DOM manipulation can trigger
            // browser auto-scroll due to transform: scale() offsets.
            var scrollEl_1 = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent');
            var savedScrollTop_1 = (_b = scrollEl_1 === null || scrollEl_1 === void 0 ? void 0 : scrollEl_1.scrollTop) !== null && _b !== void 0 ? _b : 0;
            var savedScrollLeft_1 = (_c = scrollEl_1 === null || scrollEl_1 === void 0 ? void 0 : scrollEl_1.scrollLeft) !== null && _c !== void 0 ? _c : 0;
            applyFormatting(cmd);
            (_d = editorRef.current) === null || _d === void 0 ? void 0 : _d.focus({ preventScroll: true });
            if (scrollEl_1) {
                requestAnimationFrame(function () {
                    scrollEl_1.scrollTop = savedScrollTop_1;
                    scrollEl_1.scrollLeft = savedScrollLeft_1;
                });
            }
            return;
        }
        // Save scroll position — MathLive insert/applyStyle can trigger
        // browser auto-scroll due to transform: scale() offsets.
        var scrollEl = (_e = editorRef.current) === null || _e === void 0 ? void 0 : _e.closest('.customScrollbarContent');
        var savedScrollTop = (_f = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _f !== void 0 ? _f : 0;
        var savedScrollLeft = (_g = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _g !== void 0 ? _g : 0;
        // Re-focus the mathfield BEFORE reading selection.
        // Clicking a toolbar button blurs the mathfield, which may clear
        // MathLive's internal selection. Re-focus restores it.
        safeFocusMathField(mf);
        if (cmd === 'bold') {
            var latex = mf.getValue(mf.selection);
            var hasBold = latex.includes('\\mathbf{') || latex.includes('\\bm{');
            var newLatex = hasBold ? unwrap('bm', unwrap('mathbf', latex)) : wrap('mathbf', latex);
            mf.insert(newLatex, {
                insertionMode: 'replaceSelection',
                selectionMode: 'item',
            });
        }
        else if (cmd === 'italic') {
            var latex = mf.getValue(mf.selection);
            var hasItalic = latex.includes('\\mathit{');
            var newLatex = hasItalic ? unwrap('mathit', latex) : wrap('mathit', latex);
            mf.insert(newLatex, {
                insertionMode: 'replaceSelection',
                selectionMode: 'item',
            });
        }
        else if (cmd === 'underline') {
            var sel = mf.selection;
            var latex = mf.getValue(sel);
            var isUnderline = latex.includes('\\underline{');
            var newLatex = isUnderline ? unwrap('underline', latex) : wrap('underline', latex);
            mf.insert(newLatex, {
                insertionMode: 'replaceSelection',
                selectionMode: 'item',
            });
        }
        // Restore scroll position after MathLive DOM changes
        if (scrollEl) {
            requestAnimationFrame(function () {
                scrollEl.scrollTop = savedScrollTop;
                scrollEl.scrollLeft = savedScrollLeft;
            });
        }
    };
    var ensureTrailingTextNode = function (editor) {
        var last = editor.lastChild;
        if (!last || last.nodeType !== Node.TEXT_NODE) {
            editor.appendChild(document.createTextNode(''));
        }
    };
    var placeCaretAfter = function (node) {
        var _a;
        // Focus editor first so the browser registers it as the active input context.
        // Without this, the first keypress after moving from a math-field is swallowed.
        (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        var range = document.createRange();
        var sel = window.getSelection();
        range.setStartAfter(node);
        range.collapse(true);
        sel === null || sel === void 0 ? void 0 : sel.removeAllRanges();
        sel === null || sel === void 0 ? void 0 : sel.addRange(range);
    };
    // Returns true when the entire math-field content is just an empty fraction
    // (both numerator and denominator contain only \placeholder{} tokens — no
    // user input). Used to delete the whole fraction on backspace.
    var isMfOnlyEmptyFrac = function (value) {
        var stripped = value.replace(/\\placeholder\{[^}]*\}/g, '').trim();
        return stripped === '\\frac{}{}';
    };
    var createMathField = function (editor, getColorEnum) {
        var _a, _b, _c;
        if (getColorEnum === void 0) { getColorEnum = false; }
        var mf = new MathfieldElement({
            contentPlaceholder: placeHolder,
        });
        // Set these as properties after creation (required for MathLive 0.108+)
        mf.smartFence = false;
        mf.smartMode = false;
        mf.setAttribute('math-virtual-keyboard-policy', 'off');
        // In manipulative label editors (fraction tiles etc.) characters should
        // render in text/upright mode so they match the surrounding plain text
        // visually. Without this, letters like 'a' are rendered in math-italic
        // (Latin Modern Math) which has a smaller x-height than the app font,
        // making mixed text+math labels look inconsistent.
        if (isManipulative) {
            mf.defaultMode = 'text';
        }
        // Completely hide the math-field from assistive technology. aria-hidden on
        // the host removes it from the AT tree entirely. The shadow-DOM container
        // span gets the same treatment (with a setTimeout retry for the span that
        // connectedCallback creates) so VoiceOver cannot reach the internal math
        // structure even when focus moves inside.
        mf.setAttribute('aria-hidden', 'true');
        var suppressMlA11y = function () {
            var sr = mf.shadowRoot;
            if (!sr)
                return;
            // Suppress aria-live/status elements via CSS
            if (!sr.querySelector('#ml-a11y-suppress')) {
                var s = document.createElement('style');
                s.id = 'ml-a11y-suppress';
                s.textContent = '[role="status"], [aria-live] { display: none !important; }';
                sr.appendChild(s);
            }
            // Hide the main shadow-DOM container from AT
            var container = sr.querySelector(':scope > span');
            if (container)
                container.setAttribute('aria-hidden', 'true');
            // Target .ML__keyboard-sink directly. MathLive's onSelectionDidChange
            // calls keyboardDelegate.setValue(latexExpanded) on every edit, writing
            // raw LaTeX like "\placeholder{}" into this contenteditable span's
            // textContent. iOS 18 VoiceOver respects aria-hidden on the element; iOS
            // 26+ VoiceOver reads the focused element's textContent directly, bypassing
            // aria-hidden on ancestors. The MutationObserver clears both aria-label
            // (which MathLive's setAriaLabel() overrides) and textContent (which VO
            // reads on iOS 26+) immediately after MathLive writes them. Clearing
            // textContent is safe because iOS input goes through the custom keypad
            // (mf.insert) — physical-keyboard events fire via keydown/beforeinput and
            // don't depend on the sink's text content.
            var sink = sr.querySelector('.ML__keyboard-sink, [part="keyboard-sink"]');
            if (sink && !sink.dataset.a11ySuppressed) {
                sink.dataset.a11ySuppressed = 'true';
                sink.setAttribute('aria-hidden', 'true');
                sink.setAttribute('tabindex', '-1');
                // Suppress the iOS native keyboard (math is entered via the custom
                // keypad). Set ONCE here — do NOT re-enforce via the MutationObserver
                // below: re-applying inputmode mid-interaction makes iOS select the
                // whole field on tap (denominator-placeholder tap selects everything).
                // Physical keyboards are unaffected (inputmode only gates the on-screen
                // keyboard). See docs/components/MathEditor.md "iPad / iOS 26".
                sink.setAttribute('inputmode', 'none');
                sink.setAttribute('aria-label', '');
                // Rewrite the sink's text content to U+200B so VoiceOver reads
                // nothing, without replacing the text node itself.
                //
                // Why in-place mutation matters: MathLive's setValue() calls
                //   sink.textContent = latex   (creates/replaces text node)
                //   selectAllChildren(sink)    (Range anchored on sink with endOffset=1)
                // Our MutationObserver fires as a microtask after both lines. If we
                // also do sink.textContent = ZWS we create a NEW text node — the old
                // Range endOffset=1 still points at 1 child so the selection is
                // technically valid. But if sink already had NO children (e.g. MathLive
                // cleared the sink in an input handler) and we add one, the previous
                // empty selection stays at offset=0 and the browser snaps the
                // document cursor back to the contenteditable. To avoid every edge
                // case, mutate .data on the existing Text node when one is present so
                // the child-count never changes; only fall back to textContent= when
                // there is genuinely no text node.
                var setSinkZWS_1 = function () {
                    var node = sink.firstChild;
                    if ((node === null || node === void 0 ? void 0 : node.nodeType) === Node.TEXT_NODE) {
                        if (node.data !== WHITESPACE_CHAR)
                            node.data = WHITESPACE_CHAR;
                    }
                    else if (sink.textContent !== WHITESPACE_CHAR) {
                        sink.textContent = WHITESPACE_CHAR;
                    }
                };
                setSinkZWS_1();
                var obs = new MutationObserver(function () {
                    if (sink.getAttribute('aria-label') !== '')
                        sink.setAttribute('aria-label', '');
                    if (sink.getAttribute('aria-hidden') !== 'true')
                        sink.setAttribute('aria-hidden', 'true');
                    setSinkZWS_1();
                });
                obs.observe(sink, {
                    attributes: true,
                    attributeFilter: ['aria-label', 'aria-hidden'],
                    childList: true,
                    characterData: true,
                    subtree: true,
                });
            }
        };
        suppressMlA11y();
        setTimeout(suppressMlA11y, 0);
        // Skip the math-field in the document Tab order. Without this, Tab from
        // the contenteditable parent lands inside the mf and the user gets
        // trapped (no keyboard-only path back out). Click / touch / programmatic
        // focus still work — tabindex="-1" only blocks the keyboard Tab walk.
        mf.setAttribute('tabindex', '-1');
        // Track this instance for cleanup later
        mathFieldInstancesRef.current.add(mf);
        var _col = '#000000';
        if (isManipulative) {
            _col = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.color) || '#000';
        }
        else {
            _col = color === 'BLACK' || !getColorEnum ? (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.color : colorProp || ColorEnum[color];
        }
        mf.applyStyle({ color: _col });
        if (!_col) {
            _col = colorProp || ColorEnum['BLACK'];
        }
        mf.setAttribute('color', _col);
        mf.style.color = _col;
        // Disable editing in viewonly mode
        if (viewOnlyRef.current) {
            mf.setAttribute('readonly', 'true');
            mf.readonly = true;
            mf.style.userSelect = 'none';
            mf.style.cursor = 'inherit';
            mf.style.pointerEvents = 'none';
            var cursorStyle = document.createElement('style');
            cursorStyle.textContent =
                ':host, * { pointer-events: none !important; } .ML__base { cursor: inherit !important; caret-color: transparent; }' +
                    PLACEHOLDER_FIX_CSS;
            (_c = mf.shadowRoot) === null || _c === void 0 ? void 0 : _c.appendChild(cursorStyle);
        }
        else {
            // On iPad/iOS, WebKit renders MathLive's internal shadow-DOM contentEditable
            // selection visibly. MathLive selects its keyboard-shim element (setting its
            // content to the current LaTeX value like "$ \placeholder{} $") so the iOS
            // virtual keyboard can replace it with new input. That selection flashes on
            // screen as highlighted text. MathLive drives its own cursor via CSS classes
            // (.ML__cursor), so suppressing the native ::selection highlight here is safe.
            var selectionStyle_1 = document.createElement('style');
            selectionStyle_1.textContent =
                '::selection { background: transparent; color: inherit; }' + PLACEHOLDER_FIX_CSS;
            // Shadow root may not be open yet at construction time — retry once connected.
            if (mf.shadowRoot) {
                mf.shadowRoot.appendChild(selectionStyle_1);
            }
            else {
                setTimeout(function () { var _a; (_a = mf.shadowRoot) === null || _a === void 0 ? void 0 : _a.appendChild(selectionStyle_1); }, 0);
            }
            // Firefox: temporarily disable pointer-events before DOM insertion.
            // connectedCallback can trigger pointerdown → onFocus → onBlur before
            // MathLive's ariaLiveText is initialized, causing a crash.
            mf.style.pointerEvents = 'none';
        }
        // Re-enable pointer-events after connectedCallback completes.
        setTimeout(function () {
            if (!viewOnlyRef.current && mf.isConnected) {
                mf.style.pointerEvents = '';
            }
        }, 0);
        // Prevent the contentEditable parent from intercepting pointer/mouse events,
        // so the mathfield can handle its own cursor positioning and text selection.
        // All three events must be stopped: pointerdown fires first but mousedown
        // still fires separately and would cause the contentEditable to reposition
        // the browser caret, overriding MathLive's internal cursor placement.
        // Firefox: ensure ariaLiveText fallback exists BEFORE MathLive's internal
        // shadow DOM handlers fire. Capture phase on the host element fires before
        // the shadow DOM's event listeners, preventing the onBlur crash.
        mf.addEventListener('pointerdown', function () { return ensureAriaLiveText(mf); }, {
            capture: true,
        });
        mf.addEventListener('focusin', function () {
            ensureAriaLiveText(mf);
            // Defer so MathLive's internal model is settled before we read position
            requestAnimationFrame(function () { return announceFracPosition(mf); });
        }, {
            capture: true,
        });
        mf.addEventListener('pointerdown', function (e) {
            e.stopPropagation();
            // (window as any).__jumpSnap?.('mf-tap', mf, `${idPreffix}[${index}]`);
        });
        mf.addEventListener('mousedown', function (e) {
            e.stopPropagation();
            // Only preventDefault on the first mousedown (when not yet focused) so
            // we can manually focus with preventScroll. When the math-field is
            // already focused (e.g. tapping the denominator after typing the
            // numerator on iPad), preventDefault here blocks the browser's native
            // tap-to-position so MathLive can't resolve the hit-test and falls
            // back to select-all of the parent expression.
            if (!mf.matches(':focus')) {
                e.preventDefault();
                mf.focus({ preventScroll: true });
            }
        });
        mf.addEventListener('click', function (e) {
            e.stopPropagation();
        });
        // MathLive's event is `selection-change` (with hyphen) — the
        // standard DOM `selectionchange` event is fired on `document`, not
        // on math-field, so listening for it here would never fire. The
        // formatting-state sync below + the fraction-position announcement
        // both depend on this.
        mf.addEventListener('selection-change', function () {
            var _a;
            emitFormattingState(mf);
            // Announce caret position when entering / leaving a \frac argument
            // so screen readers say "Numerator" / "Denominator" the moment the
            // user arrives in the input box (after pressing the / fraction key,
            // arrowing across, or tabbing through placeholders). Cached against
            // the mf so we only announce on transitions, not on every keystroke.
            //
            // MathLive's atom model exposes `parentBranch` on each atom: inside
            // a \frac, 'above' = numerator and 'below' = denominator. We walk
            // up from the cursor's current atom until we find a genfrac
            // ancestor and read the branch the cursor sits in.
            try {
                var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
                if (!model)
                    return;
                var atom = model.at(model.position);
                var branchInFrac = 'outside';
                // Track the branch as we walk up — the LAST non-frac ancestor we
                // pass through before hitting a genfrac is the branch the cursor
                // is in. Stop at the first genfrac ancestor.
                var currentBranch = atom === null || atom === void 0 ? void 0 : atom.parentBranch;
                while (atom && atom.type !== 'root') {
                    if (atom.type === 'genfrac') {
                        if (currentBranch === 'above' || currentBranch === 'below') {
                            branchInFrac = currentBranch;
                        }
                        break;
                    }
                    currentBranch = atom.parentBranch;
                    atom = atom.parent;
                }
                var prev = lastFracBranchRef.current.get(mf);
                if (prev !== branchInFrac) {
                    lastFracBranchRef.current.set(mf, branchInFrac);
                    if (branchInFrac === 'above') {
                        announce(t('text.fractionNumerator'));
                    }
                    else if (branchInFrac === 'below') {
                        announce(t('text.fractionDenominator'));
                    }
                    // No announcement when transitioning OUT of a fraction — the
                    // surrounding context (next atom typed, or MathLive's own
                    // spoken-text on blur) gives the user enough orientation.
                }
            }
            catch (_b) {
                // MathLive private API may shift across versions — silently bail.
            }
        });
        // iPad fall-back for taps that land MathLive on a select-all because the
        // hit-test couldn't resolve to a precise atom (typically tapping an empty
        // \placeholder{} like the denominator while the mf is already focused
        // from typing the numerator). The focus listener has the same logic, but
        // it only fires when focus enters the mf — not when the user re-taps
        // inside an already-focused mf. Only collapses when at least one
        // \placeholder{} is present so deliberate select-all on filled content
        // (Cmd/Ctrl+A, drag-select) is preserved.
        mf.addEventListener('pointerup', function () {
            requestAnimationFrame(function () {
                var _a;
                if (!mf.isConnected)
                    return;
                try {
                    var sel = mf.selection;
                    var ranges = (sel === null || sel === void 0 ? void 0 : sel.ranges) || [];
                    var lastOffset = (_a = mf.lastOffset) !== null && _a !== void 0 ? _a : 0;
                    var isSelectAll = ranges.length === 1 &&
                        ranges[0][0] === 0 &&
                        ranges[0][1] === lastOffset &&
                        lastOffset > 0;
                    if (!isSelectAll)
                        return;
                    if (!(mf.value || '').includes('\\placeholder{}'))
                        return;
                    mf.executeCommand('moveToNextPlaceholder');
                }
                catch (_b) {
                    /* ignore — MathLive private API may shift across versions */
                }
            });
        });
        // Intercept Tab before MathLive's shadow-DOM keyboardSink (capture phase).
        //   • Forward Tab + keypad visible → first keypad button ('1') so the
        //     user can drive math input from the keyboard.
        //   • Otherwise (Shift+Tab, or no keypad mounted) → exit the math editor
        //     to the nearest tabbable element outside `.mathEditorParent`.
        // Arrow keys handle in-math navigation between groups. The mf itself
        // is tabindex="-1" so the document Tab walk never lands here from
        // outside the mf — the user only reaches this code path after a click
        // or programmatic focus puts the caret inside an mf.
        // Also blocks MathLive's moveToNextGroup command, which crashes when
        // the internal model state is undefined.
        mf.addEventListener('keydown', function (e) {
            var _a, _b, _c, _d;
            if (e.key !== 'Tab')
                return;
            e.stopImmediatePropagation();
            e.preventDefault();
            if (!e.shiftKey && keypadWrapperRef.current) {
                var firstBtn = keypadWrapperRef.current.querySelector('button:not([disabled]):not([tabindex="-1"])');
                if (firstBtn) {
                    firstBtn.focus();
                    return;
                }
            }
            // Delegate to the parent's Tab handler when one is provided (e.g. table
            // cells), so cell navigation matches regular-text Tab behaviour exactly.
            if (onTabRef.current) {
                onTabRef.current(e.shiftKey);
                return;
            }
            // No keypad to land in (Shift+Tab, or keypad isn't mounted yet) —
            // exit the math editor entirely.
            var root = (_a = mf.closest('.mathEditorParent')) !== null && _a !== void 0 ? _a : editorRef.current;
            if (!root)
                return;
            var tabbableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
            var all = Array.from(document.querySelectorAll(tabbableSelector)).filter(function (el) {
                if (root.contains(el))
                    return false;
                if (el.offsetParent === null && el !== document.body)
                    return false;
                return true;
            });
            var target = null;
            if (e.shiftKey) {
                for (var _i = 0, all_1 = all; _i < all_1.length; _i++) {
                    var el = all_1[_i];
                    if (root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) {
                        target = el;
                    }
                }
            }
            else {
                target =
                    (_b = all.find(function (el) { return root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING; })) !== null && _b !== void 0 ? _b : null;
            }
            if (target) {
                target.focus();
            }
            else {
                (_d = (_c = document.activeElement) === null || _c === void 0 ? void 0 : _c.blur) === null || _d === void 0 ? void 0 : _d.call(_c);
            }
        }, { capture: true });
        // Escape inside the math-field: exit cleanly. Without this, MathLive's
        // own Esc handling can leave focus stuck on the shadow-DOM keyboard sink
        // — the user can't Tab forward/backward and arrow keys also stop
        // working from the user's perspective. We close the keypad, blur the mf,
        // and place the caret in the contenteditable editor just after the mf
        // so the user lands in a known-good text-editing state.
        mf.addEventListener('keydown', function (e) {
            var _a, _b;
            if (e.key !== 'Escape')
                return;
            e.stopImmediatePropagation();
            e.preventDefault();
            setmathKeyboardVisible(false);
            setActiveMathText && setActiveMathText(-1);
            var editorEl = mf.closest('[contenteditable]');
            if (!editorEl) {
                (_b = (_a = mf).blur) === null || _b === void 0 ? void 0 : _b.call(_a);
                return;
            }
            editorEl.focus({ preventScroll: true });
            var sel = window.getSelection();
            var parent = mf.parentNode;
            if (sel && parent) {
                var range = document.createRange();
                var mfIdx = Array.from(parent.childNodes).indexOf(mf);
                // Caret immediately after the mf — past any trailing spacer the
                // browser collapses through.
                range.setStart(parent, mfIdx + 1);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
            }
            if (lastFocusedMathField.current === mf) {
                lastFocusedMathField.current = null;
            }
        }, { capture: true });
        // Arrow exits at the boundaries of the math-field, in step with the
        // editor's mf-adjacent enter handlers:
        //   • ArrowLeft at offset 0 (start)        → exit to the editor caret
        //                                            position immediately before
        //                                            the mf
        //   • ArrowRight at lastOffset (end)        → exit to the editor caret
        //                                            position immediately after
        //                                            the mf
        // Any other position inside the mf falls through to MathLive's native
        // arrow stepping so the user can surf through math content normally.
        mf.addEventListener('keydown', function (e) {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')
                return;
            if (e.shiftKey || e.altKey || e.metaKey || e.ctrlKey)
                return;
            // Use the public `position` getter — simpler than digging into
            // `selection.ranges` and stable across MathLive minor versions.
            var pos = mf.position;
            var lastOffset = mf.lastOffset;
            if (typeof pos !== 'number' || typeof lastOffset !== 'number')
                return;
            // Range-extending (rare without shift but possible) stays with
            // MathLive — only collapsed-cursor boundary exits route here.
            var sel = mf.selection;
            var ranges = sel === null || sel === void 0 ? void 0 : sel.ranges;
            if (Array.isArray(ranges) && ranges.length === 1) {
                var r = ranges[0];
                var start = Array.isArray(r) ? r[0] : r === null || r === void 0 ? void 0 : r.start;
                var end = Array.isArray(r) ? r[1] : r === null || r === void 0 ? void 0 : r.end;
                if (typeof start === 'number' && typeof end === 'number' && start !== end) {
                    return;
                }
            }
            // Left exits ONLY at offset 0 (start). Right exits ONLY at lastOffset
            // (end). Anywhere else falls through so MathLive moves the cursor
            // through math content normally.
            var exitLeft = e.key === 'ArrowLeft' && pos === 0;
            var exitRight = e.key === 'ArrowRight' && pos === lastOffset;
            console.log('[mf-arrow-debug]', {
                key: e.key,
                pos: pos,
                lastOffset: lastOffset,
                exitLeft: exitLeft,
                exitRight: exitRight,
                willExit: exitLeft || exitRight,
            });
            if (!exitLeft && !exitRight)
                return;
            var editorEl = mf.closest('[contenteditable]');
            if (!editorEl)
                return;
            e.stopImmediatePropagation();
            e.preventDefault();
            editorEl.focus({ preventScroll: true });
            var winSel = window.getSelection();
            var parent = mf.parentNode;
            if (!winSel || !parent)
                return;
            var range = document.createRange();
            var mfIdx = Array.from(parent.childNodes).indexOf(mf);
            range.setStart(parent, exitLeft ? mfIdx : mfIdx + 1);
            range.collapse(true);
            winSel.removeAllRanges();
            winSel.addRange(range);
            announce('Exited math expression');
        }, { capture: true });
        // Prevent MathLive's internal scrollIntoView from scrolling the outer container.
        // The parent .mainHolder uses transform: scale(zoomLevel), causing the browser to
        // miscalculate scroll offsets and push the entire view out of the visible area.
        mf.scrollIntoView = function () { };
        // Prevent the browser's native "keep focused element in view" auto-scroll on
        // physical keyboard input. The whiteboard scroll container wraps a transform:
        // scale() element, so the browser miscalculates the required scroll offset and
        // jumps the view. The in-app keypad uses mf.insert() which only triggers
        // MathLive's scrollIntoView (already no-op'd above) and doesn't hit this path.
        mf.addEventListener('keydown', function (ev) {
            var _a, _b, _c, _d;
            // Preserve scroll on the whiteboard container (transform: scale() miscalc)
            var scrollEl = editor.closest('.customScrollbarContent');
            var savedLeft = (_a = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _a !== void 0 ? _a : 0;
            var savedTop = (_b = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _b !== void 0 ? _b : 0;
            // Preserve window + document scroll too — structural inserts like
            // `/` → \frac cause MathLive to shift focus inside the shadow DOM
            // which VoiceOver + the browser respond to by scrolling the page.
            // Covers Safari/iPad VO jumpiness on fraction / sqrt / exponent inputs.
            var structuralKey = ev.key === '/' ||
                ev.key === '^' ||
                ev.key === '_' ||
                ev.key === '(' ||
                ev.key === '{' ||
                ev.key === '[' ||
                (ev.altKey && ['f', 'F', 'e', 'E', 'r', 'R'].includes(ev.key));
            var winX = window.scrollX;
            var winY = window.scrollY;
            var docEl = document.scrollingElement;
            var docLeft = (_c = docEl === null || docEl === void 0 ? void 0 : docEl.scrollLeft) !== null && _c !== void 0 ? _c : winX;
            var docTop = (_d = docEl === null || docEl === void 0 ? void 0 : docEl.scrollTop) !== null && _d !== void 0 ? _d : winY;
            var restore = function () {
                if (scrollEl) {
                    scrollEl.scrollLeft = savedLeft;
                    scrollEl.scrollTop = savedTop;
                }
                if (structuralKey) {
                    window.scrollTo(winX, winY);
                    if (docEl) {
                        docEl.scrollLeft = docLeft;
                        docEl.scrollTop = docTop;
                    }
                }
            };
            requestAnimationFrame(restore);
            // Second rAF catches late scrolls from MathLive's async structural
            // changes (seen on fraction insert with VoiceOver active).
            if (structuralKey) {
                requestAnimationFrame(function () { return requestAnimationFrame(restore); });
            }
        }, { capture: true });
        mf.addEventListener('copy', function (e) { return e.preventDefault(); });
        mf.addEventListener('paste', function (e) { return e.preventDefault(); });
        mf.addEventListener('cut', function (e) { return e.preventDefault(); });
        mf.addEventListener('keydown', function (ev) {
            if (viewOnlyRef.current) {
                ev.preventDefault();
                return;
            }
            // Prevent copy/paste/cut shortcuts
            if ((ev.ctrlKey || ev.metaKey) && ['c', 'v', 'x'].includes(ev.key.toLowerCase())) {
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
            // Prevent alphabet typing inside math-field — only math input allowed
            if (ev.key.length === 1 &&
                /[a-zA-Z]/.test(ev.key) &&
                !ev.ctrlKey &&
                !ev.metaKey &&
                !ev.altKey) {
                ev.preventDefault();
                return;
            }
            // Prevent Enter key inside math-field — line breaks make no sense
            // in a single-line math expression. Text editor handles its own
            // Enter key separately (inserts <br>).
            if (ev.key === 'Enter' || ev.keyCode === 13) {
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
            if ((ev.key === 'Backspace' || ev.key === 'Delete') && (mf.value === '' || isMfOnlyEmptyFrac(mf.value))) {
                ev.preventDefault();
                var next = mf.nextSibling;
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
                mf.remove();
                announceAssertive(t('text.mathEditor.mathFieldDeleted'));
                batchDisposeMathfields([mf]);
                // Always hide keyboard — cursor moves to contentEditable, not another mathfield.
                // If the user clicks another mathfield, its focus handler will re-open it.
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                if (next)
                    placeCaretAfter(next);
                else
                    editor.focus({ preventScroll: true });
                // Sync data after deletion
                getMathData();
                return;
            }
            // Prevent input if maxCharacters limit is reached
            if (maxCharacters) {
                var currentCount = getTotalCharacterCount();
                var currentMfValue = mf.value || '';
                // For non-deletion keys, check if we're at limit.
                // Exception: when cursor is inside a \frac argument, Math.max() makes
                // the total look full even if one side is empty — skip the block here
                // and let the input-event revert handler enforce the actual per-side limit.
                if (ev.key !== 'Backspace' &&
                    ev.key !== 'Delete' &&
                    currentCount >= maxCharacters &&
                    !isInFrac(mf)) {
                    // Allow navigation keys
                    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(ev.key)) {
                        ev.preventDefault();
                        announce(t('text.mathEditor.charLimitReached'));
                        return;
                    }
                }
            }
        });
        // iPad: virtual keyboard backspace fires beforeinput on the math-field
        // instead of keydown. When the math-field is empty, remove it and move
        // focus back to the contentEditable editor.
        mf.addEventListener('beforeinput', function (ev) {
            var inputEv = ev;
            if (inputEv.inputType === 'deleteContentBackward' && (mf.value === '' || isMfOnlyEmptyFrac(mf.value))) {
                ev.preventDefault();
                var next = mf.nextSibling;
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
                mf.remove();
                batchDisposeMathfields([mf]);
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                if (next)
                    placeCaretAfter(next);
                else
                    editor.focus({ preventScroll: true });
                getMathData();
            }
        });
        // When the user taps a placeholder inside the math-field, iOS scrolls the
        // entire window to keep the focused input visible when the keyboard opens.
        // The lock must stay active for the entire focus session (not just 500ms),
        // because iOS may issue multiple scroll adjustments as the keyboard settles.
        // Strategy:
        //   pointerdown/touchstart → snapshot scroll position, install window lock
        //   mf focus             → confirm lock is active (belt-and-suspenders)
        //   mf blur              → remove window lock
        // Install on first touch (before focus fires) to catch the earliest scroll.
        // Uses the module-level singleton lock (see installWinLock) so listeners
        // can't leak/fight across math-field recreation.
        // The scroll container to pin (whiteboard canvas, JumpBar, or Table cell).
        var _scrollContainer = function () {
            var _a;
            return mf.closest('.customScrollbarContent') ||
                ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent'));
        };
        var _touchLockFired = false;
        var _onTouch = function () {
            if (_touchLockFired)
                return;
            _touchLockFired = true;
            installWinLock(window.scrollX, window.scrollY);
            installElLock(_scrollContainer());
            setTimeout(function () { _touchLockFired = false; }, 600);
        };
        mf.addEventListener('pointerdown', _onTouch);
        mf.addEventListener('touchstart', _onTouch, { passive: true });
        // Re-confirm lock on actual focus (covers keyboard navigation). No-op if
        // the singleton is already installed (keeps the original saved position).
        mf.addEventListener('focus', function () {
            installWinLock(window.scrollX, window.scrollY);
            installElLock(_scrollContainer());
        });
        // Release on blur — debounced inside removeWinLock/removeElLock so it
        // survives the keypad blur→refocus churn and only tears down when the user
        // genuinely leaves the math-field.
        mf.addEventListener('blur', function () {
            removeWinLock();
            removeElLock();
        });
        // Add input listener to prevent input when limit is reached
        if (maxCharacters) {
            // Store previous value for potential revert
            mf.setAttribute('data-prev-value', '');
            mf.addEventListener('input', function () {
                var currentCount = getTotalCharacterCount();
                var prevValue = mf.getAttribute('data-prev-value') || '';
                if (currentCount > maxCharacters) {
                    // Revert to previous value if exceeded
                    mf.value = prevValue;
                    // Restore cursor position might be needed, but MathLive handles it
                }
                else {
                    // Update previous value
                    mf.setAttribute('data-prev-value', mf.value || '');
                }
            });
        }
        // Live-sync edits inside the math-field to the parent via cb. Without
        // this, the keydown listener only calls getMathData on Backspace/Delete
        // when the field is empty, so typing inside `\frac{...}{...}` (e.g.
        // bumping 13 → 14) never propagates. Consumers like the JumpBar need
        // every keystroke to recompute the bar length from the new fraction.
        // force=true bypasses the mathKeyboardVisible bail since the keyboard
        // is open while the user is editing. Defer one microtask so MathLive
        // has finished updating mf.value before calcMathData walks the DOM.
        var liveSync = function () {
            if (viewOnlyRef.current)
                return;
            Promise.resolve().then(function () {
                var _a;
                (_a = getMathDataRef.current) === null || _a === void 0 ? void 0 : _a.call(getMathDataRef, false, true);
            });
        };
        mf.addEventListener('input', function () {
            // iOS 26: revert iOS's per-input document + container scroll before paint.
            holdWindowScrollBriefly();
            holdElScrollBriefly(_scrollContainer());
            // (window as any).__jumpSnap?.('mf-input', mf, `${idPreffix}[${index}]`);
        });
        mf.addEventListener('input', liveSync);
        mf.addEventListener('change', liveSync);
        mf.addEventListener('focus', function (e) {
            if (viewOnlyRef.current)
                return;
            // Ignore a stray focus landing here immediately after a DOM rebuild
            // (initial mount, duplication, or content reset) — a duplicated text
            // item is auto-selected on mount, so without this guard that stray
            // focus would open the keypad before the user has clicked anything.
            if (Date.now() - justHydratedRef.current < 300)
                return;
            // Track the element that triggered keypad open (e.g., math keypad button)
            var related = e.relatedTarget;
            var openKeyboard = function () {
                if (related)
                    keypadTriggerRef.current = related;
                lastFocusedMathField.current = mf;
                setmathKeyboardVisible(true);
                setActiveMathText && typeof index == 'number' && setActiveMathText(index);
                if (maxCharacters) {
                    mf.setAttribute('data-prev-value', mf.value || '');
                }
                // Announce math-field focus for SR users
                var editor = editorRef.current;
                var mfIndex = editor
                    ? Array.from(editor.querySelectorAll('math-field')).indexOf(mf) + 1
                    : 1;
                announce("Math expression ".concat(mfIndex, ". Use the keypad or keyboard to edit. Alt+K opens keypad."));
                // iPad fallback: if MathLive lands on a select-all because the tap
                // couldn't be resolved to a precise position (typical when tapping
                // an empty placeholder like a fraction denominator), collapse the
                // selection — preferring the next empty placeholder, otherwise the
                // end of the expression.
                requestAnimationFrame(function () {
                    var _a;
                    if (!mf.isConnected)
                        return;
                    try {
                        var sel = mf.selection;
                        var ranges = (sel === null || sel === void 0 ? void 0 : sel.ranges) || [];
                        var lastOffset = (_a = mf.lastOffset) !== null && _a !== void 0 ? _a : 0;
                        var isSelectAll = ranges.length === 1 &&
                            ranges[0][0] === 0 &&
                            ranges[0][1] === lastOffset &&
                            lastOffset > 0;
                        if (!isSelectAll)
                            return;
                        if ((mf.value || '').includes('\\placeholder{}')) {
                            mf.executeCommand('moveToNextPlaceholder');
                        }
                        else {
                            mf.executeCommand('moveToMathfieldEnd');
                        }
                    }
                    catch (_b) {
                        /* ignore — MathLive private API may shift across versions */
                    }
                });
            };
            // A leftover native DOM focus can land on this math-field (e.g. the
            // browser re-firing focus during a shift-click) even when our own
            // selection logic has NOT made this editor the active one — e.g. it's
            // being added to a multi-selection rather than edited alone. Only open
            // the keyboard when this editor is actually the sole focused/selected
            // item; otherwise the keyboard could open and never get a matching
            // isFocused→false transition to close it again.
            if (isFocusedRef.current) {
                e.preventDefault();
                openKeyboard();
                return;
            }
            // First click on a math-field in a not-yet-selected box: MathLive calls
            // mf.focus() from its own pointerdown handler, which fires (and this
            // 'focus' listener with it) BEFORE React commits the click's
            // setSelectedTool update and re-syncs isFocusedRef — so isFocusedRef
            // is still stale/false here even though this box is about to become
            // the sole selection. Wait two frames for that commit to land, then
            // retry once; bail if focus moved elsewhere in the meantime (e.g. a
            // genuine multi-select, where isFocusedRef correctly stays false).
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    if (document.activeElement !== mf)
                        return;
                    if (!isFocusedRef.current)
                        return;
                    openKeyboard();
                });
            });
        });
        mf.addEventListener('blur', function (e) {
            var _a, _b, _c;
            var relatedTarget = e.relatedTarget;
            if (viewOnlyRef.current) {
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                return;
            }
            var editor = editorRef.current;
            // Announce spoken-math value via MathLive's getValue('spoken-text') API
            try {
                var spoken = (_b = (_a = mf).getValue) === null || _b === void 0 ? void 0 : _b.call(_a, 'spoken-text');
                if (spoken && typeof spoken === 'string' && spoken.trim()) {
                    announce("Math expression updated: ".concat(spoken));
                }
            }
            catch (_d) {
                /* ignore if API unavailable */
            }
            // If focus moved within the same editor, hide the keypad only.
            if (editor && relatedTarget && editor.contains(relatedTarget)) {
                // Opt-in via stickyKeypadAfterInsertOnTouch: keep the math context
                // alive instead of closing the keypad on every internal blur.
                // Required when the math-trigger button is a sibling of the
                // editorDiv inside the same focused subtree (JumpBar / CustomTick
                // / fraction-tile labels). Two distinct triggers cause this branch
                // to fire and both should be sticky:
                //   1. iOS Safari oscillates focus between a freshly-inserted
                //      math-field and its parent editorDiv (iPad-specific).
                //   2. MathLive's internal cursor moves during mf.insert(...)
                //      for fraction / symbols can blur the host mf with
                //      relatedTarget = the editorDiv (desktop + iPad).
                // The old 500 ms post-insertMath gate covered (1) but lapsed
                // between keypad button clicks (every "+" or "/" press) so the
                // keypad closed mid-edit. Holding the math-field focus
                // unconditionally for stickyKeypadAfterInsertOnTouch consumers
                // matches their intent ("user is editing a single math expression,
                // never the surrounding text") and lets users keep typing on the
                // keypad continuously. The keypad still closes when the editor
                // genuinely loses selection — that runs through the !isFocused
                // useEffect at line 391 below, which is unaffected.
                if (stickyKeypadAfterInsertOnTouch) {
                    requestAnimationFrame(function () {
                        if (mf.isConnected)
                            safeFocusMathField(mf);
                    });
                    return;
                }
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                return;
            }
            // If focus moved to the keypad, do nothing — the keypad handler
            // will re-focus the mathfield and insert a value.
            if (keypadWrapperRef.current &&
                relatedTarget &&
                keypadWrapperRef.current.contains(relatedTarget)) {
                return;
            }
            // If focus moved to the tool bottom panel, right side panel, sidebar,
            // or footer, keep selection so buttons can operate on the focused math content.
            if (relatedTarget instanceof HTMLElement) {
                if (relatedTarget.closest('.toolBottomPanelContainer') ||
                    relatedTarget.closest('#rightSideDrawerContainer') ||
                    relatedTarget.closest('.sidebar') ||
                    relatedTarget.closest('.footer') ||
                    relatedTarget.closest('.collapsibleRight') ||
                    relatedTarget.closest('.collapsibleOptionHolder')) {
                    return;
                }
            }
            // If switching focus to another mathfield, keep this one and don't remove it
            if ((relatedTarget === null || relatedTarget === void 0 ? void 0 : relatedTarget.nodeName) === 'MATH-FIELD' ||
                (!relatedTarget && ((_c = e.target) === null || _c === void 0 ? void 0 : _c.nodeName) === 'MATH-FIELD')) {
                return;
            }
            // Auto-remove mathfield only when it is truly empty (no value at all).
            // \placeholder{} counts as real content (a visible box) so we keep it.
            // Sticky consumers (JumpBar / CustomTick / fraction-tile labels) skip
            // this in-blur removal: a freshly-inserted empty mf would otherwise
            // be deleted between keypad taps (e.g. when relatedTarget is a
            // sibling that misses every early-return above), dropping
            // lastFocusedMathField and routing subsequent keypad inserts to the
            // editorDiv. The empty-mf cleanup for sticky runs at session end via
            // the !isFocused effect below.
            var raw = (mf.value || '').trim();
            if (raw === '' && mf.isConnected && !stickyKeypadAfterInsertOnTouch) {
                var next = mf.nextSibling;
                if (next &&
                    next.nodeType === Node.TEXT_NODE &&
                    (next.textContent === WHITESPACE_CHAR || next.textContent === '')) {
                    next.remove();
                }
                mf.remove();
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
                batchDisposeMathfields([mf]);
                // Hide keyboard if no math fields remain
                var remainingMf = editor === null || editor === void 0 ? void 0 : editor.querySelector('math-field');
                if (!remainingMf) {
                    setmathKeyboardVisible(false);
                    setActiveMathText && setActiveMathText(-1);
                }
            }
            // Sticky-keypad consumers own the close lifecycle externally; never
            // auto-deselect from inside the math-field blur. Without this, a blur
            // whose relatedTarget happens to be a wrapper carrying data-mfdeselect
            // (e.g. the JumpBar's outer div) tears down selection + keypad
            // mid-edit on iPad even though the user is still tapping the keypad.
            var deselect = !stickyKeypadAfterInsertOnTouch &&
                (relatedTarget.getAttribute('data-mfdeselect') === 'true' ||
                    relatedTarget.className.includes('customScrollbar'));
            if (deselect) {
                setSelectedTool([]);
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(function () {
                    timeoutRef.current = null;
                    getMathData();
                }, 100);
            }
        });
        return mf;
    };
    // Cleanup function to properly dispose of MathfieldElement instances
    // Now using optimized batch disposal from mathliveOptimizations
    var cleanupMathFields = function () {
        if (mathFieldInstancesRef.current.size === 0)
            return;
        // Use batch disposal for efficient cleanup
        batchDisposeMathfields(mathFieldInstancesRef.current);
        mathFieldInstancesRef.current.clear();
        // Schedule garbage collection hint after cleanup
        scheduleSuggestGarbageCollection(500);
    };
    // Cleanup on component unmount
    useEffect(function () {
        return function () {
            var _a;
            // Flush any pending data save before disposing math fields
            // (blur sets a 100ms timeout for getMathData; if unmount happens
            // before it fires, the entered text would be lost)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
                (_a = getMathDataRef.current) === null || _a === void 0 ? void 0 : _a.call(getMathDataRef);
            }
            if (onChangeSaveTimerRef.current) {
                clearTimeout(onChangeSaveTimerRef.current);
                onChangeSaveTimerRef.current = null;
            }
            cleanupMathFields();
        };
    }, []);
    var insertMath = function () {
        var _a, _b;
        if (viewOnlyRef.current)
            return;
        if (!mlReady)
            return alert('MathLive is still loading…');
        // Guard: prevent rapid double insertion (e.g., re-render triggers setMathData
        // which rebuilds the editor, then this runs and adds another math-field)
        var now = Date.now();
        if (now - insertMathGuardRef.current < 300)
            return;
        insertMathGuardRef.current = now;
        var editor = editorRef.current;
        var sel = window.getSelection();
        var mf = createMathField(editor, true);
        ensureTrailingTextNode(editor);
        var spacerAfter = document.createTextNode(WHITESPACE_CHAR);
        var spacerBefore = document.createTextNode(WHITESPACE_CHAR);
        if (viewOnlyRef.current) {
            mf.readonly = true;
        }
        var focusMf = function () {
            safeFocusMathField(mf);
            // Retry if focus didn't land inside the mf
            requestAnimationFrame(function () {
                if (document.activeElement !== mf && mf.isConnected) {
                    safeFocusMathField(mf);
                }
            });
        };
        if (!(sel === null || sel === void 0 ? void 0 : sel.rangeCount) || !editor.contains(sel.anchorNode)) {
            // If the editor's last text content ends with 'e' or 'a', insert a space
            // to prevent font-shaping distortion on those glyphs when adjacent to ZWS.
            var lastTextNode = (function () {
                var node = editor.lastChild;
                while (node && node.nodeType !== Node.TEXT_NODE)
                    node = node.previousSibling;
                return node;
            })();
            if (lastTextNode) {
                var t_1 = (_a = lastTextNode.textContent) !== null && _a !== void 0 ? _a : '';
                if (t_1.length > 0 && /[eaoEAO]/.test(t_1[t_1.length - 1])) {
                    editor.appendChild(document.createTextNode(' '));
                }
            }
            editor.appendChild(spacerBefore);
            editor.appendChild(mf);
            ensureAriaLiveText(mf);
            editor.appendChild(spacerAfter);
            setTimeout(focusMf, 50);
            return;
        }
        var range = sel.getRangeAt(0);
        range.collapse(true);
        // If the character immediately before the cursor is 'e' or 'a', insert a
        // regular space to break the font-shaping context before the ZWS spacer.
        // Without it, the contextual glyph for those letters changes when the
        // next "character" is U+200B, making them appear in the wrong font style.
        if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset > 0) {
            var textBefore = (_b = range.startContainer.textContent) !== null && _b !== void 0 ? _b : '';
            var ch = textBefore[range.startOffset - 1];
            if (/[eaoEAO]/.test(ch)) {
                var spaceNode = document.createTextNode(' ');
                range.insertNode(spaceNode);
                range.setStartAfter(spaceNode);
                range.collapse(true);
            }
        }
        // Insert in reverse DOM order: spacerAfter first, then mf before it, then spacerBefore before mf
        range.insertNode(spacerAfter);
        range.insertNode(mf);
        range.insertNode(spacerBefore);
        ensureAriaLiveText(mf);
        setTimeout(focusMf, 20);
    };
    var calcMathData = function () {
        var _a;
        var editor = editorRef.current;
        var output = [];
        // Don't save WHITESPACE_CHAR in data - it should only exist in DOM as spacers
        (_a = editor === null || editor === void 0 ? void 0 : editor.childNodes) === null || _a === void 0 ? void 0 : _a.forEach(function (node) {
            var _a, _b;
            // Skip transient spans whose only content is U+200B / whitespace —
            // mf spacer wrappers (data-mf-spacer) and any legacy color-anchor
            // spans from older builds. Persisting them re-introduces a ZWS
            // sibling next to typed text which causes font-fallback distortion
            // on adjacent letters (most visibly "a" / "e").
            if (node.nodeType === Node.ELEMENT_NODE &&
                node.tagName === 'SPAN' &&
                !node.querySelector('math-field') &&
                ((_a = node.textContent) !== null && _a !== void 0 ? _a : '').replace(/[​\s]/g, '').length === 0) {
                return;
            }
            if (node.nodeType === Node.TEXT_NODE) {
                var rawText = (_b = node.textContent) !== null && _b !== void 0 ? _b : '';
                var cleanedText = rawText.replace(new RegExp("".concat(WHITESPACE_CHAR, "+"), 'g'), '').trim();
                if (cleanedText.length > 0) {
                    output.push({ type: 'text', value: cleanedText });
                }
            }
            else if (node.nodeName === 'MATH-FIELD') {
                var mf = node;
                if (mf.value !== '') {
                    var latex = mf.value;
                    // Normalise single-letter/digit math to plain text in manipulative
                    // label editors so the saved data never re-introduces math-italic
                    // rendering for characters that should match surrounding text.
                    if (isManipulative) {
                        var simpleLetter = /^[a-zA-Z0-9]$/.test(latex);
                        var textModeMatch = latex.match(/^\\text\{([a-zA-Z0-9])\}$/);
                        if (simpleLetter) {
                            output.push({ type: 'text', value: latex });
                        }
                        else if (textModeMatch) {
                            output.push({ type: 'text', value: textModeMatch[1] });
                        }
                        else {
                            output.push({ type: 'math', latex: latex, col: mf.style.color });
                        }
                    }
                    else {
                        output.push({ type: 'math', latex: latex, col: mf.style.color });
                    }
                }
            }
            else {
                var el = node;
                // Color spans that contain math-fields can't be saved as outerHTML:
                // MathLive never serializes mf.value into the DOM, so the latex would
                // be silently lost. Decompose the span into individual text/math items
                // and carry the span's color on each piece.
                if (el.tagName === 'SPAN' && el.querySelector('math-field')) {
                    var spanColor_1 = el.style.color || '';
                    el.childNodes.forEach(function (child) {
                        var _a;
                        if (child.nodeType === Node.TEXT_NODE) {
                            var raw = (_a = child.textContent) !== null && _a !== void 0 ? _a : '';
                            var cleaned = raw.replace(new RegExp("".concat(WHITESPACE_CHAR, "+"), 'g'), '').trim();
                            if (cleaned.length > 0) {
                                if (spanColor_1) {
                                    output.push({
                                        type: 'html',
                                        value: "<span style=\"color: ".concat(spanColor_1, ";\">").concat(cleaned, "</span>"),
                                    });
                                }
                                else {
                                    output.push({ type: 'text', value: cleaned });
                                }
                            }
                        }
                        else if (child.nodeName === 'MATH-FIELD') {
                            var mf = child;
                            if (mf.value !== '') {
                                var latex = mf.value;
                                if (isManipulative) {
                                    var simpleLetter = /^[a-zA-Z0-9]$/.test(latex);
                                    var textModeMatch = latex.match(/^\\text\{([a-zA-Z0-9])\}$/);
                                    if (simpleLetter) {
                                        output.push({ type: 'text', value: latex });
                                    }
                                    else if (textModeMatch) {
                                        output.push({ type: 'text', value: textModeMatch[1] });
                                    }
                                    else {
                                        output.push({ type: 'math', latex: latex, col: spanColor_1 || mf.style.color });
                                    }
                                }
                                else {
                                    output.push({ type: 'math', latex: latex, col: spanColor_1 || mf.style.color });
                                }
                            }
                        }
                    });
                }
                else {
                    output.push({ type: 'html', value: el.outerHTML });
                }
            }
        });
        return output;
    };
    // State saving on focus-out is handled in the first useEffect([isFocused]) above.
    var getMathData = function (skipUndo, force) {
        if (skipUndo === void 0) { skipUndo = false; }
        if (force === void 0) { force = false; }
        var editor = editorRef.current;
        // Use the REF (not the state variable) to avoid the stale-closure bug.
        // When the user clicks away from a MathEditor whose keypad is open,
        // setmathKeyboardVisible(false) is called but the state update is batched.
        // The getMathData closures captured by setTimeout / useEffect still hold
        // the OLD mathKeyboardVisible=true, causing every save to be skipped and
        // the data to remain empty.  The WhiteBoard empty-text cleanup then
        // removes the textbox.  Reading the ref at call-time picks up the latest
        // value regardless of which render created the closure.
        // DO NOT REMOVE — see docs/components/MathEditor.md
        if (!force && (mathKeyboardVisibleRef.current || viewOnlyRef.current)) {
            return;
        }
        var output = calcMathData();
        if (isTextUpdate && typeof index == 'number' && !isManipulative) {
            // if (!skipUndo && textEditHistoryIndexRef.current >= 0) {
            //   // const newSerialized = JSON.stringify(output);
            //   // const oldForUndo = textBeforeEditRef.current;
            //   // if (oldForUndo !== null && oldForUndo !== newSerialized) {
            //   //   updateStateEntryNew(textEditHistoryIndexRef.current, {
            //   //     type: 'text',
            //   //     id: index,
            //   //     propertyName: 'text',
            //   //     new: newSerialized,
            //   //   });
            //   // }
            //   // textBeforeEditRef.current = null;
            //   // textEditHistoryIndexRef.current = -1;
            // }
            setText(function (prev) {
                var newText = __spreadArray([], prev, true);
                if (!newText[index] || !newText[index].data)
                    return prev;
                newText[index].data.text = output;
                return newText;
            });
        }
        else {
            cb === null || cb === void 0 ? void 0 : cb(output, 'text', index, {
                height: editor === null || editor === void 0 ? void 0 : editor.clientHeight,
                width: editor === null || editor === void 0 ? void 0 : editor.clientWidth,
            });
        }
    };
    // Keep ref in sync so unmount cleanup always calls the latest version
    getMathDataRef.current = getMathData;
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g;
        // Skip if insertMath() was called recently — the editor is being mutated
        // directly and a re-render with stale/empty data would destroy the new field.
        var insertGuardAge = Date.now() - insertMathGuardRef.current;
        var editorIsEmpty = !editorRef.current || editorRef.current.childNodes.length === 0;
        if (insertGuardAge < 500) {
            return;
        }
        // If the editor DOM is empty (first mount, React Strict Mode remount, or
        // after a full reset), always inject data regardless of focus state.
        // A duplicated text item is immediately selected (isFocused=true) on mount,
        // so without this bypass the focus guard below would block the initial
        // setMathData call and the duplicated editor would render empty.
        // Using the live DOM state (childNodes.length) rather than a mount flag
        // also handles React Strict Mode's double-invocation: on the second run
        // the component DOM is fresh/empty even though refs still have old values.
        if (!editorIsEmpty) {
            if (!viewOnlyRef.current && (mathKeyboardVisibleRef.current || isFocusedRef.current)) {
                return;
            }
            var editor = editorRef.current;
            if (editor) {
                var activeEl = document.activeElement;
                if (activeEl === editor || editor.contains(activeEl)) {
                    return;
                }
            }
        }
        // Early bail-out: compare incoming data with current editor content to avoid
        // unnecessary rebuilds caused by new object references with identical content.
        // This prevents the cascade: tapping one editor → parent re-render → other
        // editors get new data refs → rebuild → destroys cursor in the focused editor.
        if (!editorIsEmpty && editorRef.current) {
            var incoming = typeof ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.text) !== 'string'
                ? (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.text
                : (data === null || data === void 0 ? void 0 : data.type) === 'equation'
                    ? [
                        {
                            type: 'math',
                            value: '',
                            latex: equationStringToLatex(data.data.text.trim()),
                            col: (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.color,
                        },
                    ]
                    : [{ value: (_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.text, type: 'html' }];
            var current = calcMathData();
            // Quick length check before expensive stringify comparison
            if ((current === null || current === void 0 ? void 0 : current.length) === (incoming === null || incoming === void 0 ? void 0 : incoming.length) &&
                JSON.stringify(current) === JSON.stringify(incoming)) {
                return;
            }
        }
        // Clear any pending blur timeout so it doesn't block reloading text
        // (e.g., when user clicks Done then Edit quickly)
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (typeof ((_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.text) !== 'string') {
            setMathData((_f = data === null || data === void 0 ? void 0 : data.data) === null || _f === void 0 ? void 0 : _f.text);
        }
        else if ((data === null || data === void 0 ? void 0 : data.type) === 'equation') {
            // Legacy "equation" items store the formula as a plain string (e.g. "30/60 + 15/60 =").
            // Convert a/b notation to \frac{a}{b} and render as a single math expression.
            var latex = equationStringToLatex(data.data.text.trim());
            setMathData([{ type: 'math', value: '', latex: latex, col: (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.color }]);
        }
        else {
            // Plain-string text (e.g. imported from production JSON) carries line
            // breaks as raw "\n". Those collapse to a single space once dropped into
            // innerHTML, so convert them to <br> to preserve the authored line breaks.
            setMathData([{ value: data.data.text.replace(/\r\n|\r|\n/g, '<br>'), type: 'html' }]);
        }
    }, [(_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.text]);
    var setMathData = function (data) {
        var editor = editorRef.current;
        // Clean up existing MathfieldElement instances before clearing editor
        var existingMathFields = editor.querySelectorAll('math-field');
        existingMathFields.forEach(function (mf) {
            var mathFieldElement = mf;
            mathFieldInstancesRef.current.delete(mathFieldElement);
        });
        editor.innerHTML = '';
        // Use DocumentFragment to batch all DOM appends (single reflow)
        var fragment = document.createDocumentFragment();
        var deferredMathFields = [];
        data === null || data === void 0 ? void 0 : data.forEach(function (item) {
            var _a, _b;
            if (item.type === 'text') {
                // Skip WHITESPACE_CHAR items (shouldn't be in data, but handle legacy data)
                if (item.value === WHITESPACE_CHAR)
                    return;
                fragment.appendChild(document.createTextNode(normalizePlaceholderText(item.value)));
            }
            else if (item.type === 'math') {
                // In manipulative label editors, single letters/digits stored as math
                // (e.g. via the math keypad) must render identically to surrounding
                // plain text. Render them as text nodes instead of math-fields to
                // avoid the math-italic / different x-height appearance.
                if (isManipulative) {
                    var latex = item.latex || '';
                    var simpleLetter = /^[a-zA-Z0-9]$/.test(latex);
                    var textModeMatch = latex.match(/^\\text\{([a-zA-Z0-9])\}$/);
                    if (simpleLetter) {
                        fragment.appendChild(document.createTextNode(latex));
                        return;
                    }
                    else if (textModeMatch) {
                        fragment.appendChild(document.createTextNode(textModeMatch[1]));
                        return;
                    }
                }
                var mf = createMathField(editor);
                var col = isManipulative ? ((_b = (_a = props.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.color) || item.col : item.col;
                if (col) {
                    mf.style.color = col;
                }
                // WHITESPACE_CHAR spacer before mathfield for cursor positioning
                fragment.appendChild(document.createTextNode(WHITESPACE_CHAR));
                fragment.appendChild(mf);
                // Defer value assignment until after fragment is attached to DOM
                deferredMathFields.push({ mf: mf, latex: item.latex || '' });
                // WHITESPACE_CHAR spacer after mathfield for cursor positioning
                fragment.appendChild(document.createTextNode(WHITESPACE_CHAR));
            }
            else if (item.type === 'html') {
                var wrapper = document.createElement('div');
                wrapper.innerHTML = normalizePlaceholderText(item.value);
                // Append every top-level node, not just the first. Plain-string text
                // (where "\n" was converted to <br> above) parses into multiple
                // siblings — text, <br>, text — and taking only firstChild dropped
                // everything after the first line break. Snapshot childNodes first
                // since appendChild mutates the live list.
                Array.from(wrapper.childNodes).forEach(function (node) {
                    var _a;
                    // Drop empty / ZWS-only spans from legacy data (mf spacer wrappers
                    // and old color-anchor spans). Re-injecting them places a ZWS
                    // sibling next to typed text and triggers font-fallback distortion
                    // on adjacent letters (a / e). Spacers around math-fields are
                    // recreated by the math branch above when needed.
                    var isEmptySpan = node.nodeType === Node.ELEMENT_NODE &&
                        node.tagName === 'SPAN' &&
                        !node.querySelector('math-field') &&
                        ((_a = node.textContent) !== null && _a !== void 0 ? _a : '').replace(/[​\s]/g, '').length === 0;
                    if (isEmptySpan)
                        return;
                    fragment.appendChild(node);
                });
            }
        });
        // Single DOM attachment — triggers one reflow
        editor.appendChild(fragment);
        justHydratedRef.current = Date.now();
        // Set values after DOM insertion so connectedCallback has fired
        deferredMathFields.forEach(function (_a) {
            var mf = _a.mf, latex = _a.latex;
            ensureAriaLiveText(mf);
            mf.value = latex;
        });
        // Merge adjacent text nodes into one.  This matters in manipulative label
        // editors where a math item converted to a text node (e.g. 'a') would
        // otherwise sit next to the preceding text node ('pa') as two separate
        // anonymous flex items in Safari — causing the second character to render
        // at a subtly different size.  normalize() coalesces them into 'paa',
        // exactly what happens after blur→save→rebuild, which is why pressing
        // space "fixed" the size before this call was added.
        editor.normalize();
        // Verify all math-field values stuck after MathLive initialization completes.
        // When duplicating a MathEditor, connectedCallback may be deferred, causing
        // the synchronous mf.value = latex above to silently fail. This post-loop
        // check runs after the microtask queue drains (setTimeout(0)), by which time
        // connectedCallback has fired and _mathfield is ready.
        setTimeout(function () {
            var mathIdx = 0;
            var mfs = editor.querySelectorAll('math-field');
            data === null || data === void 0 ? void 0 : data.forEach(function (item) {
                if (item.type === 'math') {
                    var mf = mfs[mathIdx];
                    if (mf && mf.isConnected) {
                        var expected = item.latex || '';
                        if (mf.value !== expected) {
                            mf.value = expected;
                        }
                    }
                    mathIdx++;
                }
            });
        }, 0);
    };
    var findColorSpan = function (node) {
        while (node && node !== document.body) {
            if (node instanceof HTMLSpanElement && node.style.color && node.style.color !== '') {
                return node;
            }
            node = node.parentNode;
        }
        return null;
    };
    var unwrapElement = function (el) {
        var parent = el.parentNode;
        if (!parent)
            return;
        while (el.firstChild)
            parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
    };
    var unwrapNestedSpans = function (fragment) {
        var spans = fragment.querySelectorAll('span[style]');
        spans.forEach(function (span) { return unwrapElement(span); });
    };
    // After inserting a new color span, if it landed inside an ancestor color span
    // (because the selection was partial within that span), lift it out so siblings
    // are flat rather than nested: <red>before</red><new>sel</new><red>after</red>
    var liftSpanOutOfParent = function (span, parentSpan) {
        var grandParent = parentSpan.parentNode;
        if (!grandParent)
            return;
        var siblings = Array.from(parentSpan.childNodes);
        var spanIdx = siblings.indexOf(span);
        if (spanIdx === -1)
            return; // not a direct child — skip complex nesting
        var before = siblings.slice(0, spanIdx);
        var after = siblings.slice(spanIdx + 1);
        // Move the lifted span to grandParent, right after parentSpan
        grandParent.insertBefore(span, parentSpan.nextSibling);
        // Remove after-nodes from parentSpan
        after.forEach(function (n) { return parentSpan.removeChild(n); });
        // Wrap after-nodes in a new span with parentSpan's color
        if (after.length > 0) {
            var afterSpan_1 = document.createElement('span');
            afterSpan_1.style.color = parentSpan.style.color;
            after.forEach(function (n) { return afterSpan_1.appendChild(n); });
            grandParent.insertBefore(afterSpan_1, span.nextSibling);
        }
        // If nothing remains before the lifted span, remove the now-empty parentSpan
        if (before.length === 0) {
            grandParent.removeChild(parentSpan);
        }
    };
    var formatLink = function (url) {
        if (viewOnlyRef.current)
            return;
        var editor = editorRef.current;
        var sel = window.getSelection();
        if (!editor || !sel)
            return;
        // REMOVE LINK — find all anchors in the editor and unwrap them
        if (url === '') {
            var anchors = editor.querySelectorAll('a');
            anchors.forEach(function (anchor) {
                if (anchor.parentNode) {
                    var parent_2 = anchor.parentNode;
                    var firstChild = anchor.firstChild;
                    var lastChild = anchor.lastChild;
                    while (anchor.firstChild) {
                        parent_2.insertBefore(anchor.firstChild, anchor);
                    }
                    parent_2.removeChild(anchor);
                    // Select the unwrapped content
                    if (firstChild && lastChild && sel) {
                        var range_1 = document.createRange();
                        range_1.setStartBefore(firstChild);
                        range_1.setEndAfter(lastChild);
                        sel.removeAllRanges();
                        sel.addRange(range_1);
                        lastSelection.current = range_1.cloneRange();
                    }
                }
            });
            clickedAnchorRef.current = null;
            setActiveLink(null);
            getMathData(false, true);
            return;
        }
        var range = null;
        // Try to get current selection
        if (sel.rangeCount > 0) {
            range = sel.getRangeAt(0);
        }
        // If no current selection or selection is collapsed, try to use saved selection (for iPad)
        if (!range || range.collapsed) {
            if (lastSelection.current && !lastSelection.current.collapsed) {
                range = lastSelection.current.cloneRange();
                // Restore the selection so the link can be applied
                sel.removeAllRanges();
                sel.addRange(range);
            }
            else {
                return;
            }
        }
        // Do not touch MathLive content — linking math-fields is not supported
        if (range.commonAncestorContainer.nodeName === 'MATH-FIELD')
            return;
        // Find parent <a> if selection is already inside a link
        var findAnchor = function (node) {
            while (node && node !== editor) {
                if (node instanceof HTMLAnchorElement)
                    return node;
                node = node.parentNode;
            }
            return null;
        };
        var anchor = findAnchor(range.commonAncestorContainer);
        // UPDATE EXISTING LINK
        if (anchor) {
            anchor.setAttribute('href', url);
            anchor.setAttribute('target', '_blank');
            setActiveLink(url);
            return;
        }
        // CREATE NEW LINK
        var link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        var contents = range.extractContents();
        link.appendChild(contents);
        range.insertNode(link);
        // Move caret after link
        range.setStartAfter(link);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        setActiveLink(url);
        getMathData(false, true);
    };
    // Decide which side to place the keyboard (once when it becomes visible).
    // Only left and below placements are allowed — right and above are
    // disabled because they collide with the right side panel and the top
    // formatting toolbar respectively.
    // For whiteboard editors (isTextUpdate), position is relative to the canvas
    // scroll container so the keyboard scrolls with the text box.
    // For other editors, position is in document space (portaled to body).
    var KEYPAD_WIDTH = 520; // 503px box-sizing:border-box + margin buffer
    useEffect(function () {
        if (!mathKeyboardVisible || !editorRef.current)
            return;
        // Defer one frame so the browser has painted the DOM before we measure.
        var raf = requestAnimationFrame(function () {
            var _a, _b, _c;
            if (!editorRef.current)
                return;
            var editor = editorRef.current;
            var editorRect = editor.getBoundingClientRect();
            var gap = 10;
            var sidebarEl = document.querySelector('.sidebar');
            var bottomToolbarEl = document.querySelector('.toolBottomPanelContainer');
            var sidebarRight = sidebarEl ? sidebarEl.getBoundingClientRect().right : 0;
            var bottomToolbarTop = bottomToolbarEl
                ? bottomToolbarEl.getBoundingClientRect().top
                : window.innerHeight;
            var keypadHeight = ((_a = keypadWrapperRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 250;
            var headerEl = document.querySelector('.appHeader');
            var headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
            var scrollX = window.scrollX || 0;
            var scrollY = window.scrollY || 0;
            // For canvas editors (isTextUpdate), the keyboard lives inside the CSS
            // transform: scale(zoomLevel) container. All viewport measurements derived
            // from getBoundingClientRect() must be compared against CSS dimensions
            // (keypadHeight, KEYPAD_WIDTH) after converting those to viewport units by
            // multiplying by scale.
            var scale = isTextUpdate ? zoomLevel || 1 : 1;
            // Viewport pixel equivalents of fixed CSS dimensions:
            var effectiveKeypadWidth = KEYPAD_WIDTH * scale;
            var effectiveKeypadHeight = keypadHeight * scale;
            // The text-editor formatting toolbar sits below the editorDiv inside the
            // canvas; its CSS height is ~80px, so its viewport height scales with zoom.
            var toolbarOffset = 80 * scale;
            // For canvas editors, get mainHolder bounds to prevent the keyboard from
            // escaping the canvas. mathEditorParent has transform:translate(-50%,-50%)
            // which creates a new CSS containing block, so mainHolder's overflow:hidden
            // won't clip keyboard children that extend beyond its visual boundary.
            var mainEl = isTextUpdate ? document.querySelector('.mainHolder') : null;
            var mainRect = mainEl ? mainEl.getBoundingClientRect() : null;
            var rightSpace = window.innerWidth - editorRect.right;
            var leftSpace = editorRect.left - sidebarRight;
            // Gate left placement on keyboard fitting within the canvas left edge.
            var wouldFitInCanvasLeft = mainRect
                ? editorRect.left - effectiveKeypadWidth - gap >= mainRect.left
                : true;
            var top;
            var left;
            var isBelow = false;
            // Text editors skip the right-side branch — a right-anchored keypad ends
            // up overlapping the formatting toolbar (B / I / U / align / link) which
            // sits directly below the text box. Left → below → above order keeps the
            // toolbar fully visible. Other manipulatives keep the original priority.
            var isTextEditor = idPreffix === 'text' || idPreffix === 'tableText';
            // The editorFocusOptions accessibility panel (pencil/kebab) also renders
            // to the left of the box, top-aligned — when the keypad is placed there
            // too it would sit right on top of the panel. Push the keypad down below
            // the panel instead of moving the panel itself.
            var focusPanelHeight = enableFocusPanel && !focusPanelRight
                ? ((_b = focusOptionsRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0
                : 0;
            if (!isTextEditor && rightSpace >= effectiveKeypadWidth + gap) {
                top = editorRect.top + scrollY;
                left = editorRect.right + gap + scrollX;
            }
            else if (leftSpace >= effectiveKeypadWidth + gap && wouldFitInCanvasLeft) {
                top = editorRect.top + scrollY + (focusPanelHeight ? focusPanelHeight + gap : 0);
                left = editorRect.left - effectiveKeypadWidth - gap + scrollX;
            }
            else {
                // below (fallback when not enough room on the left or keyboard would clip canvas edge)
                top = editorRect.bottom + toolbarOffset + gap + scrollY;
                left = editorRect.left + scrollX;
                isBelow = true;
            }
            // Clamp: stay below header, above bottom toolbar, within horizontal bounds.
            // For below placement the bottom-toolbar clamp is skipped: at high zoom levels
            // effectiveKeypadHeight can be larger than the remaining viewport space, which
            // would pull the keyboard up and overlap the editor. It is better to let the
            // keyboard extend below the toolbar than to cover the text being edited.
            var belowBaseline = editorRect.bottom + toolbarOffset + gap + scrollY;
            top = Math.max(headerBottom + gap + scrollY, top);
            if (!isBelow) {
                top = Math.min(bottomToolbarTop - effectiveKeypadHeight - gap + scrollY, top);
            }
            // For below placement, guarantee top never rises above the editor baseline
            // (clamping could push it up into the editor at high zoom).
            if (isBelow) {
                top = Math.max(belowBaseline, top);
            }
            left = Math.max(sidebarRight + gap + scrollX, left);
            left = Math.min(window.innerWidth - effectiveKeypadWidth - gap + scrollX, left);
            // For whiteboard editors, convert to coordinates relative to the
            // mathEditorParent so the keyboard is a direct child (no portal) and
            // scrolls with the text box automatically.
            if (isTextUpdate) {
                var parentEl = (_c = editorRef.current) === null || _c === void 0 ? void 0 : _c.closest('.mathEditorParent');
                if (parentEl) {
                    var parentRect = parentEl.getBoundingClientRect();
                    // Divide by zoomLevel because the parent lives inside the scaled canvas
                    // (transform: scale(zoomLevel) on mainHolder). CSS top/left are in the
                    // pre-transform coordinate system, so viewport pixels must be converted
                    // to element pixels by dividing by the scale factor.
                    top = (top - scrollY - parentRect.top) / scale;
                    left = (left - scrollX - parentRect.left) / scale;
                    // Hard-clamp to canvas bounds in element space. The pre-check above
                    // catches most cases, but this safety net handles edge cases (e.g. the
                    // editor itself is very close to the canvas edge).
                    if (mainRect) {
                        var minLeftCss = (mainRect.left - parentRect.left) / scale;
                        var maxLeftCss = (mainRect.right - parentRect.left) / scale - KEYPAD_WIDTH;
                        left = Math.max(minLeftCss, left);
                        left = Math.min(maxLeftCss, left);
                    }
                }
            }
            // editorRect.height is in viewport px; after the isTextUpdate conversion
            // top is in element-space px, so divide by scale to match coordinate spaces.
            var finalTop = top + (offsetKeypadByEditorHeight ? editorRect.height / scale : 0);
            var finalLeft = left;
            // Final viewport bound for portaled editors (tick / jump-bar /
            // fraction-tile labels — everything except the canvas text editor, which
            // uses the isTextUpdate canvas-space clamps above). On small devices like
            // iPad a below-placed keypad can run off the bottom of the screen; this
            // guarantees the whole keypad stays visible. Applied to finalTop so the
            // offsetKeypadByEditorHeight shift is included. Skipped when the keypad is
            // taller/wider than the available band so we never force it up over the
            // editor (extreme cases fall back to the prior best-effort position).
            if (!isTextUpdate) {
                var viewportBottom = Math.min(bottomToolbarTop, window.innerHeight);
                var minTop = headerBottom + gap + scrollY;
                var maxTop = viewportBottom - effectiveKeypadHeight - gap + scrollY;
                if (maxTop >= minTop) {
                    finalTop = Math.min(Math.max(finalTop, minTop), maxTop);
                }
                var minLeft = Math.max(sidebarRight, 0) + gap + scrollX;
                var maxLeft = window.innerWidth - effectiveKeypadWidth - gap + scrollX;
                // Keep the left edge on-screen even if the keypad is wider than the
                // available horizontal band (buttons read left-to-right).
                finalLeft = maxLeft >= minLeft ? Math.min(Math.max(finalLeft, minLeft), maxLeft) : minLeft;
            }
            setKeypadPos({
                top: finalTop,
                left: finalLeft,
            });
        }); // end rAF
        return function () { return cancelAnimationFrame(raf); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mathKeyboardVisible]);
    // Scroll editor into view when native iPad keyboard appears
    useEffect(function () {
        var editor = editorRef.current;
        if (!editor)
            return;
        // Detect if this is a touch device (iPad/mobile)
        // Handler for when editor or math field gets focus (native keyboard appears)
        var handleFocusForKeyboard = function () {
            // Don't scroll if the custom keypad is visible - we're handling positioning ourselves
            if (mathKeyboardVisibleRef.current)
                return;
            // Don't scroll if focus was triggered by a color change, not a user tap
            if (isColorChangeFocusRef.current)
                return;
            // Delay to allow keyboard to appear and viewport to resize
            setTimeout(function () {
                var _a;
                var editorRect = editor.getBoundingClientRect();
                // Use visualViewport for accurate viewport height when keyboard is open
                var viewportHeight = ((_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.height) || window.innerHeight;
                // Calculate if editor is hidden or partially hidden by keyboard
                var editorBottom = editorRect.bottom;
                var editorTop = editorRect.top;
                // If editor would be covered by keyboard, manually scroll the
                // nearest scrollable parent instead of using scrollIntoView().
                // scrollIntoView miscalculates offsets when the parent uses
                // transform: scale(zoomLevel).
                if (editorBottom > viewportHeight || editorTop < 0) {
                    var scrollParent = editor.closest('[class*="scrollbar"], [class*="Scrollbar"]') ||
                        editor.closest('[style*="overflow"]');
                    if (scrollParent) {
                        var parentRect = scrollParent.getBoundingClientRect();
                        // editorRect/parentRect are visual (post-transform) viewport pixels, but
                        // scrollTop moves the scrollParent's content in pre-transform (unscaled)
                        // layout pixels since transform: scale(zoomLevel) on mainHolder doesn't
                        // affect layout size. Divide the visual delta by zoomLevel before adding
                        // it to scrollTop, or the offset is off by the zoom factor (see the same
                        // fix applied to the keypad positioning above).
                        var scale = zoomLevel || 1;
                        var targetY = scrollParent.scrollTop +
                            (editorRect.top - parentRect.top - parentRect.height / 2 + editorRect.height / 2) /
                                scale;
                        scrollParent.scrollTo({ top: targetY, behavior: 'smooth' });
                    }
                }
            }, 300); // Delay for keyboard animation
        };
        var mathFields;
        if (isTouchDevice) {
            // Listen to focus events on editor
            editor.addEventListener('focus', handleFocusForKeyboard, true);
            // Also listen to focus on any math-field elements
            mathFields = editor.querySelectorAll('math-field');
            mathFields === null || mathFields === void 0 ? void 0 : mathFields.forEach(function (mf) {
                mf.addEventListener('focus', handleFocusForKeyboard, true);
            });
        }
        return function () {
            editor.removeEventListener('focus', handleFocusForKeyboard, true);
            mathFields === null || mathFields === void 0 ? void 0 : mathFields.forEach(function (mf) {
                mf.removeEventListener('focus', handleFocusForKeyboard, true);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoomLevel]);
    useEffect(function () {
        var editor = editorRef.current;
        if (!editor)
            return;
        editor.childNodes.forEach(function (node) {
            var _a;
            if (node.nodeName === 'MATH-FIELD') {
                var mf = node;
                mf.style.color = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.color) || ColorEnum['BLACK'];
            }
        });
    }, [(_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.color]);
    // Auto-focus when the editor is selected (newly created or clicked)
    useEffect(function () {
        if (!isFocused)
            return;
        var editor = editorRef.current;
        if (!editor)
            return;
        // Short delay to ensure the editor is fully rendered
        var timeoutId = setTimeout(function () {
            var _a;
            // Don't steal focus if it's already within the editor
            // (e.g., user clicked in text area before/after the mathfield)
            var activeEl = document.activeElement;
            if (activeEl === editor || editor.contains(activeEl)) {
                return;
            }
            // Don't steal focus if the caller has opted in and the toolbar already has focus
            if (suppressAutoFocusWhenToolbarActive && ((_a = activeEl === null || activeEl === void 0 ? void 0 : activeEl.closest) === null || _a === void 0 ? void 0 : _a.call(activeEl, '.bottomPanelTool'))) {
                return;
            }
            // First try to focus a math-field if one exists
            var mathField = editor.querySelector('math-field');
            if (mathField) {
                safeFocusMathField(mathField);
            }
            else {
                // Otherwise focus the editor itself
                editor.focus({ preventScroll: true });
            }
        }, 50);
        return function () { return clearTimeout(timeoutId); };
    }, [isFocused]);
    // Wait for MathLive to fully register
    useEffect(function () {
        var element = customElements.get('math-field');
        if (!element) {
            customElements.whenDefined('math-field').then(function () {
                setMlReady(true);
            });
            customElements.define('math-field', MathfieldElement);
        }
        else {
            setMlReady(true);
        }
    }, []);
    useEffect(function () {
        var changeColour = function (payload) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            console.log('[color-debug] changeColour fired', {
                idPreffix: idPreffix,
                index: index,
                payloadColor: payload.color,
                contextColor: color,
                viewonly: viewOnlyRef.current,
                isFocused: isFocusedRef.current,
                activeEl: (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.tagName,
                activeElInsideEditor: (_c = (_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement)) !== null && _c !== void 0 ? _c : false,
            });
            if (viewOnlyRef.current)
                return;
            var localColor = payload.color;
            var sel = window.getSelection();
            var editor = editorRef.current;
            if (!editor || !sel) {
                console.log('[color-debug] changeColour bail: no editor/sel');
                return;
            }
            var mf = lastFocusedMathField.current;
            if (mf) {
                console.log('[color-debug] changeColour: mf-focused branch, applyStyle', localColor);
                // Save scroll position before focus — focus + applyStyle can trigger
                // browser auto-scroll that miscalculates offsets due to transform: scale().
                var scrollEl_2 = editor.closest('.customScrollbarContent');
                var savedScrollTop_2 = (_d = scrollEl_2 === null || scrollEl_2 === void 0 ? void 0 : scrollEl_2.scrollTop) !== null && _d !== void 0 ? _d : 0;
                var savedScrollLeft_2 = (_e = scrollEl_2 === null || scrollEl_2 === void 0 ? void 0 : scrollEl_2.scrollLeft) !== null && _e !== void 0 ? _e : 0;
                // Suppress the 300ms-delayed scroll-into-view handlers that fire on focus.
                // Focus here is programmatic (for MathLive selection), not user-initiated.
                isColorChangeFocusRef.current = true;
                // Re-focus so MathLive's selection is active after toolbar click
                safeFocusMathField(mf);
                mf.applyStyle({ color: localColor });
                if (scrollEl_2) {
                    requestAnimationFrame(function () {
                        scrollEl_2.scrollTop = savedScrollTop_2;
                        scrollEl_2.scrollLeft = savedScrollLeft_2;
                    });
                }
                // Clear after the 300ms delayed scroll handlers would have checked the flag
                setTimeout(function () {
                    isColorChangeFocusRef.current = false;
                }, 350);
                return;
            }
            if (sel.rangeCount === 0) {
                // For canvas text boxes (not manipulatives): no DOM selection means the
                // user wants to change ALL text color (e.g. context menu with no selection).
                if (!isManipulative && isTextUpdate && idPreffix === 'text') {
                    // Recolor all math-fields directly
                    editor.childNodes.forEach(function (node) {
                        if (node.nodeName === 'MATH-FIELD') {
                            node.style.color = localColor;
                        }
                    });
                    // Select all content and rewrap in a color span
                    var allRange = document.createRange();
                    allRange.selectNodeContents(editor);
                    if (!allRange.collapsed) {
                        var fragment_1 = allRange.extractContents();
                        unwrapNestedSpans(fragment_1);
                        var span_1 = document.createElement('span');
                        span_1.style.color = localColor;
                        span_1.appendChild(fragment_1);
                        allRange.insertNode(span_1);
                    }
                    (_f = getMathDataRef.current) === null || _f === void 0 ? void 0 : _f.call(getMathDataRef);
                }
                else {
                    console.log('[color-debug] changeColour bail: rangeCount=0 (selection lost — likely picker stole focus on iPad)');
                }
                return;
            }
            var range = sel.getRangeAt(0);
            if (!editor.contains(range.commonAncestorContainer)) {
                console.log('[color-debug] changeColour bail: selection not in this editor', {
                    commonAncestor: (_h = (_g = range.commonAncestorContainer) === null || _g === void 0 ? void 0 : _g.tagName) !== null && _h !== void 0 ? _h : range.commonAncestorContainer.nodeName,
                });
                return;
            }
            console.log('[color-debug] changeColour: editor selection branch', {
                collapsed: range.collapsed,
                startContainer: range.startContainer.nodeName,
                startOffset: range.startOffset,
            });
            // Save scroll position — DOM manipulation (extractContents/insertNode)
            // can trigger browser auto-scroll due to transform: scale() offsets.
            var scrollEl = editor.closest('.customScrollbarContent');
            var savedScrollTop = (_j = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _j !== void 0 ? _j : 0;
            var savedScrollLeft = (_k = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _k !== void 0 ? _k : 0;
            // Collapsed cursor (no text selected): no DOM mutation. The picker
            // color now flows through DataContext and gets enforced at type time
            // by onBeforeInput / insertTextIntoEditor / enforceCaretColor \u2014 all
            // three read the current color and wrap typed chars in a fresh span,
            // so a DOM "color anchor" is no longer required.
            //
            // Removing the anchor matters: the anchor used to be a span containing
            // a U+200B, with the caret placed inside it. Typing then put the new
            // chars in the same text node as the ZWS (`\u200Be`), which the
            // browser shapes as one run. `oso-sans` has no glyph for U+200B so the
            // run falls back to a different font \u2014 and adjacent letters (most
            // visibly `e` and `a`) end up rendered in the fallback font. By not
            // inserting the anchor at all, no ZWS ever sits next to typed text.
            if (range.collapsed) {
                // For canvas text boxes: collapsed cursor with no selection → change all text.
                if (!isManipulative && isTextUpdate && idPreffix === 'text') {
                    editor.childNodes.forEach(function (node) {
                        if (node.nodeName === 'MATH-FIELD') {
                            node.style.color = localColor;
                        }
                    });
                    var allRange = document.createRange();
                    allRange.selectNodeContents(editor);
                    if (!allRange.collapsed) {
                        var fragment_2 = allRange.extractContents();
                        unwrapNestedSpans(fragment_2);
                        var span_2 = document.createElement('span');
                        span_2.style.color = localColor;
                        span_2.appendChild(fragment_2);
                        allRange.insertNode(span_2);
                    }
                    (_l = getMathDataRef.current) === null || _l === void 0 ? void 0 : _l.call(getMathDataRef);
                }
                if (scrollEl) {
                    requestAnimationFrame(function () {
                        scrollEl.scrollTop = savedScrollTop;
                        scrollEl.scrollLeft = savedScrollLeft;
                    });
                }
                return;
            }
            // Text is selected: extract, unwrap any nested color spans, then rewrap
            // with the new color. Using extractContents removes from the live DOM and
            // returns the fragment in one step, so partial selections inside an existing
            // color span are handled cleanly.
            var fragment = range.extractContents();
            unwrapNestedSpans(fragment);
            var span = document.createElement('span');
            span.style.color = localColor;
            span.appendChild(fragment);
            range.insertNode(span);
            // If the new span landed inside an ancestor color span (partial selection
            // within an existing colored region), lift it out to produce flat siblings:
            // <orig-color>before</orig-color><new-color>sel</new-color><orig-color>after</orig-color>
            var parentColorSpan = findColorSpan(span.parentNode);
            if (parentColorSpan && parentColorSpan !== span) {
                liftSpanOutOfParent(span, parentColorSpan);
            }
            var newRange = document.createRange();
            newRange.selectNodeContents(span);
            sel.removeAllRanges();
            sel.addRange(newRange);
            lastSelection.current = newRange.cloneRange();
            // Restore scroll position after DOM manipulation
            if (scrollEl) {
                requestAnimationFrame(function () {
                    scrollEl.scrollTop = savedScrollTop;
                    scrollEl.scrollLeft = savedScrollLeft;
                });
            }
        };
        var onClick = function (data) {
            var _a, _b;
            if (viewOnlyRef.current)
                return;
            switch (data.id) {
                case 'bold':
                    if (idPreffix !== 'tableText')
                        exec('bold');
                    break;
                case 'italic':
                    if (idPreffix !== 'tableText')
                        exec('italic');
                    break;
                case 'underline':
                    if (idPreffix !== 'tableText')
                        exec('underline');
                    break;
                case 'mathtext':
                    if (mathKeyboardVisibleRef.current) {
                        // Keypad already open — exit the current math-field.
                        var mf = lastFocusedMathField.current;
                        if (mf && ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.contains(mf))) {
                            if ((mf.value || '').trim() === '') {
                                // Empty math-field — remove it and place cursor where it was
                                var next = mf.nextSibling;
                                mathFieldInstancesRef.current.delete(mf);
                                lastFocusedMathField.current = null;
                                mf.remove();
                                batchDisposeMathfields([mf]);
                                if (next)
                                    placeCaretAfter(next);
                                else
                                    (_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.focus({ preventScroll: true });
                            }
                            else {
                                // Has content — move cursor just after the math-field
                                var spacer = mf.nextSibling;
                                placeCaretAfter(spacer !== null && spacer !== void 0 ? spacer : mf);
                                lastFocusedMathField.current = null;
                            }
                            setmathKeyboardVisible(false);
                            setActiveMathText && setActiveMathText(-1);
                            getMathData();
                        }
                    }
                    else {
                        // Arm the post-open focus shift before insertMath kicks the
                        // mf-focus → keypad-open chain. The keypad-visibility useEffect
                        // reads this ref once the keypad mounts and lands focus on '1'.
                        focusKeypadOnOpenRef.current = true;
                        insertMath();
                    }
                    break;
                case 'closeMathText':
                    setmathKeyboardVisible(false);
                    setSelectedTool([]);
                    setActiveMathText && setActiveMathText(-1);
                    break;
                case 'linkInput':
                    formatLink(data.val);
                    break;
                case 'removeLink':
                    formatLink('');
                    break;
                case 'leftAlign':
                    if (isTextUpdate && typeof index == 'number') {
                        setText(function (prev) {
                            var newText = __spreadArray([], prev, true);
                            var oldData = newText[index].data.alignment;
                            newText[index].data.alignment = 'left';
                            saveState({
                                type: 'text',
                                id: index,
                                propertyName: 'alignment',
                                old: oldData,
                                new: 'left',
                            });
                            return newText;
                        });
                    }
                    else {
                        cb === null || cb === void 0 ? void 0 : cb('left', 'alignment', index);
                    }
                    break;
                case 'centerAlign':
                    if (isTextUpdate && typeof index == 'number') {
                        setText(function (prev) {
                            var newText = __spreadArray([], prev, true);
                            var target = __assign({}, newText[index]);
                            var newData = __assign({}, target.data);
                            var oldData = newText[index].data.alignment;
                            newData.alignment = 'center';
                            target.data = newData;
                            newText[index] = target;
                            saveState({
                                type: 'text',
                                id: index,
                                propertyName: 'alignment',
                                old: oldData,
                                new: 'center',
                            });
                            return newText;
                        });
                    }
                    else {
                        cb === null || cb === void 0 ? void 0 : cb('center', 'alignment', index);
                    }
                    break;
                case 'rightAlign':
                    if (isTextUpdate && typeof index == 'number') {
                        setText(function (prev) {
                            var newText = __spreadArray([], prev, true);
                            var target = __assign({}, newText[index]);
                            var newData = __assign({}, target.data);
                            var oldData = newText[index].data.alignment;
                            newData.alignment = 'right';
                            target.data = newData;
                            newText[index] = target;
                            saveState({
                                type: 'text',
                                id: index,
                                propertyName: 'alignment',
                                old: oldData,
                                new: 'right',
                            });
                            return newText;
                        });
                    }
                    else {
                        cb === null || cb === void 0 ? void 0 : cb('right', 'alignment', index);
                    }
                    break;
                case 'larger':
                    // Apply font size increase to selected text/math only. Table cells manage
                    // font size entirely through TableManipulativeComp's own bulk handler (a
                    // per-selection span override here would desync from that cell-level model).
                    if (idPreffix !== 'tableText') {
                        execFontSize('larger');
                        if (!isTextUpdate) {
                            cb === null || cb === void 0 ? void 0 : cb('up', 'fontSize', index);
                        }
                    }
                    break;
                case 'smaller':
                    // Apply font size decrease to selected text/math only. See 'larger' above.
                    if (idPreffix !== 'tableText') {
                        execFontSize('smaller');
                        if (!isTextUpdate) {
                            cb === null || cb === void 0 ? void 0 : cb('down', 'fontSize', index);
                        }
                    }
                    break;
            }
        };
        // Force sync: immediately write current DOM content to state
        // (bypasses mathKeyboardVisible guard used by getMathData)
        var forceSync = function () {
            var editor = editorRef.current;
            if (!editor || viewOnlyRef.current)
                return;
            var output = calcMathData();
            if (isTextUpdate && typeof index == 'number' && !isManipulative) {
                setText(function (prev) {
                    var newText = __spreadArray([], prev, true);
                    if (!newText[index] || !newText[index].data)
                        return prev;
                    newText[index] = __assign(__assign({}, newText[index]), { data: __assign(__assign({}, newText[index].data), { text: output }) });
                    return newText;
                });
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('colourChange', changeColour);
            customEventHandler.addEventListener('bottomPanel', onClick);
            customEventHandler.addEventListener('forceSync', forceSync);
        }
        else {
            getMathData();
        }
        return function () {
            customEventHandler.removeEventListener('colourChange', changeColour);
            customEventHandler.removeEventListener('bottomPanel', onClick);
            customEventHandler.removeEventListener('forceSync', forceSync);
        };
    }, [isFocused, color, mlReady]);
    // Save and restore all scroll positions around a mf.insert() call.
    // mf.insert() for structural commands (frac, sqrt, exponent, placeholder)
    // triggers MathLive's async scrollIntoView on a later frame, jumping the
    // viewport. We lock the scroll container by immediately snapping it back
    // inside a 'scroll' event listener for a 500 ms window after the insert.
    var preserveScroll = function (fn) {
        var _a, _b, _c, _d, _e;
        var scrollEl = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent');
        var savedLeft = (_b = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _b !== void 0 ? _b : 0;
        var savedTop = (_c = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _c !== void 0 ? _c : 0;
        var winX = window.scrollX;
        var winY = window.scrollY;
        var docEl = document.scrollingElement;
        var docLeft = (_d = docEl === null || docEl === void 0 ? void 0 : docEl.scrollLeft) !== null && _d !== void 0 ? _d : winX;
        var docTop = (_e = docEl === null || docEl === void 0 ? void 0 : docEl.scrollTop) !== null && _e !== void 0 ? _e : winY;
        // Override scrollLeft/scrollTop setters on the container so MathLive's
        // synchronous scrollIntoView inside mf.insert() is a no-op — this
        // prevents the one-frame blink that the event-listener approach cannot.
        // The getter returns the saved value so MathLive's internal layout reads
        // still see a consistent position.
        if (scrollEl) {
            Object.defineProperty(scrollEl, 'scrollLeft', {
                configurable: true,
                get: function () { return savedLeft; },
                set: function (v) { return console.log('[preserveScroll] scrollLeft set blocked:', v, '→', savedLeft); },
            });
            Object.defineProperty(scrollEl, 'scrollTop', {
                configurable: true,
                get: function () { return savedTop; },
                set: function (v) { return console.log('[preserveScroll] scrollTop set blocked:', v, '→', savedTop); },
            });
        }
        fn();
        // Unlock after MathLive has fully settled, then restore the saved position.
        setTimeout(function () {
            if (scrollEl) {
                delete scrollEl.scrollLeft;
                delete scrollEl.scrollTop;
                scrollEl.scrollLeft = savedLeft;
                scrollEl.scrollTop = savedTop;
            }
            window.scrollTo(winX, winY);
            if (docEl) {
                docEl.scrollLeft = docLeft;
                docEl.scrollTop = docTop;
            }
        }, 500);
    };
    var keypadBtnClickHandler = function (sae) {
        var _a, _b;
        // Debounce guard: prevent duplicate execution from touch+mouse dual-fire
        // or Firefox focus/blur re-entry within a single interaction
        var now = Date.now();
        if (now - keypadGuardRef.current < 80)
            return;
        keypadGuardRef.current = now;
        if (viewOnlyRef.current)
            return;
        var mf = lastFocusedMathField.current;
        var editor = editorRef.current;
        if (!editor && !mf)
            return;
        // Check character limit before inserting (except for backspace and clear).
        // Skip when inside a \frac argument — the input-event revert handler enforces
        // the per-side limit so each fraction side can independently reach maxCharacters.
        if (maxCharacters && sae.id !== 'back' && sae.id !== 'clear') {
            var currentCount = getTotalCharacterCount();
            if (currentCount >= maxCharacters && !(mf && isInFrac(mf))) {
                return;
            }
        }
        var keyMap = {
            one: '1',
            two: '2',
            three: '3',
            four: '4',
            five: '5',
            six: '6',
            seven: '7',
            eight: '8',
            nine: '9',
            zero: '0',
            plus: '+',
            minus: '-',
            multiply: '\\times ',
            divideSign: '\\div ',
            equals: '=',
            dot: '.',
            comma: ',',
            percent: '\\%',
            leftParen: '(',
            rightParen: ')',
            lessThan: '<',
            greaterThan: '>',
            lessThanOrEqual: '\\le ',
            greaterThanOrEqual: '\\ge ',
            dollar: '\\$',
            cent: '\\text{¢}',
            questionMark: '\\placeholder{}',
        };
        // Helper: only refocus if mathfield doesn't already have focus.
        // With the blur-handler keypad guard the mathfield stays focused,
        // so calling focus() again would disrupt cursor position (e.g.,
        // inside fraction placeholders).
        var ensureFocus = function () {
            if (mf && document.activeElement !== mf) {
                safeFocusMathField(mf);
            }
        };
        // Check if cursor is inside a specific LaTeX command (e.g. \frac or ^)
        // Uses two counters: commandSeen (hit a \frac or ^) and braceDepth (all
        // open braces, including those of nested commands like \placeholder{ }).
        // Check if cursor is inside a \frac or superscript by walking up
        // MathLive's internal atom tree. The model is on _mathfield.model
        // (not directly on the MathfieldElement custom element).
        // - 'genfrac' = \frac atom type
        // - parentBranch 'superscript' = cursor is inside ^{} argument
        // - !hasEmptyBranch('superscript') = cursor is at the BASE of a power
        //   (after inserting \placeholder{}^{\placeholder{}}, MathLive places
        //   the cursor at the base placeholder which is at root level, not in
        //   the superscript branch, so parentBranch check alone is not enough)
        var isCursorInside = function (command) {
            var _a;
            if (!mf)
                return false;
            try {
                var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
                if (!model)
                    return false;
                var atom = model.at(model.position);
                while (atom && atom.type !== 'root') {
                    if (command === 'frac' && atom.type === 'genfrac')
                        return true;
                    if (command === 'superscript') {
                        // Case 1: cursor is inside the ^{} exponent argument
                        if (atom.parentBranch === 'superscript')
                            return true;
                        // Case 2: this atom itself has a non-empty superscript
                        if (typeof atom.hasEmptyBranch === 'function' && !atom.hasEmptyBranch('superscript'))
                            return true;
                        // Case 3 (base-as-sibling): MathLive may place the base atom and
                        // SubsupAtom as siblings in the mathlist rather than parent-child.
                        // Check if the NEXT sibling has a non-empty superscript branch.
                        if (atom.parent && atom.parentBranch != null) {
                            var siblings = atom.parent.branch(atom.parentBranch);
                            if (siblings) {
                                for (var i = 0; i < siblings.length; i++) {
                                    if (siblings[i] === atom && i + 1 < siblings.length) {
                                        var next = siblings[i + 1];
                                        if (typeof next.hasEmptyBranch === 'function' &&
                                            !next.hasEmptyBranch('superscript'))
                                            return true;
                                        if (next.superscript)
                                            return true;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    atom = atom.parent;
                }
            }
            catch (_b) {
                // model API not accessible — fall through
            }
            return false;
        };
        if (sae.id === 'spaceBar') {
            if (mf) {
                ensureFocus();
                if (maxCharacters) {
                    var currentCount = getTotalCharacterCount();
                    if (currentCount >= maxCharacters) {
                        return;
                    }
                }
                mf.insert('\\;');
            }
            else {
                insertTextIntoEditor(' ');
            }
            return;
        }
        if (sae.id === 'back') {
            if (mf) {
                if (mf.value === '' || isMfOnlyEmptyFrac(mf.value)) {
                    // Math-field is empty or contains only an empty fraction template — remove it
                    var next = mf.nextSibling;
                    mathFieldInstancesRef.current.delete(mf);
                    if (lastFocusedMathField.current === mf) {
                        lastFocusedMathField.current = null;
                    }
                    mf.remove();
                    batchDisposeMathfields([mf]);
                    setmathKeyboardVisible(false);
                    setActiveMathText && setActiveMathText(-1);
                    if (next)
                        placeCaretAfter(next);
                    else
                        editor === null || editor === void 0 ? void 0 : editor.focus({ preventScroll: true });
                    getMathData();
                }
                else {
                    ensureFocus();
                    mf.executeCommand('deleteBackward');
                }
            }
            else {
                deleteBackwardInEditor();
            }
            return;
        }
        if (sae.id === 'power') {
            if (mf) {
                // DO NOT REMOVE — Nested power (^) prevention.
                // See docs/components/MathEditor.md "Nested Power (`^`) Prevention" section.
                //
                // Prevents power-inside-power nesting. Uses isCursorInside which
                // walks the atom tree and checks:
                //   Case 1: parentBranch === 'superscript'       → cursor in exponent
                //   Case 2: !hasEmptyBranch('superscript')       → atom has superscript
                //   Case 3: next sibling has superscript          → base-as-sibling
                if (isCursorInside('superscript'))
                    return;
                ensureFocus();
                if (maxCharacters) {
                    var currentCount = getTotalCharacterCount();
                    if (currentCount >= maxCharacters) {
                        return;
                    }
                }
                preserveScroll(function () { return mf.insert('\\placeholder{ }^{ \\placeholder{ }}'); });
            }
            return;
        }
        if (sae.id === 'clear') {
            if (mf) {
                mf.value = '';
                ensureFocus();
            }
            else {
                editor.innerText = '';
            }
            announce(t('text.mathEditor.mathCleared'));
            return;
        }
        if (sae.id === 'slash') {
            if (!mf) {
                insertTextIntoEditor('/');
                return;
            }
            // Check before ensureFocus() for the same reason as the power guard above
            if (isCursorInside('frac'))
                return;
            ensureFocus();
            if (maxCharacters) {
                var currentCount = getTotalCharacterCount();
                if (currentCount >= maxCharacters) {
                    return;
                }
            }
            preserveScroll(function () { return mf.insert("\\frac{\\placeholder{ }}{\\placeholder{ }}"); });
            return;
        }
        if (sae.id === 'questionMark') {
            if (!mf) {
                insertTextIntoEditor('?');
                return;
            }
            ensureFocus();
            if (maxCharacters) {
                var currentCount = getTotalCharacterCount();
                if (currentCount >= maxCharacters) {
                    return;
                }
            }
            // Insert placeholder and position cursor after it (not inside it)
            preserveScroll(function () { return mf.insert('\\placeholder{}', { selectionMode: 'after' }); });
            return;
        }
        var insertValue = (_b = (_a = keyMap[sae.id]) !== null && _a !== void 0 ? _a : sae === null || sae === void 0 ? void 0 : sae.title) !== null && _b !== void 0 ? _b : '';
        if (!insertValue)
            return;
        if (mf) {
            ensureFocus();
            // Check limit again before inserting
            if (maxCharacters) {
                var currentCount = getTotalCharacterCount();
                if (currentCount >= maxCharacters) {
                    return;
                }
            }
            preserveScroll(function () { return mf.insert(insertValue); });
            return;
        }
        insertTextIntoEditor(insertValue.replace(/\\(.)/g, '$1'));
    };
    var insertTextIntoEditor = function (text) {
        var _a, _b, _c, _d;
        var editor = editorRef.current;
        if (!editor)
            return;
        // Check character limit before inserting
        if (maxCharacters) {
            var currentCount = getTotalCharacterCount();
            var textToInsert = text.length;
            if (currentCount + textToInsert > maxCharacters) {
                // Only insert what fits
                var remaining = maxCharacters - currentCount;
                if (remaining <= 0) {
                    return; // No space left
                }
                text = text.substring(0, remaining);
            }
        }
        editor.focus({ preventScroll: true });
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
            return;
        var range = sel.getRangeAt(0);
        range.deleteContents();
        // Wrap the insertion in a span carrying the current picker color
        // whenever the caret isn't already inside a span with that color.
        // Keypad inserts skip the beforeinput color-wrap path, so without this,
        // picking a new color (or refocusing a textbox after a color change)
        // and then typing via the keypad inherits the surrounding stale color.
        // The changeColour handler bails when the selection is lost during the
        // picker interaction (common on iPad), so we cannot rely on the anchor
        // span it normally inserts — every insert must enforce the color
        // itself.
        var buildInsertNode = function () {
            var _a, _b, _c, _d;
            if (!isTextUpdate || isManipulative) {
                console.log('[color-debug] insertTextIntoEditor: skipping wrap (not text-update or manipulative)', {
                    isTextUpdate: isTextUpdate,
                    isManipulative: isManipulative,
                    text: text,
                });
                return document.createTextNode(text);
            }
            var pickerColor = ColorEnum[color] || ColorEnum['BLACK'];
            console.log('[color-debug] insertTextIntoEditor: entering wrap path', {
                text: text,
                color: color,
                pickerColor: pickerColor,
                startContainer: range.startContainer.nodeName,
                startOffset: range.startOffset,
            });
            var toRGB = function (c) {
                var _a, _b, _c, _d;
                var s = c.trim().toLowerCase();
                var hex = (_b = (_a = s.match(/^#([0-9a-f]{6})$/i)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : (_d = (_c = s.match(/^#([0-9a-f]{3})$/i)) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.replace(/./g, '$&$&');
                if (hex) {
                    return "".concat(parseInt(hex.slice(0, 2), 16), ",").concat(parseInt(hex.slice(2, 4), 16), ",").concat(parseInt(hex.slice(4, 6), 16));
                }
                var m = s.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
                return m ? "".concat(m[1], ",").concat(m[2], ",").concat(m[3]) : s;
            };
            var normalizedPicker = toRGB(pickerColor);
            var node = range.startContainer;
            var alreadyCorrect = false;
            while (node && node !== editor) {
                if (node instanceof HTMLElement && node.style.color) {
                    alreadyCorrect = toRGB(node.style.color) === normalizedPicker;
                    break;
                }
                node = node.parentNode;
            }
            console.log('[color-debug] insertTextIntoEditor: ancestor color check', {
                coloredAncestorTag: (_a = node === null || node === void 0 ? void 0 : node.tagName) !== null && _a !== void 0 ? _a : null,
                coloredAncestorColor: (_c = (_b = node === null || node === void 0 ? void 0 : node.style) === null || _b === void 0 ? void 0 : _b.color) !== null && _c !== void 0 ? _c : null,
                normalizedPicker: normalizedPicker,
                alreadyCorrect: alreadyCorrect,
            });
            if (alreadyCorrect)
                return document.createTextNode(text);
            // If the immediately-preceding sibling is a span with the same
            // picker color, append into it so the keypad doesn't fragment
            // typed runs into a chain of single-character spans.
            var prevNode = range.startContainer.nodeType === Node.TEXT_NODE
                ? range.startContainer.parentElement
                : range.startContainer.childNodes[range.startOffset - 1];
            var prevSpan = prevNode instanceof HTMLSpanElement &&
                prevNode.style.color &&
                toRGB(prevNode.style.color) === normalizedPicker
                ? prevNode
                : null;
            if (prevSpan) {
                console.log('[color-debug] insertTextIntoEditor: appending into matching prevSpan');
                // Append into the span's existing trailing text node when present so
                // consecutive same-color chars stay in a SINGLE text node — separate
                // text nodes form separate shaping runs and the browser renders the
                // glyphs with subtle width / kerning differences (visible as
                // "the font changed" on duplicated letters like "ee" / "aa").
                var last = prevSpan.lastChild;
                if (last && last.nodeType === Node.TEXT_NODE) {
                    last.data = ((_d = last.data) !== null && _d !== void 0 ? _d : '') + text;
                    return last;
                }
                var t_2 = document.createTextNode(text);
                prevSpan.appendChild(t_2);
                return t_2;
            }
            console.log('[color-debug] insertTextIntoEditor: creating fresh color span', {
                pickerColor: pickerColor,
                text: text,
            });
            var span = document.createElement('span');
            span.style.color = pickerColor;
            span.textContent = text;
            return span;
        };
        var insertNode = buildInsertNode();
        // Append-into-prevSpan path already attached the node; nothing to insert
        // at the range. Detect by checking parent — a freshly-created node has
        // no parent until insertNode() runs.
        if (!insertNode.parentNode) {
            range.insertNode(insertNode);
        }
        // Place caret at the end of the inserted text (inside the new/existing
        // span when wrapped, otherwise after the plain text node) so subsequent
        // keypad inserts merge into the same colored run.
        if (insertNode.nodeType === Node.ELEMENT_NODE) {
            var lastChild = insertNode.firstChild;
            if (lastChild) {
                range.setStart(lastChild, (_b = (_a = lastChild.textContent) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0);
            }
            else {
                range.setStartAfter(insertNode);
            }
        }
        else {
            range.setStart(insertNode, (_d = (_c = insertNode.textContent) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    };
    var deleteBackwardInEditor = function () {
        var editor = editorRef.current;
        if (!editor)
            return;
        editor.focus({ preventScroll: true });
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
            return;
        var range = sel.getRangeAt(0);
        if (!range.collapsed) {
            range.deleteContents();
            return;
        }
        var startContainer = range.startContainer, startOffset = range.startOffset;
        if (startContainer.nodeType === Node.TEXT_NODE && startOffset > 0) {
            var textNode = startContainer;
            textNode.deleteData(startOffset - 1, 1);
            range.setStart(textNode, startOffset - 1);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    };
    var focusHandler = function (e) {
        if (viewOnlyRef.current && !selectOnViewOnly)
            return;
        // If this item is already part of a multi-selection and no modifier key
        // is pressed, keep the multi-selection intact so dragging one item moves
        // all selected items together via the resizer.
        var mouseEvt = e;
        var isModifier = mouseEvt.ctrlKey || mouseEvt.metaKey || mouseEvt.shiftKey;
        if (!isModifier && selectedTool.length > 1 && selectedTool.includes(selectionType)) {
            return;
        }
        var _tool = selectTool(mouseEvt, selectionType, selectedTool, false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setSelectedTool(_tool);
        onFocusEvent && (cb === null || cb === void 0 ? void 0 : cb(calcMathData(), 'focus', index));
        // Firefox fix: in Firefox, pointerdown.stopPropagation() on math-field
        // can prevent Firefox from completing native focus assignment in popup
        // contexts. When the editor has no DOM focus at all and the click landed
        // on the outer mathEditorParent wrapper, focus the appropriate element
        // synchronously inside the click handler (user-gesture context).
        if (e.type === 'click') {
            var editor = editorRef.current;
            if (editor && !editor.contains(document.activeElement)) {
                var clickedMf = e.target.closest('math-field');
                var mathField = clickedMf !== null && clickedMf !== void 0 ? clickedMf : editor.querySelector('math-field');
                if (mathField) {
                    safeFocusMathField(mathField);
                }
                else {
                    editor.focus({ preventScroll: true });
                }
            }
        }
    };
    /**
     * Insert a line break (<br>) at the current cursor position.
     * Two <br>s are inserted when the cursor is at the very end of the
     * editor content — a single trailing <br> in contentEditable is just
     * the line terminator and doesn't produce a visible new line.
     */
    var insertLineBreak = function () {
        var _a;
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
            return;
        var range = sel.getRangeAt(0);
        range.deleteContents();
        var br = document.createElement('br');
        range.insertNode(br);
        // If the <br> is now the last meaningful node (only followed by
        // whitespace text nodes or nothing), add a second <br> so the
        // browser actually renders the new blank line.
        var next = br.nextSibling;
        while (next &&
            next.nodeType === Node.TEXT_NODE &&
            !((_a = next.textContent) !== null && _a !== void 0 ? _a : '').replace(/[\u200B\s]/g, '').length) {
            next = next.nextSibling;
        }
        if (!next) {
            var extra = document.createElement('br');
            br.after(extra);
        }
        // Place cursor after the (first) <br>
        range.setStartAfter(br);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    };
    // Post-input safety net for picker-color enforcement. Runs after every
    // input event; if the just-typed chars landed in a span whose color
    // doesn't match the current picker (e.g. preventDefault didn't fire
    // because the inputType wasn't `insertText`, or some other path inserted
    // raw text), extract that run and rewrap it. Belt-and-suspenders for the
    // long-standing client report that "newly typed text follows the old
    // color after focus-out → focus-in".
    var enforceCaretColor = function (e) {
        var _a, _b, _c, _d, _e;
        var ne = e.nativeEvent;
        console.log('[color-debug] enforceCaretColor entered', {
            inputType: ne.inputType,
            data: ne.data,
            isTextUpdate: isTextUpdate,
            isManipulative: isManipulative,
            viewonly: viewOnlyRef.current,
            contextColor: color,
        });
        if (!isTextUpdate || isManipulative || viewOnlyRef.current) {
            console.log('[color-debug] enforceCaretColor skip: not text-update / manipulative / viewonly');
            return;
        }
        if (!ne.data) {
            console.log('[color-debug] enforceCaretColor skip: no data (delete or synthesized event)');
            return;
        }
        // Only handle textual insertions (cover the variants iPad/desktop
        // browsers emit for keyboard typing, predictive text, autocomplete,
        // and paste). Skip composition mid-progress to avoid mid-IME mutations.
        var allowed = new Set([
            'insertText',
            'insertReplacementText',
            'insertFromComposition',
            'insertFromPaste',
        ]);
        if (!allowed.has(ne.inputType)) {
            console.log('[color-debug] enforceCaretColor skip: inputType not in allowlist', ne.inputType);
            return;
        }
        var insertedLen = ne.data.length;
        if (insertedLen <= 0)
            return;
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !sel.isCollapsed)
            return;
        var range = sel.getRangeAt(0);
        var editor = editorRef.current;
        if (!editor || !editor.contains(range.startContainer))
            return;
        if (range.startContainer.nodeType !== Node.TEXT_NODE)
            return;
        var textNode = range.startContainer;
        var startOffset = range.startOffset;
        if (startOffset < insertedLen)
            return;
        var pickerColor = ColorEnum[color] || ColorEnum['BLACK'];
        var toRGB = function (c) {
            var _a, _b, _c, _d;
            var s = c.trim().toLowerCase();
            var hex = (_b = (_a = s.match(/^#([0-9a-f]{6})$/i)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : (_d = (_c = s.match(/^#([0-9a-f]{3})$/i)) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.replace(/./g, '$&$&');
            if (hex) {
                return "".concat(parseInt(hex.slice(0, 2), 16), ",").concat(parseInt(hex.slice(2, 4), 16), ",").concat(parseInt(hex.slice(4, 6), 16));
            }
            var m = s.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
            return m ? "".concat(m[1], ",").concat(m[2], ",").concat(m[3]) : s;
        };
        var normalizedPicker = toRGB(pickerColor);
        // If the caret's nearest colored ancestor already matches the picker,
        // no fix-up needed.
        var coloredAncestor = null;
        var node = textNode.parentNode;
        while (node && node !== editor) {
            if (node instanceof HTMLElement && node.style.color) {
                coloredAncestor = node;
                break;
            }
            node = node.parentNode;
        }
        console.log('[color-debug] enforceCaretColor: ancestor check', {
            coloredAncestorTag: (_a = coloredAncestor === null || coloredAncestor === void 0 ? void 0 : coloredAncestor.tagName) !== null && _a !== void 0 ? _a : null,
            coloredAncestorColor: (_c = (_b = coloredAncestor === null || coloredAncestor === void 0 ? void 0 : coloredAncestor.style) === null || _b === void 0 ? void 0 : _b.color) !== null && _c !== void 0 ? _c : null,
            pickerColor: pickerColor,
            normalizedPicker: normalizedPicker,
            match: !!coloredAncestor && toRGB(coloredAncestor.style.color) === normalizedPicker,
        });
        if (coloredAncestor && toRGB(coloredAncestor.style.color) === normalizedPicker) {
            return;
        }
        console.log('[color-debug] enforceCaretColor: REWRAPPING just-typed run', {
            insertedLen: insertedLen,
            data: ne.data,
            pickerColor: pickerColor,
        });
        // The just-typed chars sit at [startOffset - insertedLen, startOffset)
        // in textNode. Split textNode at startOffset so chars after the caret
        // become a sibling, then strip the inserted run from textNode.
        var insertedText = textNode.substringData(startOffset - insertedLen, insertedLen);
        var remainingNode = startOffset < textNode.length ? textNode.splitText(startOffset) : null;
        textNode.deleteData(startOffset - insertedLen, insertedLen);
        var parent = textNode.parentNode;
        if (!parent)
            return;
        // If the previous sibling element is a span with a matching color,
        // extend its trailing text node so the just-typed run merges into the
        // existing same-color shaping run. Without this, two adjacent spans
        // end up holding the same color but with separate element boundaries —
        // the browser starts a new shaping run between them and duplicated
        // letters ("ee" / "aa") render with subtly different glyph metrics.
        var insertAfterNode = textNode; // chars are inserted after textNode
        var adjacentEl = insertAfterNode.previousSibling
            ? // previousSibling-of-our-textNode is the wrong direction; we need
                // the element that sits just BEFORE the typed run, which is the
                // textNode itself's preceding element if textNode is now empty,
                // otherwise textNode acts as the boundary itself.
                null
            : null;
        // The actual lookup: the span we want to merge with is either textNode
        // itself (if textNode's parent is a color span and we're inside it) or
        // the previous sibling element of textNode if textNode is at the
        // editor level.
        var mergeTarget = null;
        if (parent instanceof HTMLSpanElement &&
            parent.style.color &&
            toRGB(parent.style.color) === normalizedPicker) {
            mergeTarget = parent;
        }
        else {
            var prev = textNode.previousSibling;
            if (prev instanceof HTMLSpanElement &&
                prev.style.color &&
                toRGB(prev.style.color) === normalizedPicker) {
                mergeTarget = prev;
            }
        }
        if (mergeTarget) {
            var last = mergeTarget.lastChild;
            var target = void 0;
            if (last && last.nodeType === Node.TEXT_NODE) {
                last.data = ((_d = last.data) !== null && _d !== void 0 ? _d : '') + insertedText;
                target = last;
            }
            else {
                target = document.createTextNode(insertedText);
                mergeTarget.appendChild(target);
            }
            // Clean up empty source text nodes that splitText left behind
            if (textNode.length === 0 && textNode.parentNode) {
                textNode.parentNode.removeChild(textNode);
            }
            var newRange_2 = document.createRange();
            newRange_2.setStart(target, ((_e = target.data) !== null && _e !== void 0 ? _e : '').length);
            newRange_2.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange_2);
            return;
        }
        // No adjacent same-color span — create a fresh span with the run.
        var span = document.createElement('span');
        span.style.color = pickerColor;
        span.textContent = insertedText;
        if (remainingNode) {
            parent.insertBefore(span, remainingNode);
        }
        else if (textNode.nextSibling) {
            parent.insertBefore(span, textNode.nextSibling);
        }
        else {
            parent.appendChild(span);
        }
        var newRange = document.createRange();
        newRange.setStart(span.firstChild, insertedText.length);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
    };
    var onChangeHandler = function (e) {
        var _a, _b, _c;
        // iOS 26: revert iOS's per-input document + container scroll before paint.
        holdWindowScrollBriefly();
        holdElScrollBriefly((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent'));
        // (window as any).__jumpSnap?.('type-input', editorRef.current, `${idPreffix}[${index}]`);
        // Ignore input events that originated inside a math-field — MathLive's
        // insert() dispatches InputEvents that bubble here, but they must not
        // trigger enforceCaretColor or any DOM manipulation on the editorDiv.
        var ne = e.nativeEvent;
        if (ne === null || ne === void 0 ? void 0 : ne.composedPath().some(function (el) { return (el === null || el === void 0 ? void 0 : el.tagName) === 'MATH-FIELD'; }))
            return;
        // Defense-in-depth: force the just-typed chars into a picker-colored
        // span before doing anything else. See enforceCaretColor doc above.
        enforceCaretColor(e);
        // Reconcile mathFieldInstancesRef with DOM — remove refs for
        // math-fields that were deleted by the browser's native editing
        // (e.g. selecting across a mathfield and pressing Backspace).
        var removedMfs = [];
        mathFieldInstancesRef.current.forEach(function (mf) {
            if (!mf.isConnected) {
                removedMfs.push(mf);
            }
        });
        if (removedMfs.length > 0) {
            removedMfs.forEach(function (mf) {
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
            });
            batchDisposeMathfields(removedMfs);
            // Hide keyboard and sync data if no math fields remain
            var mfPresent_1 = ((_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('math-field')) !== null;
            if (!mfPresent_1) {
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
            }
            if (onChangeSaveTimerRef.current)
                clearTimeout(onChangeSaveTimerRef.current);
            onChangeSaveTimerRef.current = setTimeout(function () { return getMathData(true); }, 500);
            return;
        }
        var mfPresent = ((_c = editorRef.current) === null || _c === void 0 ? void 0 : _c.querySelector('math-field')) !== null;
        if (mathKeyboardVisible || mfPresent)
            return;
        if (onChangeSaveTimerRef.current)
            clearTimeout(onChangeSaveTimerRef.current);
        onChangeSaveTimerRef.current = setTimeout(function () { return getMathData(true); }, 500);
    };
    // Auto-resize: Expand height when content overflows and keep within bounds
    useEffect(function () {
        var editor = editorRef.current;
        if (!editor || !isTextUpdate || typeof index !== 'number')
            return;
        var resizeTimeoutId = null;
        var checkAndResize = function () {
            // Clear any pending resize
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId);
            }
            // Debounce to prevent rapid updates
            resizeTimeoutId = setTimeout(function () {
                var _a, _b, _c, _d, _e, _f, _g;
                var scrollHeight = editor.scrollHeight;
                var clientHeight = editor.clientHeight;
                var currentHeight = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.height) || 300;
                var currentPosX = ((_c = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.position) === null || _c === void 0 ? void 0 : _c.x) || 0;
                var currentPosY = ((_e = (_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.position) === null || _e === void 0 ? void 0 : _e.y) || 0;
                var currentPosZ = ((_g = (_f = data === null || data === void 0 ? void 0 : data.data) === null || _f === void 0 ? void 0 : _f.position) === null || _g === void 0 ? void 0 : _g.z) || 0;
                // Only expand if content is significantly overflowing
                // Use a threshold of 10px to avoid infinite resize loops
                var threshold = 10;
                var overflow = scrollHeight - clientHeight;
                var needsUpdate = false;
                var newHeight = currentHeight;
                var newPosY = currentPosY;
                // Only resize if overflow is significant (more than threshold)
                if (overflow > threshold) {
                    newHeight = currentHeight + overflow + 20; // Add 20px padding
                    needsUpdate = true;
                    // Check if expanded box goes out of whiteboard bounds
                    var whiteboardHeight = canvasHeight || 800;
                    // Calculate box boundaries (box is centered at position)
                    var halfHeight = newHeight / 2;
                    var boxTop = currentPosY - halfHeight;
                    var boxBottom = currentPosY + halfHeight;
                    // Adjust position if going out of bounds
                    var margin = 20;
                    // Check bottom boundary
                    if (boxBottom > whiteboardHeight - margin) {
                        newPosY = whiteboardHeight - margin - halfHeight;
                    }
                    // Check top boundary
                    if (boxTop < margin) {
                        newPosY = margin + halfHeight;
                    }
                }
                // Update dimensions and position if needed
                if (needsUpdate) {
                    setText(function (prev) {
                        var _a;
                        var newText = __spreadArray([], prev, true);
                        if (!newText[index] || !newText[index].data)
                            return prev;
                        // Double-check we're actually changing something to avoid infinite loops
                        var oldHeight = newText[index].data.height || 300;
                        var oldPosY = ((_a = newText[index].data.position) === null || _a === void 0 ? void 0 : _a.y) || 0;
                        if (Math.abs(oldHeight - newHeight) < 1 && Math.abs(oldPosY - newPosY) < 1) {
                            return prev; // No significant change, don't update
                        }
                        newText[index] = __assign(__assign({}, newText[index]), { data: __assign(__assign({}, newText[index].data), { height: newHeight, position: {
                                    x: currentPosX,
                                    y: newPosY,
                                    z: currentPosZ,
                                } }) });
                        return newText;
                    });
                }
                resizeTimeoutId = null;
            }, 100); // 100ms debounce
        };
        // Only check on input events, not on every resize
        var handleInput = function () {
            checkAndResize();
        };
        editor.addEventListener('input', handleInput);
        return function () {
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId);
            }
            editor.removeEventListener('input', handleInput);
        };
    }, [(_f = data === null || data === void 0 ? void 0 : data.data) === null || _f === void 0 ? void 0 : _f.height, (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.position, isTextUpdate, index, canvasHeight, setText]);
    var createMathKeyboard = function () {
        var keypadEl = (_jsxs("div", { ref: keypadWrapperRef, className: "keypadWrapper", role: "group", "aria-label": "Math keypad", onMouseDown: function (e) { return e.preventDefault(); }, onKeyDown: function (e) {
                if (e.key === 'Escape') {
                    e.stopPropagation();
                    setmathKeyboardVisible(false);
                    setActiveMathText && setActiveMathText(-1);
                    announce(t('text.mathEditor.keypadClosed'));
                    setTimeout(function () {
                        var _a;
                        (_a = keypadTriggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        keypadTriggerRef.current = null;
                    }, 0);
                }
                else if (e.key === 'Tab') {
                    // Trap Tab focus within the keypad
                    var wrapper = keypadWrapperRef.current;
                    if (!wrapper)
                        return;
                    var focusable = wrapper.querySelectorAll('button:not([disabled]):not([tabindex="-1"])');
                    if (focusable.length === 0)
                        return;
                    var first = focusable[0];
                    var last = focusable[focusable.length - 1];
                    if (e.shiftKey && document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    }
                    else if (!e.shiftKey && document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }, style: {
                position: 'absolute',
                top: keypadPos.top,
                left: keypadPos.left,
                zIndex: 10000,
            }, children: [_jsx("div", { className: "keypadGrid", role: "group", "aria-label": t('text.mathEditor.mathKeys'), children: (function () {
                        // Accessible key metadata keyed by item.id.
                        // DOM order matches the visual grid (row-by-row, left-to-right) so
                        // Tab navigation follows the same path the eye does — otherwise
                        // visually-adjacent keys like the fraction (/) and empty-box (?)
                        // get skipped past and revisited at the end.
                        var KP = 'text.mathEditor.keys.';
                        var keyMeta = {
                            zero: { labelKey: "".concat(KP, "digit0"), cat: 0 },
                            one: { labelKey: "".concat(KP, "digit1"), cat: 0 },
                            two: { labelKey: "".concat(KP, "digit2"), cat: 0 },
                            three: { labelKey: "".concat(KP, "digit3"), cat: 0 },
                            four: { labelKey: "".concat(KP, "digit4"), cat: 0 },
                            five: { labelKey: "".concat(KP, "digit5"), cat: 0 },
                            six: { labelKey: "".concat(KP, "digit6"), cat: 0 },
                            seven: { labelKey: "".concat(KP, "digit7"), cat: 0 },
                            eight: { labelKey: "".concat(KP, "digit8"), cat: 0 },
                            nine: { labelKey: "".concat(KP, "digit9"), cat: 0 },
                            plus: { labelKey: "".concat(KP, "plus"), cat: 1 },
                            minus: { labelKey: "".concat(KP, "minus"), cat: 1 },
                            multiply: { labelKey: "".concat(KP, "multiply"), cat: 1 },
                            divideSign: { labelKey: "".concat(KP, "divide"), cat: 1 },
                            equals: { labelKey: "".concat(KP, "equals"), cat: 1 },
                            lessThan: { labelKey: "".concat(KP, "lessThan"), cat: 1 },
                            greaterThan: { labelKey: "".concat(KP, "greaterThan"), cat: 1 },
                            lessThanOrEqual: { labelKey: "".concat(KP, "lessThanOrEqual"), cat: 1 },
                            greaterThanOrEqual: { labelKey: "".concat(KP, "greaterThanOrEqual"), cat: 1 },
                            percent: { labelKey: "".concat(KP, "percent"), cat: 1 },
                            comma: { labelKey: "".concat(KP, "comma"), cat: 1 },
                            dot: { labelKey: "".concat(KP, "decimalPoint"), cat: 1 },
                            dollar: { labelKey: "".concat(KP, "dollar"), cat: 1 },
                            cent: { labelKey: "".concat(KP, "cent"), cat: 1 },
                            leftParen: { labelKey: "".concat(KP, "leftParen"), cat: 2 },
                            rightParen: { labelKey: "".concat(KP, "rightParen"), cat: 2 },
                            slash: { labelKey: "".concat(KP, "insertFraction"), cat: 2, shortcut: 'Alt+F' },
                            fraction: { labelKey: "".concat(KP, "insertFraction"), cat: 2, shortcut: 'Alt+F' },
                            power: { labelKey: "".concat(KP, "insertExponent"), cat: 2, shortcut: 'Alt+E' },
                            exponent: { labelKey: "".concat(KP, "insertExponent"), cat: 2, shortcut: 'Alt+E' },
                            sqrt: { labelKey: "".concat(KP, "insertSquareRoot"), cat: 2, shortcut: 'Alt+R' },
                            squareRoot: { labelKey: "".concat(KP, "insertSquareRoot"), cat: 2, shortcut: 'Alt+R' },
                            questionMark: { labelKey: "".concat(KP, "questionMark"), cat: 2 },
                            clear: { labelKey: "".concat(KP, "clear"), cat: 3 },
                            back: { labelKey: "".concat(KP, "backspace"), cat: 3, shortcut: 'Backspace' },
                        };
                        var items = keypadButtons !== null && keypadButtons !== void 0 ? keypadButtons : [];
                        // DOM order: the keypad is visually split into a left half
                        // (cols 1-5, the number pad) and a right half (cols 6-10, the
                        // operators / structures). Tab sweeps the left half top-to-
                        // bottom first, then the right half top-to-bottom — so the
                        // focus ring stays on one side instead of zig-zagging across
                        // the visual gap. Grid position is unaffected; gridRow /
                        // gridColumn are derived from visualIndex below.
                        var indexed = items.map(function (item, i) { return ({ item: item, visualIndex: i }); });
                        indexed.sort(function (a, b) {
                            var sideA = a.visualIndex % 10 < 5 ? 0 : 1;
                            var sideB = b.visualIndex % 10 < 5 ? 0 : 1;
                            if (sideA !== sideB)
                                return sideA - sideB;
                            return a.visualIndex - b.visualIndex;
                        });
                        return indexed.map(function (_a) {
                            var item = _a.item, visualIndex = _a.visualIndex;
                            var meta = keyMeta[item.id];
                            var isOnLeft = visualIndex % 10 < 5;
                            // Grid position: row = floor(idx/10)+1, col = (idx%10)+1
                            var gridRow = Math.floor(visualIndex / 10) + 1;
                            var gridColumn = (visualIndex % 10) + 1;
                            return (_jsx(ButtonComponent, { imgSrc: item.imgSrc, normalCSS: "keypadBtn", defaultCSS: "keypadBtn ".concat((item === null || item === void 0 ? void 0 : item.height100) ? 'height100' : '', " ").concat(isOnLeft ? 'keypadLeft' : 'keypadRight'), ariaLabel: (meta === null || meta === void 0 ? void 0 : meta.labelKey) ? t(meta.labelKey) : item.ariaLabel, ariaKeyShortcuts: meta === null || meta === void 0 ? void 0 : meta.shortcut, clickActive: item === null || item === void 0 ? void 0 : item.activeState, text: item.title, title: (meta === null || meta === void 0 ? void 0 : meta.labelKey) ? t(meta.labelKey) : item.ariaLabel, direction: "bottom", isParentRelative: false, isWhiteBoard: true, buttonStyle: { gridRow: gridRow, gridColumn: gridColumn }, clickHandler: function (e) {
                                    if (e.detail !== 0)
                                        return;
                                    var btn = e.currentTarget;
                                    keypadBtnClickHandler(item);
                                    setTimeout(function () { return btn === null || btn === void 0 ? void 0 : btn.focus(); }, 50);
                                }, onMouseDown: function (e) {
                                    e.preventDefault();
                                    if (Date.now() - keypadTouchRef.current < 500)
                                        return;
                                    keypadBtnClickHandler(item);
                                }, onTouchStart: function (e) {
                                    e.preventDefault();
                                    keypadTouchRef.current = Date.now();
                                    keypadBtnClickHandler(item);
                                } }, item.id));
                        });
                    })() }), _jsx("div", { className: "bottomBtns", children: bottomBtns === null || bottomBtns === void 0 ? void 0 : bottomBtns.map(function (item, idx) {
                        return item.id === 'emptyBtn' ? (
                        // Non-interactive grid spacer. `inert` strips it from focus
                        // order, the a11y tree, and pointer events — stronger than
                        // tabIndex=-1 alone (which only excludes from sequential tab).
                        _jsx("div", { inert: true, "aria-hidden": "true", className: "keypadBtn bottomBtns keyBtn_".concat(idx + 1) }, item.id)) : (_jsx(ButtonComponent, { defaultCSS: "keypadBtn bottomBtns keyBtn_".concat(idx + 1), clickHandler: function (e) {
                                if (e.detail !== 0)
                                    return;
                                var btn = e.currentTarget;
                                keypadBtnClickHandler(item);
                                setTimeout(function () { return btn === null || btn === void 0 ? void 0 : btn.focus(); }, 50);
                            }, onMouseDown: function (e) {
                                e.preventDefault();
                                if (Date.now() - keypadTouchRef.current < 500)
                                    return;
                                keypadBtnClickHandler(item);
                            }, onTouchStart: function (e) {
                                e.preventDefault();
                                keypadTouchRef.current = Date.now();
                                keypadBtnClickHandler(item);
                            }, imgSrc: item.imgSrc, normalCSS: "keypadBtn", ariaLabel: item.ariaLabel, clickActive: item === null || item === void 0 ? void 0 : item.activeState, text: item.title, title: item.ariaLabel, direction: "bottom", isParentRelative: false, isWhiteBoard: true }, item.id));
                    }) })] }));
        // For whiteboard editors, render directly (no portal) so the keyboard
        // stays anchored to the text box and scrolls with the canvas.
        // For all other editors, portal to body to escape parent transforms/overflow.
        if (isTextUpdate) {
            return keypadEl;
        }
        return createPortal(_jsx("div", { className: "mathEditorParent", style: { display: 'contents' }, children: keypadEl }), document.body);
    };
    useImperativeHandle(ref, function () { return ({
        insertMath: function (opts) {
            // When called from a consumer whose math button is itself a
            // keyboard tab stop (JumpBar / CustomTick / FractionTile), arm
            // the post-open focus shift so the keypad's first button gets
            // focus as soon as the keypad mounts. The mathKeyboardVisible
            // effect reads focusKeypadOnOpenRef and moves focus there.
            if (opts === null || opts === void 0 ? void 0 : opts.focusKeypadOnOpen) {
                focusKeypadOnOpenRef.current = true;
            }
            insertMath();
        },
        focus: function () {
            var editor = editorRef.current;
            if (!editor)
                return;
            editor.focus({ preventScroll: true });
            // On iPad, re-focusing a contentEditable places the cursor at the
            // start. Move it to the end so insertMath() appends at the right spot.
            var sel = window.getSelection();
            if (sel && (!sel.rangeCount || !editor.contains(sel.focusNode) || sel.focusOffset === 0)) {
                var range = document.createRange();
                range.selectNodeContents(editor);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
            }
            focusHandler(new MouseEvent('click'));
        },
        getTotalCharacterCount: function (ignoreSpaces) {
            if (ignoreSpaces === void 0) { ignoreSpaces = false; }
            var count = getTotalCharacterCount(ignoreSpaces);
            return count;
        },
        mathFieldInstancesCount: function () {
            var array = Array.from(mathFieldInstancesRef.current);
            return array.length;
        },
        getNumericValue: function () {
            var editor = editorRef.current;
            if (!editor)
                return { isValid: false, value: null };
            // Walk the editor DOM, collecting text from text nodes and LaTeX from
            // math-field elements so split content like "2" + \frac{5}{3} is unified.
            var combined = '';
            var walk = function (node) {
                var _a;
                if (node.nodeType === Node.TEXT_NODE) {
                    combined += (_a = node.textContent) !== null && _a !== void 0 ? _a : '';
                }
                else if (node.nodeName === 'MATH-FIELD') {
                    combined += node.getValue();
                }
                else {
                    node.childNodes.forEach(walk);
                }
            };
            editor.childNodes.forEach(walk);
            console.log(parseNumericLatex(combined), 'parseNumericLatex(combined)');
            return parseNumericLatex(combined);
        },
        getContent: function () { return calcMathData(); },
        closeKeyboard: function () {
            // Synchronously clear the ref so the data-sync guard (line 2518) is
            // bypassed on the very next render — needed when a resize touchend
            // fires before React has processed the async setmathKeyboardVisible(false)
            // triggered by the isFocused→false transition.
            mathKeyboardVisibleRef.current = false;
            setmathKeyboardVisible(false);
        },
    }); }, [mlReady]);
    return (_jsx(_Fragment, { children: _jsxs("div", { className: "mathEditorParent ".concat(className), role: suppressOuterGroup ? 'none' : 'group', "aria-hidden": outerAriaHidden || undefined, "aria-label": suppressOuterGroup ? undefined : t('text.mathEditor.rootLabel'), "aria-describedby": "matheditor-hint-".concat(idPreffix, "-").concat(index !== null && index !== void 0 ? index : 0), "data-manipulative-id": "".concat(idPreffix || 'text', "_").concat(index), style: __assign(__assign({ position: !isTextUpdate ? 'unset' : 'absolute', top: ((_j = (_h = data === null || data === void 0 ? void 0 : data.data) === null || _h === void 0 ? void 0 : _h.position) === null || _j === void 0 ? void 0 : _j.y) || 0, left: ((_l = (_k = data === null || data === void 0 ? void 0 : data.data) === null || _k === void 0 ? void 0 : _k.position) === null || _l === void 0 ? void 0 : _l.x) || 0, transform: disableTransform
                    ? undefined
                    : isTextUpdate && (data === null || data === void 0 ? void 0 : data.type) !== 'equation'
                        ? "translate(-50%, -50%)"
                        : undefined, transformOrigin: disableTransform ? undefined : 'center center', width: !isTextUpdate
                    ? '100%'
                    : (data === null || data === void 0 ? void 0 : data.type) === 'equation'
                        ? 'auto'
                        : ((_m = data === null || data === void 0 ? void 0 : data.data) === null || _m === void 0 ? void 0 : _m.lineWidth) || 300, height: !isTextUpdate
                    ? '100%'
                    : (data === null || data === void 0 ? void 0 : data.type) === 'equation'
                        ? 'auto'
                        : ((_o = data === null || data === void 0 ? void 0 : data.data) === null || _o === void 0 ? void 0 : _o.height) || 300 }, (isFocused ? { zIndex: 1001 } : {})), style), onClick: focusHandler, children: [_jsx("span", { id: "matheditor-hint-".concat(idPreffix, "-").concat(index !== null && index !== void 0 ? index : 0), className: "popupSrOnly", "aria-hidden": suppressOuterGroup ? true : undefined, children: t('text.mathEditor.rootHint') }), _jsx("span", { ref: fracPositionLiveRef, "aria-live": "polite", "aria-atomic": "true", className: "popupSrOnly" }), _jsx("div", { id: "editor_".concat(idPreffix, "_").concat(index !== null && index !== void 0 ? index : 0), ref: editorRef, contentEditable: !viewonly, tabIndex: viewonly ? 0 : undefined, spellCheck: isManipulative ? false : undefined, autoCorrect: isManipulative ? 'off' : undefined, autoCapitalize: isManipulative ? 'off' : undefined, 
                    // Math-keypad-only editors (stickyKeypadAfterInsertOnTouch consumers:
                    // JumpBar / CustomTick / fraction-tile labels) are filled via the
                    // custom on-screen keypad, never the native keyboard. Suppress the
                    // native keyboard so iOS doesn't open it (and scroll the document to
                    // fit it) during keypad churn. Static attribute only — see the sink
                    // note above re: NOT re-enforcing inputmode (causes iOS tap-select).
                    inputMode: stickyKeypadAfterInsertOnTouch ? 'none' : undefined, role: "textbox", "aria-label": editorAriaLabel || undefined, "aria-multiline": "true", "aria-keyshortcuts": !viewonly ? 'Alt+M Alt+K' : undefined, "aria-placeholder": dataPlaceholder ? dataPlaceholder : viewonly ? undefined : t('text.mathEditor.typeHere'), className: "editorDiv ".concat(isFocused && 'focused', " ").concat(viewonly && 'editorDivDisable'), "data-placeholder": dataPlaceholder ? dataPlaceholder : viewonly ? undefined : 'Type here...', onTouchStart: function (e) {
                        // iPad: stop parent canvas/whiteboard touch handlers (pan, drag)
                        // from stealing the long-press gesture needed for text selection.
                        if (isFocusedRef.current && !viewOnlyRef.current) {
                            e.stopPropagation();
                        }
                        // Long-press fallback: if native selection doesn't fire within
                        // 500ms, programmatically select the word under the touch point.
                        if (isFocusedRef.current && !viewOnlyRef.current && isTouchDevice) {
                            var touch = e.touches[0];
                            longPressTouchPosRef.current = { x: touch.clientX, y: touch.clientY };
                            if (longPressTouchRef.current)
                                clearTimeout(longPressTouchRef.current);
                            longPressTouchRef.current = setTimeout(function () {
                                var _a, _b;
                                var pos = longPressTouchPosRef.current;
                                if (!pos)
                                    return;
                                var sel = window.getSelection();
                                // Only intervene if there's no active selection (native didn't fire)
                                if (sel && sel.isCollapsed) {
                                    // caretRangeFromPoint is WebKit/Blink (covers Safari & Chrome)
                                    var range = (_a = document.caretRangeFromPoint) === null || _a === void 0 ? void 0 : _a.call(document, pos.x, pos.y);
                                    if (range && ((_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.contains(range.startContainer))) {
                                        // Expand range to select the word at the touch point
                                        sel.removeAllRanges();
                                        sel.addRange(range);
                                        sel.modify('move', 'backward', 'word');
                                        sel.modify('extend', 'forward', 'word');
                                    }
                                }
                                longPressTouchRef.current = null;
                                longPressTouchPosRef.current = null;
                            }, 500);
                        }
                    }, onTouchMove: function () {
                        // Cancel long-press if the user moves their finger (scrolling/dragging)
                        if (longPressTouchRef.current) {
                            clearTimeout(longPressTouchRef.current);
                            longPressTouchRef.current = null;
                            longPressTouchPosRef.current = null;
                        }
                    }, onTouchEnd: function () {
                        // Cancel long-press timer on touch end (tap, not long-press)
                        if (longPressTouchRef.current) {
                            clearTimeout(longPressTouchRef.current);
                            longPressTouchRef.current = null;
                            longPressTouchPosRef.current = null;
                        }
                    }, onPointerDown: function (e) {
                        var _a, _b;
                        // DO NOT REMOVE — Firefox first-click fix for text-only editors.
                        // See docs/components/MathEditor.md "Firefox First-Click Fix" section.
                        //
                        // Firefox: contenteditable inside a popup/dialog may not receive
                        // keyboard access from the browser's default mousedown behaviour.
                        // MathLive's <math-field> works around this by calling focus() in
                        // its own pointerdown handler — we do the same for the plain
                        // contenteditable.  If the element was already focused
                        // programmatically (e.g. DiceManipulativePopup auto-focus on
                        // mount), blur first so the subsequent focus() is a *fresh*
                        // operation rather than a no-op on an already-focused element.
                        // The browser's default mousedown (which fires after pointerdown)
                        // still places the caret at the click position.
                        if (viewOnlyRef.current)
                            return;
                        // Math-field pointerdown calls stopPropagation, so we never reach
                        // here for math-field clicks.  Guard anyway for safety.
                        if ((_b = (_a = e.target).closest) === null || _b === void 0 ? void 0 : _b.call(_a, 'math-field'))
                            return;
                        var editor = editorRef.current;
                        if (!editor || editor.querySelector('math-field'))
                            return;
                        if (document.activeElement === editor) {
                            editor.blur();
                        }
                        editor.focus({ preventScroll: true });
                        if (isTouchDevice && e.pointerType === 'touch') {
                            onTouchFocusSelf === null || onTouchFocusSelf === void 0 ? void 0 : onTouchFocusSelf();
                        }
                    }, onFocus: function (e) {
                        // Clear lastFocusedMathField when user focuses the editorDiv itself
                        // (i.e. clicks on regular text, not a math-field inside it).
                        // This ensures exec/execFontSize take the text formatting path.
                        if (e.target === editorRef.current) {
                            lastFocusedMathField.current = null;
                            // Announce for iPad VO which doesn't always read role="textbox"
                            if (!viewOnlyRef.current) {
                                announce('Text field. Type normally.');
                            }
                        }
                        // Keyboard-focus path: show the options popover instead of
                        // dropping the caret straight into contentEditable. Mouse/touch
                        // clicks already flipped editingAllowedRef in the global listener.
                        if (e.target === editorRef.current &&
                            focusFromKeyboardRef.current &&
                            (viewOnlyRef.current || !editingAllowedRef.current)) {
                            focusFromKeyboardRef.current = false;
                            // Select the item first so isFocused becomes true and the
                            // popover renders. The popover auto-focus effect will move
                            // focus to the first button; no manual blur needed.
                            focusHandler(e);
                            return;
                        }
                        focusHandler(e);
                        // iPad/mobile: scroll focused input into view above on-screen keyboard.
                        // Use both a timeout (for when keyboard is already visible) and a
                        // visualViewport resize listener (for when the keyboard is animating in,
                        // where 300ms may not be enough to catch the final viewport height).
                        if (scrollIntoViewOnFocus && !isColorChangeFocusRef.current) {
                            var scrollIfNeeded_1 = function () {
                                var _a, _b;
                                var el = editorRef.current;
                                if (!el)
                                    return;
                                var rect = el.getBoundingClientRect();
                                var visibleHeight = (_b = (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : window.innerHeight;
                                if (rect.top < 0 || rect.bottom > visibleHeight) {
                                    window.scrollTo({
                                        top: rect.top + window.scrollY - visibleHeight / 3,
                                        behavior: 'smooth',
                                    });
                                }
                            };
                            // Handle case where keyboard is already visible (e.g. switching cells)
                            setTimeout(scrollIfNeeded_1, 300);
                            // Handle case where keyboard is appearing — scroll once viewport shrinks
                            if (window.visualViewport) {
                                var vv_1 = window.visualViewport;
                                var onResize_1 = function () {
                                    scrollIfNeeded_1();
                                    vv_1.removeEventListener('resize', onResize_1);
                                };
                                vv_1.addEventListener('resize', onResize_1);
                                // Clean up if resize never fires (keyboard was already up)
                                setTimeout(function () { return vv_1.removeEventListener('resize', onResize_1); }, 1500);
                            }
                        }
                    }, onBlur: function (e) {
                        var _a, _b;
                        var relatedTarget = e.relatedTarget;
                        // If focus moved to a math-field inside this editor, let its own
                        // focus handler manage keyboard visibility — don't interfere.
                        if ((relatedTarget === null || relatedTarget === void 0 ? void 0 : relatedTarget.nodeName) === 'MATH-FIELD')
                            return;
                        // If focus moved inside the keypad, keep keyboard visible.
                        if (keypadWrapperRef.current &&
                            relatedTarget &&
                            keypadWrapperRef.current.contains(relatedTarget))
                            return;
                        // Clear link popup unless focus moved to the link popup itself.
                        // This must happen BEFORE the toolbar early-returns below so that
                        // tapping a toolbar button (e.g. the new-text-box icon) while the
                        // link popup is visible still dismisses the stale popup — otherwise
                        // it lingers and re-attaches to the next textbox via popupPos.
                        var isLinkPopup = relatedTarget instanceof HTMLElement && relatedTarget.closest('.linkPopup');
                        if (!isLinkPopup) {
                            setActiveLink(null);
                        }
                        // If focus moved to a toolbar button (e.g. color picker), keep
                        // the math keyboard state so the re-render doesn't collapse
                        // open toolbars like the color palette.
                        if (relatedTarget instanceof HTMLElement) {
                            if (relatedTarget.closest('.toolBottomPanelContainer') ||
                                relatedTarget.closest('.bottomPanelToolContainer') ||
                                relatedTarget.closest('#rightSideDrawerContainer') ||
                                relatedTarget.closest('.sidebar') ||
                                relatedTarget.closest('.footer') ||
                                relatedTarget.closest('.collapsibleRight') ||
                                relatedTarget.closest('.collapsibleOptionHolder')) {
                                return;
                            }
                        }
                        // Focus left the editor — hide the math keyboard.
                        // iPad: tapping a keypad button can fire a spurious focusout
                        // on the math-field that bubbles through here, even though
                        // preventDefault on the keypad button's touchstart keeps focus
                        // visually on the mf. Without this guard the keypad closes
                        // after every keystroke (the user reproduces this in fraction
                        // tiles). keypadTouchRef is set on every keypad-button
                        // touchstart, so a fresh value here means the user is mid-tap
                        // and we should leave the keypad open.
                        //
                        // stickyKeypadAfterInsertOnTouch consumers (JumpBar /
                        // CustomTick / fraction-tile labels) opt into "never auto-
                        // close on focusout" — every keypad press causes some
                        // platform-level focus shuffle (preventDefault on the button
                        // doesn't always block it on iPad, and MathLive's internal
                        // cursor moves do similar on desktop). The previous time-
                        // gated guard (recent keypadTouchRef + insertMathGuardRef)
                        // lapsed after ~500 ms of thinking time, dropping the keypad
                        // mid-edit. For sticky consumers we let the dedicated
                        // isFocused cleanup effect at line 391 close the keypad when
                        // selectedTool no longer references the editor — i.e. only
                        // when the user genuinely exits the math context (clicks
                        // outside, deselects). Other consumers retain the old
                        // time-based guard.
                        // Sticky-keypad consumers (JumpBar / CustomTick / fraction-tile
                        // labels) are FULLY exempt from this focusout-driven close — the
                        // dedicated isFocused cleanup effect (see useEffect([isFocused]))
                        // closes the keypad only when selectedTool no longer references the
                        // editor, i.e. the user genuinely exits the math context. Closing
                        // here instead dropped the keypad after every keypad button press:
                        // the desktop mouse path (onMouseDown) preventDefaults but never
                        // refreshes keypadTouchRef, so the time-gate below is always stale
                        // on click and the keypad vanished between every keystroke on a
                        // tick-mark / jump-bar / fraction-tile label. Non-sticky editors
                        // keep the original time-based auto-close.
                        if (!stickyKeypadAfterInsertOnTouch &&
                            mathKeyboardVisibleRef.current &&
                            Date.now() - keypadTouchRef.current >= 500) {
                            setmathKeyboardVisible(false);
                            setActiveMathText && setActiveMathText(-1);
                        }
                        // For text-only editors (no math-field), clear selection so .focused
                        // outline is removed. Math-field editors handle this in their own
                        // focusout handler via data-MFDeselect.
                        // When keepSelectedOnInternalBlur is true, skip deselection when
                        // focus merely moved to another element within the same manipulative
                        // or to a tool panel (e.g. clicking a fraction tile body to exit
                        // label editing — the tile should stay selected).
                        if (!isTextUpdate) {
                            var editor = editorRef.current;
                            var skipDeselect = false;
                            // Sticky-keypad consumers (JumpBar / CustomTick / fraction-tile
                            // labels) own the selection lifecycle via their own click-outside
                            // handlers. Clearing selectedTool here on iPad's spurious blurs
                            // (relatedTarget=null after a keypad tap on a still-empty label)
                            // drops isFocused → the keypad unmounts → user has to re-tap
                            // the label between every keystroke. Skip the clear unconditionally
                            // for these consumers; the parent widget closes us when the user
                            // actually taps outside.
                            if (stickyKeypadAfterInsertOnTouch) {
                                skipDeselect = true;
                            }
                            if (keepSelectedOnInternalBlur) {
                                // On touch devices, blur with null relatedTarget typically means
                                // the iPad keyboard was dismissed — not that the user tapped
                                // outside.  Actual "tap outside" deselection is handled by the
                                // tapped component's own event handlers (e.g. WhiteBoard sets
                                // selectedTool directly), so we can safely skip here to preserve
                                // the text selection.
                                if (!relatedTarget && isTouchDevice) {
                                    skipDeselect = true;
                                }
                                var sameManipulative = relatedTarget &&
                                    (((_a = editor === null || editor === void 0 ? void 0 : editor.closest('[data-manipulative-id]')) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget)) ||
                                        ((_b = editor === null || editor === void 0 ? void 0 : editor.closest('.tableManipContainer')) === null || _b === void 0 ? void 0 : _b.contains(relatedTarget)));
                                var isToolPanel = relatedTarget instanceof HTMLElement &&
                                    !!(relatedTarget.closest('#rightSideToolPanel') ||
                                        relatedTarget.closest('.toolBottomPanelContainer') ||
                                        relatedTarget.closest('.bottomPanelToolContainer') ||
                                        relatedTarget.closest('.collapsibleOptionHolder') ||
                                        relatedTarget.closest('.manipulativePopup') ||
                                        relatedTarget.closest('.sidebar'));
                                skipDeselect = skipDeselect || !!(sameManipulative || isToolPanel);
                            }
                            if (editor && !editor.querySelector('math-field') && !skipDeselect) {
                                setSelectedTool([]);
                            }
                        }
                        // throwChangeOnBlur: save data on every blur (used by some callers)
                        if (throwChangeOnBlur) {
                            timeoutRef.current = setTimeout(function () {
                                timeoutRef.current = null;
                                getMathData();
                            }, 100);
                        }
                    }, onCopy: function (e) { return e.preventDefault(); }, onPaste: function (e) { return e.preventDefault(); }, onCut: function (e) { return e.preventDefault(); }, onKeyDown: function (e) {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
                        // Events from inside an mf bubble up through the contenteditable
                        // — skip the navigation handlers below (Tab, arrows, mf-adjacent
                        // Backspace) so MathLive's own arrow/tab handling isn't
                        // double-intercepted. Other handlers below (Alt+M / Alt+K /
                        // Alt+F/E/R, Escape, Enter) intentionally still run because they
                        // are shortcuts that should work from inside an mf too.
                        //
                        // Use BOTH `e.target` (the React-retargeted source element) and
                        // `document.activeElement` (the actual focused element). React's
                        // shadow-DOM retargeting can be inconsistent across browsers, so
                        // either signal alone is unreliable; together they reliably
                        // catch every event originating from a focused mf.
                        var evtTarget = e.target;
                        var active = document.activeElement;
                        var fromInsideMf = (!!evtTarget &&
                            (evtTarget.tagName === 'MATH-FIELD' || !!((_a = evtTarget.closest) === null || _a === void 0 ? void 0 : _a.call(evtTarget, 'math-field')))) ||
                            (!!active && (active.tagName === 'MATH-FIELD' || !!((_b = active.closest) === null || _b === void 0 ? void 0 : _b.call(active, 'math-field'))));
                        // Escape — deselect the editor and blur
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedTool([]);
                            return;
                        }
                        // Alt+M — insert a new math block at the cursor
                        if (e.altKey && (e.key === 'm' || e.key === 'M') && !viewonly) {
                            e.preventDefault();
                            e.stopPropagation();
                            insertMath();
                            announce('Math expression inserted. Keypad open.');
                            return;
                        }
                        // Alt+K — toggle keypad open/closed (works whether math-field is focused or not)
                        if (e.altKey && (e.key === 'k' || e.key === 'K') && !viewonly) {
                            e.preventDefault();
                            e.stopPropagation();
                            var willOpen = !mathKeyboardVisibleRef.current;
                            setmathKeyboardVisible(willOpen);
                            announce(willOpen ? 'Math keypad opened.' : 'Math keypad closed.');
                            return;
                        }
                        // Alt+F / Alt+E / Alt+R — insert math structures into the focused math-field
                        var mfFocused = lastFocusedMathField.current;
                        if (e.altKey && mfFocused && !viewonly) {
                            var k = e.key.toLowerCase();
                            if (k === 'f') {
                                e.preventDefault();
                                e.stopPropagation();
                                (_d = (_c = mfFocused).executeCommand) === null || _d === void 0 ? void 0 : _d.call(_c, 'insert', '\\frac{#?}{#?}');
                                announce('Fraction inserted');
                                return;
                            }
                            if (k === 'e') {
                                e.preventDefault();
                                e.stopPropagation();
                                (_f = (_e = mfFocused).executeCommand) === null || _f === void 0 ? void 0 : _f.call(_e, 'insert', '#@^{#?}');
                                announce('Exponent inserted');
                                return;
                            }
                            if (k === 'r') {
                                e.preventDefault();
                                e.stopPropagation();
                                (_h = (_g = mfFocused).executeCommand) === null || _h === void 0 ? void 0 : _h.call(_g, 'insert', '\\sqrt{#?}');
                                announce('Square root inserted');
                                return;
                            }
                        }
                        // Backspace when the cursor sits immediately to the right of a math-field
                        // (or its spacer) — focus into the math-field at the end so the next
                        // backspace deletes content from within it.
                        if (e.key === 'Backspace' && !viewonly) {
                            var mfBefore = findMathFieldBeforeCursor();
                            if (mfBefore) {
                                e.preventDefault();
                                safeFocusMathField(mfBefore);
                                mfBefore.position = mfBefore.lastOffset;
                                lastFocusedMathField.current = mfBefore;
                            }
                        }
                        // ArrowLeft when the cursor sits immediately to the right of a
                        // math-field — enter the mf at lastOffset so arrow navigation
                        // flows naturally between editor text and inline math.
                        // Symmetric counterpart to the Backspace handler above.
                        if (e.key === 'ArrowLeft' &&
                            !viewonly &&
                            !e.shiftKey &&
                            !e.altKey &&
                            !e.metaKey &&
                            !e.ctrlKey &&
                            !fromInsideMf) {
                            var mfBefore = findMathFieldBeforeCursor();
                            if (mfBefore) {
                                e.preventDefault();
                                safeFocusMathField(mfBefore);
                                mfBefore.position = mfBefore.lastOffset;
                                lastFocusedMathField.current = mfBefore;
                                announce('Entering math expression');
                                return;
                            }
                        }
                        // ArrowRight when the cursor sits immediately to the left of a
                        // math-field — enter the mf at offset 0.
                        if (e.key === 'ArrowRight' &&
                            !viewonly &&
                            !e.shiftKey &&
                            !e.altKey &&
                            !e.metaKey &&
                            !e.ctrlKey &&
                            !fromInsideMf) {
                            var mfAfter = findMathFieldAfterCursor();
                            if (mfAfter) {
                                e.preventDefault();
                                safeFocusMathField(mfAfter);
                                mfAfter.position = 0;
                                lastFocusedMathField.current = mfAfter;
                                announce('Entering math expression');
                                return;
                            }
                        }
                        // Save/restore outer scroll position to prevent the browser's native
                        // "keep focused element in view" from jumping the scaled whiteboard.
                        var scrollEl = (_j = editorRef.current) === null || _j === void 0 ? void 0 : _j.closest('.customScrollbarContent');
                        if (scrollEl) {
                            var savedLeft_1 = scrollEl.scrollLeft;
                            var savedTop_1 = scrollEl.scrollTop;
                            requestAnimationFrame(function () {
                                scrollEl.scrollLeft = savedLeft_1;
                                scrollEl.scrollTop = savedTop_1;
                            });
                        }
                        // Enter key — insert a <br> line break (unless blocked)
                        if (e.key === 'Enter' || e.keyCode === 13) {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!blockNewLine) {
                                insertLineBreak();
                            }
                            return false;
                        }
                        // Prevent copy/paste/cut shortcuts
                        if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x'].includes(e.key.toLowerCase())) {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                        }
                    }, onBeforeInput: function (e) {
                        var _a, _b, _c, _d;
                        var inputEvent = e.nativeEvent;
                        // MathLive dispatches 'insertText' InputEvents on the math-field
                        // element that bubble to this contenteditable handler. Letting them
                        // through causes the color-wrap logic below to fire with a stale
                        // window.getSelection() and insert raw LaTeX text (e.g. \placeholder{})
                        // at the wrong location. Bail out whenever the event path passes
                        // through a math-field so we only handle genuine editorDiv input.
                        if (inputEvent.composedPath().some(function (el) { return (el === null || el === void 0 ? void 0 : el.tagName) === 'MATH-FIELD'; })) {
                            return;
                        }
                        console.log('[color-debug] onBeforeInput fired', {
                            inputType: inputEvent.inputType,
                            data: inputEvent.data,
                            isTextUpdate: isTextUpdate,
                            viewonly: viewonly,
                            isManipulative: isManipulative,
                            contextColor: color,
                        });
                        // Enforce color picker color for newly typed text.
                        // When the caret is collapsed and a character is being inserted,
                        // check if the caret is already inside a span with the correct
                        // picker color. If not, preventDefault the native insert and
                        // manually place the character inside a new colored span.
                        // We must preventDefault because the browser's native handler
                        // ignores DOM changes made in onBeforeInput — it inserts the
                        // character at its own tracked position, bypassing our span.
                        if (isTextUpdate &&
                            !viewonly &&
                            !isManipulative &&
                            inputEvent.inputType === 'insertText' &&
                            inputEvent.data) {
                            var sel = window.getSelection();
                            if ((sel === null || sel === void 0 ? void 0 : sel.isCollapsed) && sel.rangeCount > 0) {
                                var pickerColor = ColorEnum[color] || ColorEnum['BLACK'];
                                // Normalize any CSS color string to "r,g,b" for reliable comparison.
                                // style.color returns rgb() while ColorEnum uses hex — direct === fails.
                                var toRGB = function (c) {
                                    var _a, _b, _c, _d;
                                    var s = c.trim().toLowerCase();
                                    // hex → r,g,b
                                    var hex = (_b = (_a = s.match(/^#([0-9a-f]{6})$/i)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : (_d = (_c = s.match(/^#([0-9a-f]{3})$/i)) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.replace(/./g, '$&$&');
                                    if (hex) {
                                        return "".concat(parseInt(hex.slice(0, 2), 16), ",").concat(parseInt(hex.slice(2, 4), 16), ",").concat(parseInt(hex.slice(4, 6), 16));
                                    }
                                    // rgb(r, g, b) → r,g,b
                                    var m = s.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
                                    return m ? "".concat(m[1], ",").concat(m[2], ",").concat(m[3]) : s;
                                };
                                var normalizedPicker = toRGB(pickerColor);
                                // Check if caret is already inside a correctly-colored span
                                var node = sel.anchorNode;
                                var alreadyCorrect = false;
                                var coloredAncestorTagDbg = null;
                                var coloredAncestorColorDbg = null;
                                while (node && node !== editorRef.current) {
                                    if (node instanceof HTMLElement && node.style.color) {
                                        coloredAncestorTagDbg = node.tagName;
                                        coloredAncestorColorDbg = node.style.color;
                                        alreadyCorrect = toRGB(node.style.color) === normalizedPicker;
                                        break;
                                    }
                                    node = node.parentNode;
                                }
                                console.log('[color-debug] onBeforeInput: ancestor check', {
                                    pickerColor: pickerColor,
                                    normalizedPicker: normalizedPicker,
                                    coloredAncestorTag: coloredAncestorTagDbg,
                                    coloredAncestorColor: coloredAncestorColorDbg,
                                    alreadyCorrect: alreadyCorrect,
                                    anchorNode: (_a = sel.anchorNode) === null || _a === void 0 ? void 0 : _a.nodeName,
                                });
                                if (!alreadyCorrect) {
                                    console.log('[color-debug] onBeforeInput: WRAPPING typed char in fresh color span');
                                    e.preventDefault();
                                    var range = sel.getRangeAt(0);
                                    range.deleteContents();
                                    // Try to append to an adjacent color-A span instead of creating
                                    // a new one for every character. This keeps the DOM clean and
                                    // ensures subsequent characters stay in the correct color.
                                    var prevNode = range.startContainer.nodeType === Node.TEXT_NODE
                                        ? range.startContainer.parentElement
                                        : range.startContainer.childNodes[range.startOffset - 1];
                                    var prevSpan = prevNode instanceof HTMLSpanElement &&
                                        prevNode.style.color &&
                                        toRGB(prevNode.style.color) === normalizedPicker
                                        ? prevNode
                                        : null;
                                    if (prevSpan) {
                                        // Append into the span's existing trailing text node so
                                        // consecutive same-color chars share a single text node
                                        // and a single shaping run. Separate text nodes break
                                        // shaping and the browser renders duplicated letters
                                        // ("ee", "aa", etc.) with visibly different glyph
                                        // widths / metrics — reads as "the font style changed".
                                        var last = prevSpan.lastChild;
                                        var target = void 0;
                                        if (last && last.nodeType === Node.TEXT_NODE) {
                                            last.data = ((_b = last.data) !== null && _b !== void 0 ? _b : '') + inputEvent.data;
                                            target = last;
                                        }
                                        else {
                                            target = document.createTextNode(inputEvent.data);
                                            prevSpan.appendChild(target);
                                        }
                                        var newRange = document.createRange();
                                        newRange.setStart(target, ((_c = target.data) !== null && _c !== void 0 ? _c : '').length);
                                        newRange.collapse(true);
                                        sel.removeAllRanges();
                                        sel.addRange(newRange);
                                    }
                                    else {
                                        // Create a new colored span with the typed character
                                        var span = document.createElement('span');
                                        span.style.color = pickerColor;
                                        span.textContent = inputEvent.data;
                                        range.insertNode(span);
                                        // Place caret inside the span after the text
                                        var newRange = document.createRange();
                                        newRange.setStart(span.firstChild, span.firstChild.textContent.length);
                                        newRange.collapse(true);
                                        sel.removeAllRanges();
                                        sel.addRange(newRange);
                                    }
                                    // Trigger input handler so data syncs
                                    (_d = editorRef.current) === null || _d === void 0 ? void 0 : _d.dispatchEvent(new Event('input', { bubbles: true }));
                                    return;
                                }
                            }
                        }
                        // iPad virtual keyboard fires beforeinput with deleteContentBackward
                        // instead of a keydown Backspace event. Intercept it here so the
                        // browser doesn't natively delete/break the math-field element.
                        if (inputEvent.inputType === 'deleteContentBackward' && !viewonly) {
                            var mfBefore = findMathFieldBeforeCursor();
                            if (mfBefore) {
                                e.preventDefault();
                                safeFocusMathField(mfBefore);
                                mfBefore.position = mfBefore.lastOffset;
                                lastFocusedMathField.current = mfBefore;
                                return;
                            }
                        }
                        // iPad virtual keyboard fires beforeinput with insertParagraph
                        // or insertLineBreak instead of a keydown Enter event.
                        // Handle it the same way as the keydown Enter handler.
                        if ((inputEvent.inputType === 'insertParagraph' ||
                            inputEvent.inputType === 'insertLineBreak') &&
                            !viewonly) {
                            e.preventDefault();
                            if (!blockNewLine) {
                                insertLineBreak();
                            }
                            return;
                        }
                        if (viewonly || !maxCharacters)
                            return;
                        var currentCount = getTotalCharacterCount();
                        if (currentCount >= maxCharacters) {
                            e.preventDefault();
                            return false;
                        }
                    }, onInput: onChangeHandler, onClick: function (e) {
                        var target = e.target;
                        // When editing is locked, open links in a new tab directly
                        if (viewonly) {
                            var anchor = target.tagName === 'A' ? target : target.closest('a');
                            if (anchor) {
                                e.preventDefault();
                                var href = anchor.getAttribute('href');
                                if (href) {
                                    var url = href.startsWith('http://') || href.startsWith('https://')
                                        ? href
                                        : "https://".concat(href);
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                }
                            }
                            else {
                                onClickWhenLocked === null || onClickWhenLocked === void 0 ? void 0 : onClickWhenLocked(index);
                            }
                            return;
                        }
                        if (target.tagName === 'A') {
                            clickedAnchorRef.current = target;
                            setActiveLink(target.getAttribute('href'));
                        }
                        else {
                            clickedAnchorRef.current = null;
                            setActiveLink(null);
                        }
                    }, onSelect: function () {
                        if (viewonly)
                            return;
                        var sel = window.getSelection();
                        if (!sel || sel.rangeCount === 0)
                            return;
                        if (!sel.isCollapsed) {
                            lastSelection.current = sel.getRangeAt(0).cloneRange();
                        }
                        emitFormattingState(null);
                    }, style: {
                        fontSize: effectiveFontSize,
                        color: ((_p = data === null || data === void 0 ? void 0 : data.data) === null || _p === void 0 ? void 0 : _p.color) || '#000000',
                        textAlign: "".concat((_r = (_q = data === null || data === void 0 ? void 0 : data.data) === null || _q === void 0 ? void 0 : _q.alignment) !== null && _r !== void 0 ? _r : 'left'),
                        cursor: viewonly ? 'inherit' : 'text',
                        userSelect: viewonly ? 'text' : 'auto',
                        pointerEvents: viewonly ? 'none' : 'auto',
                        transform: disableTransform ? undefined : "rotate(".concat(((_s = data === null || data === void 0 ? void 0 : data.data) === null || _s === void 0 ? void 0 : _s.rotation) || 0, "deg)"),
                        transformOrigin: disableTransform ? undefined : 'center center',
                    }, autoFocus: isFocused && !viewonly }), mathKeyboardVisible && !viewonly && createMathKeyboard(), enableFocusPanel && isFocused && (_jsxs("div", { ref: focusOptionsRef, className: "editorFocusOptions", role: "dialog", "aria-modal": "true", "aria-label": t('text.mathEditor.focusOptionsLabel'), onClick: function (e) { return e.stopPropagation(); }, onKeyDown: function (e) {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedTool([]);
                        }
                    }, style: {
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        transform: focusPanelRight
                            ? "translate(calc(".concat(((_t = data === null || data === void 0 ? void 0 : data.data) === null || _t === void 0 ? void 0 : _t.lineWidth) || 300, "px + 12px), 0)")
                            : 'translate(calc(-100% - 12px), 0)',
                        background: '#fff',
                        border: '2px solid #293273',
                        borderRadius: 8,
                        padding: 6,
                        display: 'flex',
                        gap: 6,
                        zIndex: 10002,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                    }, children: [!viewonly && (_jsx(ButtonComponent, { imgSrc: {
                                normal: './assets/images/icons/editPencilN.svg',
                                hover: './assets/images/icons/editPencilH.svg',
                                disable: './assets/images/icons/editPencilDN.svg',
                            }, defaultCSS: "editorFocusOption editorFocusOption-edit", normalCSS: "editorFocusOptionBtn", ariaLabel: t('text.mathEditor.enterEditing'), title: t('text.mathEditor.enterEditing'), clickHandler: function () {
                                editingAllowedRef.current = true;
                                setTimeout(function () {
                                    var el = editorRef.current;
                                    if (!el)
                                        return;
                                    el.focus({ preventScroll: true });
                                    var sel = window.getSelection();
                                    var range = document.createRange();
                                    range.selectNodeContents(el);
                                    range.collapse(false);
                                    sel === null || sel === void 0 ? void 0 : sel.removeAllRanges();
                                    sel === null || sel === void 0 ? void 0 : sel.addRange(range);
                                    announce(t('text.mathEditor.editingMode'));
                                }, 0);
                            } })), _jsx(ButtonComponent, { imgSrc: {
                                normal: './assets/images/icons/kebabN.svg',
                                hover: './assets/images/icons/kebabH.svg',
                                disable: './assets/images/icons/kebabN.svg',
                            }, defaultCSS: "editorFocusOption editorFocusOption-menu", normalCSS: "editorFocusOptionBtn", ariaLabel: t('text.mathEditor.openOptions'), ariaHaspopup: "menu", title: t('text.mathEditor.openOptions'), clickHandler: function () {
                                focusFromKeyboardRef.current = false;
                                if (typeof index !== 'number')
                                    return;
                                var el = editorRef.current;
                                if (!el)
                                    return;
                                var rect = el.getBoundingClientRect();
                                var manipId = "".concat(idPreffix, "_").concat(index);
                                var x = rect.left + rect.width / 2;
                                var y = rect.top + rect.height / 2;
                                setSelectedTool([manipId]);
                                setContextMenu({ x: x, y: y, manipulativeId: manipId });
                                announce(t('text.mathEditor.optionsOpened'));
                            } })] }))] }) }));
});
export default MathEditor;
